import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { database } from "../firebaseConfig";
import { ref, update, onValue, push, remove, set, get } from "firebase/database";
import "./WorldSettingsTopics.css";
import ConfirmModal from "./ConfirmModal";
import EmailComplianceChecker from './EmailCompliance/EmailComplianceChecker';
import TemplateDeliverabilityChecker from './TemplateDeliverabilityChecker';
import { useEmailComplianceChecker } from './EmailCompliance/useEmailComplianceChecker';
import TopicComplianceBadge from './WorldSettingsTopics/TopicComplianceBadge';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';
import { handleFormattingKeyDown } from './EmailAiModes/formattingShortcuts';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import useCustomFields from '../hooks/useCustomFields';
import { emailGenerationCost, costOf } from '../config/creditPlans';
import {
  checkTemplateBraces,
  refinementIsSafe,
  SEND_TIME_BODY_VARIABLES,
  BODY_VARIABLE_ALIASES,
} from './templateBraceCheck';
import { DYNAMIC_SNIPPETS, snippetInsertText } from './dynamicSnippets';
import { PINNED_INSERT_VAR_KEYS, partitionInsertVariables } from './insertVariableChips';
import VariantForgeModal from './VariantForgeModal';
import { ChanceRadarTemplateField } from './ChanceRadarSettings';
import CallContextSettings from './CallContextSettings';
import { DEFAULT_CALL_CONTEXT, normalizeCallContext } from './callContextConfig';
import { buildVariantForgePrompt, auditDerivedVariant } from './variantForge';
import emailPersonalities from './emailPersonalities';
import emailFrameworks, {
  localizeFramework,
  getFrameworkById,
  frameworkLengthPlan,
  buildLengthRule,
  normalizeLengthScale,
  buildFrameworkPromptBlock,
  resolveSkeleton,
} from './emailFrameworks';
// Voice Clone Mode — separate creation flow + editor for clone-driven templates
import ModePickerModal from './CloneTemplate/ModePickerModal';
import CopyTwinAutoSetupModal from './CloneTemplate/CopyTwinAutoSetupModal';
import VoiceCloneTemplateEditor from './CloneTemplate/VoiceCloneTemplateEditor';
import {
  TEMPLATE_MODES,
  createVoiceCloneTemplate,
  applyAutoSetupToCloneTemplate,
  isVoiceCloneTemplate,
} from './CloneTemplate/cloneTemplateUtils';
import {
  selectCopywritingVaultEntries,
  summarizeVaultCopyContext,
  buildVaultCopyPromptBlock,
} from '../utils/vaultCopyContext';

// Fresh email text is generated one full call per variant (writer + polish
// pass), so the ceiling is lower than for subject lines, where a single call
// returns several lines.
const MAX_TEXT_VARIANTS = 3;
const clampTextVariants = (n) => Math.min(Math.max(Number(n) || 1, 1), MAX_TEXT_VARIANTS);

/** Estimated credits for one email-text variant: writer call + polish pass. */
const textVariantCredits = () => costOf('templateWriter') + costOf('templateRefine');

/** How many generation presets we keep around for re-use. */
const MAX_GEN_PRESETS = 3;

// Simple hash function for consistent legacy IDs (must match emailTrackingUtils.js)
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

// Helper function to generate unique IDs (for NEW items only)
const generateId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Helper function to generate consistent ID for legacy string items
// Uses hash-based ID to match emailTrackingUtils.js extractItemId function
// Note: prefix is ignored - format must be "legacy_hash" to match tracking system
const generateLegacyId = (text) => `legacy_${hashString(text)}`;

// Helper function to create a new subject line object with ID
const createSubjectLine = (text = "") => ({ id: generateId("subj"), text });

// Helper function to create a new instruction object with ID
const createInstruction = (text = "", pacing = null) => ({ id: generateId("instr"), text, pacing });

// Helper function to normalize subject lines (convert old string format to new object format)
// Uses hash-based IDs for legacy strings to match the tracking system
const normalizeSubjectLines = (lines) => {
  if (!lines || lines.length === 0) return [createSubjectLine()];
  return lines.map(line => {
    if (typeof line === 'string') {
      // Use hash-based ID for legacy strings - this matches extractItemId in emailTrackingUtils.js
      return { id: generateLegacyId(line), text: line };
    }
    // Already in object format, ensure it has an id
    // If no ID exists, generate hash-based ID for consistency
    return { id: line.id || generateLegacyId(line.text || ""), text: line.text || "" };
  });
};

// Helper function to normalize instructions (convert old string format to new object format)
// Uses hash-based IDs for legacy strings to match the tracking system
const normalizeInstructions = (notes) => {
  if (!notes || notes.length === 0) return [createInstruction()];
  return notes.map(note => {
    if (typeof note === 'string') {
      // Use hash-based ID for legacy strings - this matches extractItemId in emailTrackingUtils.js
      return { id: generateLegacyId(note), text: note, pacing: null };
    }
    // Already in object format, ensure it has an id
    // If no ID exists, generate hash-based ID for consistency
    return { id: note.id || generateLegacyId(note.text || ""), text: note.text || "", pacing: note.pacing || null };
  });
};

// Helper function to check if topic data needs migration (old format without IDs)
const needsMigration = (topicData) => {
  if (!topicData) return false;
  
  // Check subject lines
  const subjectLines = topicData.subjectLines || (topicData.subjectLine ? [topicData.subjectLine] : []);
  const hasOldSubjectLines = subjectLines.some(line => typeof line === 'string');
  
  // Check instructions
  const instructions = topicData.topicSpecificNotes || (topicData.topicSpecificNote ? [topicData.topicSpecificNote] : []);
  const hasOldInstructions = instructions.some(note => typeof note === 'string');
  
  // Check for legacy singular fields
  const hasLegacyFields = !!topicData.subjectLine || !!topicData.topicSpecificNote;
  
  return hasOldSubjectLines || hasOldInstructions || hasLegacyFields;
};

// Helper function to calculate reply rate
const calculateReplyRate = (sent, replies) => {
  if (!sent || sent === 0) return '0.0';
  return ((replies || 0) / sent * 100).toFixed(1);
};

// Stats Badge Component (Header - shows reply rate)
const TemplateStatsBadge = ({ stats }) => {
  const sent = stats?.totalSent || 0;
  const replies = stats?.totalReplies || 0;
  const replyRate = calculateReplyRate(sent, replies);
  const rateClass = sent === 0 ? 'neutral' : parseFloat(replyRate) >= 10 ? 'good' : parseFloat(replyRate) >= 5 ? 'average' : 'low';
  
  return (
    <span className="topics-stats-badge">
      <span className={`topics-stats-rate ${rateClass}`}>
        {replyRate}% Reply Rate
      </span>
      <span className="topics-stats-detail">
        {sent} sent · {replies} replies
      </span>
    </span>
  );
};

// Analytics Row Component (Woodpecker-style) - Always visible
const AnalyticsRow = ({ sent = 0, replies = 0, label = '' }) => {
  const replyRate = calculateReplyRate(sent, replies);
  const rateClass = parseFloat(replyRate) >= 10 ? 'good' : parseFloat(replyRate) >= 5 ? 'average' : sent === 0 ? 'neutral' : 'low';
  
  return (
    <div className="topics-analytics-row">
      <div className="topics-analytics-item">
        <span className="topics-analytics-value">{sent}</span>
        <span className="topics-analytics-label">Sent</span>
      </div>
      <div className="topics-analytics-item">
        <span className="topics-analytics-value">{replies}</span>
        <span className="topics-analytics-label">Replies</span>
      </div>
      <div className={`topics-analytics-item topics-analytics-rate ${rateClass}`}>
        <span className="topics-analytics-value">{replyRate}%</span>
        <span className="topics-analytics-label">Reply Rate</span>
      </div>
    </div>
  );
};

// Subject Line Stats Component - Always visible
const SubjectLineStats = ({ subjectLineId, stats }) => {
  const lineStats = stats?.subjectLineStats?.[subjectLineId] || { sent: 0, replies: 0 };
  const sent = lineStats.sent || 0;
  const replies = lineStats.replies || 0;
  
  return <AnalyticsRow sent={sent} replies={replies} />;
};

// Instruction Stats Component - Always visible
const InstructionStats = ({ instructionId, stats }) => {
  const instrStats = stats?.instructionStats?.[instructionId] || { sent: 0, replies: 0 };
  const sent = instrStats.sent || 0;
  const replies = instrStats.replies || 0;
  
  return <AnalyticsRow sent={sent} replies={replies} />;
};

// ===== INLINE COMPLIANCE BADGE (per variant) =====
const InlineComplianceBadge = ({ text }) => {
  const hasContent = text?.trim()?.length > 0;
  const { violations, spamWarnings, stats } = useEmailComplianceChecker(hasContent ? text : '');
  const [showTooltip, setShowTooltip] = useState(false);
  
  const redViolations = violations.filter(v => v.severity === 'high');
  const yellowViolations = violations.filter(v => v.severity === 'medium');
  const totalIssues = stats.totalViolations + stats.spamRisk;
  
  if (!hasContent || totalIssues === 0) {
    if (hasContent) {
      return (
        <span className="ed-inline-compliance ed-inline-ok" title="Compliant">
          <i className="fa fa-check-circle"></i>
        </span>
      );
    }
    return null;
  }
  
  const isHighRisk = redViolations.length > 0;
  const isSpamOnly = stats.totalViolations === 0 && stats.spamRisk > 0;
  
  return (
    <div className="ed-inline-compliance-wrap">
      <button 
        className={`ed-inline-compliance ${isHighRisk ? 'ed-inline-danger' : isSpamOnly ? 'ed-inline-spam' : 'ed-inline-warn'}`}
        onClick={() => setShowTooltip(!showTooltip)}
        type="button"
      >
        <i className={`fa ${isHighRisk ? 'fa-exclamation-triangle' : isSpamOnly ? 'fa-envelope' : 'fa-exclamation-circle'}`}></i>
        <span>
          {isHighRisk ? `${redViolations.length} Critical` 
            : isSpamOnly ? `${stats.spamRisk} Spam` 
            : `${yellowViolations.length} Warning${yellowViolations.length !== 1 ? 's' : ''}`}
        </span>
        {redViolations.length > 0 && (yellowViolations.length > 0 || stats.spamRisk > 0) && (
          <span className="ed-inline-extra">+{yellowViolations.length + stats.spamRisk}</span>
        )}
      </button>
      {showTooltip && (
        <>
          <div className="ed-inline-tooltip-overlay" onClick={() => setShowTooltip(false)}></div>
          <div className="ed-inline-tooltip">
            <div className="ed-inline-tooltip-header">
              <span>Compliance & Spam Check</span>
              <button className="ed-inline-tooltip-close" onClick={() => setShowTooltip(false)}>
                <i className="fa fa-times"></i>
              </button>
            </div>
            {redViolations.length > 0 && (
              <div className="ed-inline-tooltip-group">
                <div className="ed-inline-tooltip-group-title ed-inline-tooltip-red">
                  <i className="fa fa-exclamation-triangle"></i> Critical ({redViolations.length})
                </div>
                {redViolations.map((v, i) => (
                  <div key={`r-${i}`} className="ed-inline-tooltip-item">
                    <span className="ed-inline-tooltip-reason">{v.reason}</span>
                    <code className="ed-inline-tooltip-match">"{v.matchedText}"</code>
                  </div>
                ))}
              </div>
            )}
            {yellowViolations.length > 0 && (
              <div className="ed-inline-tooltip-group">
                <div className="ed-inline-tooltip-group-title ed-inline-tooltip-yellow">
                  <i className="fa fa-exclamation-circle"></i> Warnings ({yellowViolations.length})
                </div>
                {yellowViolations.map((v, i) => (
                  <div key={`y-${i}`} className="ed-inline-tooltip-item">
                    <span className="ed-inline-tooltip-reason">{v.reason}</span>
                    <code className="ed-inline-tooltip-match">"{v.matchedText}"</code>
                  </div>
                ))}
              </div>
            )}
            {spamWarnings.length > 0 && (
              <div className="ed-inline-tooltip-group">
                <div className="ed-inline-tooltip-group-title ed-inline-tooltip-spam">
                  <i className="fa fa-envelope"></i> Spam Triggers ({spamWarnings.length})
                </div>
                {spamWarnings.map((v, i) => (
                  <div key={`s-${i}`} className="ed-inline-tooltip-item">
                    <span className="ed-inline-tooltip-reason">{v.reason}</span>
                    <code className="ed-inline-tooltip-match">"{v.matchedText}"</code>
                  </div>
                ))}
              </div>
            )}
            <div className="ed-inline-tooltip-disclaimer">
              <i className="fa fa-info-circle"></i>
              <span>This is not legal advice. The system may contain errors. Please consult a legal professional for compliance matters.</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ===== COMPACT SIDEBAR COMPLIANCE CARD =====
const SidebarComplianceCard = ({ masterEditData }) => {
  const fullText = `${masterEditData.subjectLines.map(l => l.text).join('\n')}\n${masterEditData.topicSpecificNotes.map(n => n.text).join('\n')}`;
  const hasContent = masterEditData.subjectLines.some(l => l.text?.trim()) || 
                     masterEditData.topicSpecificNotes.some(n => n.text?.trim());
  
  const { violations, spamWarnings, complianceStatus, stats, isAnalyzing } = useEmailComplianceChecker(hasContent ? fullText : '');
  
  const [expanded, setExpanded] = useState(false);
  
  const redViolations = violations.filter(v => v.severity === 'high');
  const yellowViolations = violations.filter(v => v.severity === 'medium');
  const totalIssues = stats.totalViolations + stats.spamRisk;
  
  const getStatusConfig = () => {
    if (!hasContent) return { label: 'No Content', color: '#94a3b8', bg: '#f1f5f9', icon: '○' };
    if (stats.highRisk > 0) return { label: 'High Risk', color: '#dc2626', bg: '#fef2f2', icon: '●' };
    if (stats.mediumRisk > 0) return { label: 'Warnings', color: '#f59e0b', bg: '#fffbeb', icon: '●' };
    if (stats.spamRisk > 0) return { label: 'Spam Risk', color: '#dc2626', bg: '#fef2f2', icon: '●' };
    return { label: 'Compliant', color: '#16a34a', bg: '#f0fdf4', icon: '●' };
  };
  
  const statusConfig = getStatusConfig();
  
  return (
    <div className="ed-sb-compliance">
      <div 
        className="ed-cc-status" 
        style={{ borderBottomColor: totalIssues > 0 ? statusConfig.color : 'rgba(148, 163, 184, 0.15)' }}
        onClick={() => hasContent && totalIssues > 0 && setExpanded(!expanded)}
      >
        <div className="ed-cc-status-left">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={statusConfig.color} strokeWidth="2" style={{flexShrink: 0}}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          <span className="ed-cc-label" style={{ color: statusConfig.color }}>{statusConfig.label}</span>
          {isAnalyzing && <span className="ed-cc-analyzing">analyzing...</span>}
        </div>
        {totalIssues > 0 && (
          <div className="ed-cc-counts">
            {stats.highRisk > 0 && <span className="ed-cc-count ed-cc-red">{stats.highRisk}</span>}
            {stats.mediumRisk > 0 && <span className="ed-cc-count ed-cc-yellow">{stats.mediumRisk}</span>}
            {stats.spamRisk > 0 && <span className="ed-cc-count ed-cc-spam">{stats.spamRisk}</span>}
            <svg 
              width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={statusConfig.color} strokeWidth="2.5"
              className={`ed-cc-chevron ${expanded ? 'ed-cc-chevron-open' : ''}`}
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>
        )}
        {totalIssues === 0 && hasContent && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        )}
      </div>
      
      {expanded && totalIssues > 0 && (
        <div className="ed-cc-details">
          {redViolations.length > 0 && (
            <div className="ed-cc-group ed-cc-group-red">
              <div className="ed-cc-group-title">
                <span className="ed-cc-group-dot" style={{background: '#dc2626'}}></span>
                Critical ({redViolations.length})
              </div>
              {redViolations.map((v, i) => (
                <div key={i} className="ed-cc-item">
                  <span className="ed-cc-item-label">{v.reason}</span>
                  <code className="ed-cc-item-match">"{v.matchedText}"</code>
                </div>
              ))}
            </div>
          )}
          {yellowViolations.length > 0 && (
            <div className="ed-cc-group ed-cc-group-yellow">
              <div className="ed-cc-group-title">
                <span className="ed-cc-group-dot" style={{background: '#f59e0b'}}></span>
                Warnings ({yellowViolations.length})
              </div>
              {yellowViolations.map((v, i) => (
                <div key={i} className="ed-cc-item">
                  <span className="ed-cc-item-label">{v.reason}</span>
                  <code className="ed-cc-item-match">"{v.matchedText}"</code>
                </div>
              ))}
            </div>
          )}
          {spamWarnings.length > 0 && (
            <div className="ed-cc-group ed-cc-group-spam">
              <div className="ed-cc-group-title">
                <span className="ed-cc-group-dot" style={{background: '#dc2626'}}></span>
                Spam Triggers ({spamWarnings.length})
              </div>
              {spamWarnings.map((v, i) => (
                <div key={i} className="ed-cc-item">
                  <span className="ed-cc-item-label">{v.reason}</span>
                  <code className="ed-cc-item-match">"{v.matchedText}"</code>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="ed-cc-disclaimer">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="16" x2="12" y2="12"/>
          <line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
        <span>This is an automated check. We provide no legal advice. The system may contain errors. Please consult a legal expert for compliance matters.</span>
      </div>
    </div>
  );
};

const WorldSettingsTopics = ({ userId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [availableTopics, setAvailableTopics] = useState({});
  const [selectedTopics, setSelectedTopics] = useState({});
  // Stay blank until the first coordinated read finishes. Painting the empty
  // category shells first makes them grow open once Firebase answers.
  const [topicsReady, setTopicsReady] = useState(false);
  const [message, setMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [newTopic, setNewTopic] = useState({
    topic: "",
    topicSpecificNotes: [createInstruction()], // Array mit Objekten {id, text}
    type: "intro",
    subjectLines: [createSubjectLine()], // Array mit Objekten {id, text}
    aiTagId: ""
  });
  const [confirmModal, setConfirmModal] = useState({ open: false, title: "", message: "", onConfirm: null });
  // Duplicate-template modal: lets the user clone a template's settings under a
  // brand-new name. The copy is created but NOT activated.
  const [duplicateModal, setDuplicateModal] = useState({ open: false, sourceId: null, name: "" });
  const [customTopics, setCustomTopics] = useState({});
  // Active category tab for the template selection ('all' shows every category)
  const [activeTemplateCategory, setActiveTemplateCategory] = useState('all');
  const [isProcessing, setIsProcessing] = useState(false);
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [openTopicId, setOpenTopicId] = useState(null);
  const [inlineEditId, setInlineEditId] = useState(null);
  const [inlineEditData, setInlineEditData] = useState({ 
    subjectLines: [createSubjectLine()], 
    topicSpecificNotes: [createInstruction()], 
    topic: '', 
    aiTagId: '' 
  });
  const [aiTags, setAiTags] = useState([]);
  const [isSaved, setIsSaved] = useState(false);
  const [showMigrationHint, setShowMigrationHint] = useState(false);
  
  // Voice Clone Mode — modal state for the "Add New" mode picker.
  // pendingNewType holds the template-type (intro / follow_up_1 / ...) that the
  // user clicked "+ Add New" on, so the mode-pick callback knows the context.
  const [showModeModal, setShowModeModal] = useState(false);
  const [pendingNewType, setPendingNewType] = useState(null);
  // CopyTwin auto-setup — shown after the user picks CopyTwin mode, before the
  // editor opens, so a brand-new template (no info entered yet) can be wired up
  // from a CopyTwin in one click.
  const [showAutoSetupModal, setShowAutoSetupModal] = useState(false);

  // Email Director - Master Editor State
  const [masterEditTopic, setMasterEditTopic] = useState(null); // { id, ...topicData }
  const [masterEditData, setMasterEditData] = useState({
    topic: '',
    subjectLines: [createSubjectLine()],
    topicSpecificNotes: [createInstruction()],
    aiTagId: '',
    type: 'intro',
    // General Info fields
    generalInfo: '',
    aiAdherence: 'exact',
    formattingStyle: 'as-written',
    // Per-template AI model config
    model: 'gpt-4.1-mini',
    maxTokens: 500,
    writingLevel: 'middle_school',
    aiTemperature: null,
    // Hyper-Intelligence Mode
    hyperIntelligence: false,
    ultraMode: false,
    personalizationGuide: '',
    // PDF Reference File (Hyper-Intelligence only)
    referenceFile: null,
    // Smart Greeting
    smartGreeting: false,
    greetingCasualPrefixes: ['Hey', 'Hi', 'Hello'],
    greetingFormalPrefixes: ['Dear'],
    // Smart Subject Line
    smartSubjectLine: false,
    smartSubjectStyle: 'neutral',
    smartSubjectInstructions: '',
    // Follow-up Subject Mode (only meaningful when type !== 'intro')
    // 'new' = use subjectLines variants (classic)
    // 'reply_to_previous' = reuse the contact's previous email subject with a reply prefix
    subjectMode: 'new',
    replyPrefix: 'AW', // 'AW' | 'RE' | 'custom'
    replyPrefixCustom: '',
    // Email Preference
    emailPreference: 'auto',
    // decision_maker only: 'info' uses the company inbox when no DM address
    // exists. 'strict' skips that contact. Absent means 'info'.
    dmFallback: 'info',
    callContext: DEFAULT_CALL_CONTEXT,
  });
  const [masterSaved, setMasterSaved] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    topic: false,
    subjectLines: [],
    instructions: []
  });
  const [aiAssistOpen, setAiAssistOpen] = useState(false);
  const [topbarMenuOpen, setTopbarMenuOpen] = useState(false);
  const [aiGenPopup, setAiGenPopup] = useState(null); // null | { type: 'subject'|'instruction', mode: 'generate'|'rewrite', rewriteIndex?: number, rewriteText?: string }
  // Variant Forge — derive siblings from one variant. null | { index, text }
  const [variantForge, setVariantForge] = useState(null);
  const [aiContextOpen, setAiContextOpen] = useState(true);
  const [varRefOpen, setVarRefOpen] = useState(false); // variable reference popup
  const [showAllVars, setShowAllVars] = useState(false);
  const [refFileUploading, setRefFileUploading] = useState(false);
  const backdropRefs = useRef({});
  const instructionRefs = useRef({});

  // Image Lab assets ({{image:token}} placeholders) — used for the "Insert image"
  // picker and to highlight image tokens distinctly in the editor backdrop.
  const [imageLabAssets, setImageLabAssets] = useState([]);
  useEffect(() => {
    if (!userId) return undefined;
    const assetsRef = ref(database, `/World/${userId}/settings/imageLab`);
    const unsub = onValue(assetsRef, (snap) => {
      const node = snap.exists() ? snap.val() : null;
      const list = node && typeof node === 'object'
        ? Object.values(node).filter((a) => a && a.token && (a.imageUrl || a.type === 'ai' || a.type === 'miniApp'))
        : [];
      setImageLabAssets(list);
    }, () => setImageLabAssets([]));
    return () => unsub();
  }, [userId]);

  // Render highlighted text — shows {…} and {{…}} blocks as colored backgrounds in the backdrop
  // CRITICAL: No extra elements, padding, or borders inside marks — must match textarea char-for-char
  const renderHighlightedText = (text) => {
    if (!text) return <span>{'\u00A0'}</span>;
    // Split on {{…}} (standalone DB variables) first, then {…} (AI prompts that may contain {{…}} inside)
    const parts = text.split(/(\{\{[^{}]+\}\}|\{(?:[^{}]|\{\{[^{}]*\}\})+\})/g);
    return parts.map((part, i) => {
      if (part.startsWith('{{') && part.endsWith('}}')) {
        // {{image:token}} = Image Lab placeholder (own colour), otherwise DB variable
        const cls = /^\{\{\s*image:/.test(part) ? 'ed-hl-block ed-hl-image' : 'ed-hl-block ed-hl-dbvar';
        return <mark key={i} className={cls}>{part}</mark>;
      } else if (part.startsWith('{') && part.endsWith('}')) {
        return (
          <mark key={i} className="ed-hl-block ed-hl-instruction">{part}</mark>
        );
      }
      return part;
    });
  };

  // Sync backdrop scroll with textarea scroll
  const syncScroll = (index) => (e) => {
    const backdrop = backdropRefs.current[index];
    if (backdrop) {
      backdrop.scrollTop = e.target.scrollTop;
      backdrop.scrollLeft = e.target.scrollLeft;
    }
  };

  // Insert a dynamic variable at cursor position in an instruction textarea
  const insertVariable = (variableKey, index) => {
    const textarea = instructionRefs.current[index];
    if (!textarea) return;
    
    // Use native input manipulation — no re-render flicker
    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? start;
    
    textarea.focus();
    textarea.setSelectionRange(start, end);
    
    // execCommand 'insertText' triggers onChange natively & keeps undo history
    document.execCommand('insertText', false, variableKey);
  };

  // AI Generation State
  const { contextFields: cfContextFields } = useCustomFields(userId);

  // Dynamic template variables available in the email system
  const dynamicKeywords = useMemo(() => {
    const base = [
      // Lead / Contact fields
      { key: '{{business_name}}', label: 'Business Name', icon: 'fa-building', description: 'Name of the lead\'s company' },
      { key: '{{lead_firstname}}', label: 'First Name', icon: 'fa-user', description: 'First name of the primary decision maker' },
      { key: '{{lead_lastname}}', label: 'Last Name', icon: 'fa-user-tag', description: 'Last name (legacy, use First Name instead)' },
      { key: '{{footer_text}}', label: 'Footer Text', icon: 'fa-pen-nib', description: 'Text snippet from the sending mailbox (e.g. Viele Grüße, Julian). Becomes part of the email body when generated — not the HTML footer.' },
      { key: '{{lead_name}}', label: 'Full Name', icon: 'fa-id-card', description: 'Full name of the primary decision maker' },
      { key: '{{lead_email}}', label: 'Email', icon: 'fa-envelope', description: 'Lead\'s email address' },
      { key: '{{lead_telephone}}', label: 'Phone', icon: 'fa-phone', description: 'Lead\'s phone number' },
      { key: '{{lead_address}}', label: 'Address', icon: 'fa-map-marker-alt', description: 'Lead\'s street address' },
      { key: '{{lead_city}}', label: 'City', icon: 'fa-city', description: 'Lead\'s city' },
      { key: '{{lead_postcode}}', label: 'Postcode', icon: 'fa-mail-bulk', description: 'Lead\'s postal code' },
      { key: '{{lead_country}}', label: 'Country', icon: 'fa-globe-europe', description: 'Lead\'s country' },
      { key: '{{lead_website}}', label: 'Website', icon: 'fa-globe', description: 'Lead\'s website URL' },
      { key: '{{lead_description}}', label: 'Description', icon: 'fa-align-left', description: 'Lead\'s business description' },
      { key: '{{lead_type}}', label: 'Type', icon: 'fa-tag', description: 'Lead\'s business type / category' },
      { key: '{{lead_instagram}}', label: 'Instagram', icon: 'fa-instagram', description: 'Lead\'s Instagram handle' },
      { key: '{{lead_linkedin}}', label: 'LinkedIn', icon: 'fa-linkedin', description: 'Lead\'s LinkedIn profile' },
      { key: '{{lead_facebook}}', label: 'Facebook', icon: 'fa-facebook', description: 'Lead\'s Facebook page' },
      { key: '{{lead_tiktok}}', label: 'TikTok', icon: 'fa-music', description: 'Lead\'s TikTok handle' },
      { key: '{{lead_rating}}', label: 'Rating', icon: 'fa-star', description: 'Lead\'s total rating' },
      { key: '{{lead_reviews}}', label: 'Reviews', icon: 'fa-comments', description: 'Lead\'s total review count' },
      // Recipient fields (resolves to selected DM or lead)
      { key: '{{recipient_name}}', label: 'Recipient Name', icon: 'fa-user-check', description: 'Name of the selected recipient (decision maker or lead)' },
      { key: '{{recipient_role}}', label: 'Recipient Role', icon: 'fa-user-tie', description: 'Role/title of the selected decision maker (empty if general email)' },
      // Sender / User fields
      { key: '{{user_name}}', label: 'Your Name', icon: 'fa-user-circle', description: 'Your sender name' },
      { key: '{{company_name}}', label: 'Your Company', icon: 'fa-briefcase', description: 'Your company name' },
      // SEO Analyzer (Sistrix) — populated after SEO scan. Empty string if no scan.
      { key: '{{seo_visibility}}', label: 'SEO Visibility', icon: 'fa-magnifying-glass-chart', description: 'Sistrix visibility index (e.g. 0.42)' },
      { key: '{{seo_keywords_total}}', label: 'SEO Keywords Total', icon: 'fa-list', description: 'Total ranking keywords on Google' },
      { key: '{{seo_keywords_top10}}', label: 'SEO Top-10 Keywords', icon: 'fa-trophy', description: 'Keywords ranking in Top 10' },
      { key: '{{seo_top_keyword}}', label: 'SEO Top Opportunity', icon: 'fa-bullseye', description: 'Best quick-win keyword (rank 4-10)' },
      { key: '{{seo_main_competitor}}', label: 'SEO Main Competitor', icon: 'fa-users', description: 'Main comparable SEO competitor domain' },
      { key: '{{seo_backlinks}}', label: 'Backlinks', icon: 'fa-link', description: 'Total backlinks pointing to the website' },
      { key: '{{seo_traffic}}', label: 'Est. Monthly Traffic', icon: 'fa-chart-line', description: 'Estimated organic visitors per month' },
      { key: '{{seo_traffic_value}}', label: 'Traffic Value (€)', icon: 'fa-euro-sign', description: 'Estimated monthly value of that traffic' },
      { key: '{{seo_trend}}', label: 'Visibility Trend', icon: 'fa-arrow-trend-down', description: 'Visibility change over time (e.g. -75% (12M))' },
    ];

    const cfVars = cfContextFields.map(f => ({
      key: `{{${f.key}}}`,
      label: f.label,
      icon: 'fa-puzzle-piece',
      description: `Custom field: ${f.label}`,
      isCustomField: true,
    }));

    return [...base, ...cfVars];
  }, [cfContextFields]);

  const { pinned: pinnedInsertVars, extra: extraInsertVars } = useMemo(
    () => partitionInsertVariables(dynamicKeywords, PINNED_INSERT_VAR_KEYS),
    [dynamicKeywords],
  );

  const [aiContext, setAiContext] = useState({
    businessDescription: '', // Describe your business / offer
    purpose: '',           // What's the goal of this email?
    targetAudience: '',    // Who is this email for?
    keyPoints: '',         // Key points to include
    userNote: '',      // Free-text instruction for the AI
    tone: 'neutral',  // formal, neutral, casual
    personality: '',           // Selected personality ID (e.g. 'alex')
    selectedFrameworks: [],    // Active framework IDs from that personality
    frameworkSlots: {},        // { [frameworkId]: { [slotKey]: 'fixed copy' } } — empty slots become {AI instructions}
    creativity: 'balanced', // 'close', 'balanced', 'creative'
    length: 'natural',     // tight | natural | roomy — scaled to the framework's beats
    language: 'German',    // German, English, etc.
    useEmojis: false,      // Include emojis?
    variantCount: 2,       // How many variants to generate (1-5)
    activeKeywords: ['{{lead_firstname}}', '{{business_name}}', '{{user_name}}'], // Which dynamic keywords to use
    useAccountBrain: true, // Pull copywriting notes from Account Brain (dataVault)
  });

  const [vaultEntriesMap, setVaultEntriesMap] = useState(null);

  const masterEditorOpen = !!masterEditTopic;

  useEffect(() => {
    if (!userId || !masterEditorOpen) {
      setVaultEntriesMap(null);
      return undefined;
    }
    const entriesRef = ref(database, `World/${userId}/dataVault/entries`);
    const unsubscribe = onValue(entriesRef, (snap) => {
      setVaultEntriesMap(snap.val() || {});
    });
    return () => unsubscribe();
  }, [userId, masterEditorOpen]);

  const vaultCopyEntries = useMemo(
    () => selectCopywritingVaultEntries(vaultEntriesMap),
    [vaultEntriesMap],
  );
  const vaultCopySummary = useMemo(
    () => summarizeVaultCopyContext(vaultCopyEntries),
    [vaultCopyEntries],
  );
  const vaultCopyPromptBlock = useMemo(
    () => buildVaultCopyPromptBlock(vaultCopyEntries),
    [vaultCopyEntries],
  );
  const vaultLoaded = vaultEntriesMap !== null;
  const brainHasCopyNotes = vaultCopySummary.count > 0;

  // ── i18n: Translate AI context labels & placeholders based on selected language ──
  const aiLabels = (() => {
    const t = {
      English: {
        aiContext: 'AI Context', optional: 'Optional',
        language: 'Language',
        describeBusiness: 'Describe Your Business', businessPlaceholder: 'e.g. We help local businesses get more customers through Google Ads and SEO...',
        goalPurpose: 'Goal / Purpose', goalPlaceholder: 'e.g. Introduce our service...',
        targetAudience: 'Target Audience', audiencePlaceholder: 'e.g. B2B SaaS founders...',
        keyPoints: 'Key Points', keyPointsPlaceholder: 'e.g. Mention free trial, ROI...',
        userNote: 'Your instructions', userNotePlaceholder: 'e.g. Focus on ROI, mention our free trial, keep it very direct...',
        tone: 'Tone', toneFormal: 'Formal', toneNeutral: 'Neutral', toneCasual: 'Casual',
        length: 'Length',
        outreachStyle: 'Outreach Style', outreachHint: 'Pick a proven approach — or skip for a general style. Tap again to deselect.',
        creativity: 'Creativity',
        creativityClose: 'Template-close', creativityCloseDesc: 'Stays close to your existing email style & structure',
        creativityBalanced: 'Balanced', creativityBalancedDesc: 'Follows your direction but adds fresh angles',
        creativityCreative: 'Creative', creativityCreativeDesc: 'Maximum freedom — new approaches & surprising ideas',
        rewriteClose: 'Polish', rewriteCloseDesc: 'Fix grammar & wording, keep almost identical',
        rewriteBalanced: 'Rework', rewriteBalancedDesc: 'Keep the core message, improve structure & impact',
        rewriteCreative: 'Free rewrite', rewriteCreativeDesc: 'Preserve intent only — everything else can change',
        variants: 'Variants', useEmojis: 'Use Emojis',
        dynamicVars: 'Dynamic Variables', dynamicVarsHint: 'Select which dynamic placeholders the AI should use.',
        cancel: 'Cancel', rewrite: 'Rewrite', generateSubjects: 'Generate Subject Lines', generateEmail: 'Generate Text Template',
        originalRewrite: 'Original text being rewritten:',
        subjectLines: 'Subject Lines', variant: 'variant', variants2: 'variants',
        smartSubjectTitle: 'Smart Subject Line',
        smartSubjectDesc: 'AI generates a unique subject line per contact based on their data',
        smartSubjectStyleLabel: 'Style',
        smartSubjectCuriosity: 'Curiosity', smartSubjectCuriosityDesc: 'Sparks interest, makes them want to open',
        smartSubjectDirect: 'Direct', smartSubjectDirectDesc: 'Clear & specific, no games',
        smartSubjectNeutral: 'Neutral', smartSubjectNeutralDesc: 'Professional, balanced tone',
        smartSubjectHumorous: 'Humorous', smartSubjectHumorousDesc: 'Witty, unexpected, stands out',
        smartSubjectTechnical: 'Technical', smartSubjectTechnicalDesc: 'Data-driven, references specifics',
        smartSubjectPersonal: 'Personal', smartSubjectPersonalDesc: 'Feels 1:1, references their world',
        smartSubjectInstructionsLabel: 'Instructions for the AI',
        smartSubjectInstructionsPlaceholder: 'e.g. Reference their product/service if possible. Keep under 6 words. Avoid clickbait. Use their first name if available...',
        smartSubjectNote: 'When active, AI creates a unique subject per contact at send time. Your static variants below serve as fallback if the AI call fails.',
        generalInfo: 'General Info', aiContextBadge: 'AI Context',
        aiTemperature: 'AI Creativity', aiTemperatureAuto: 'Auto',
        emailTexts: 'Email Texts', instruction: 'instruction', instructions2: 'instructions',
        accountBrain: 'Account Brain',
        accountBrainHint: 'Reuse notes from World Settings as the account source of truth.',
        accountBrainOn: 'On — copywriting notes go into every generation',
        accountBrainOff: 'Off — only the fields below are used',
        accountBrainEmpty: 'No copywriting notes yet. Add them in World Settings → Cockpit.',
        accountBrainNotes: 'notes',
        accountBrainFieldsHint: 'Optional extras for this template — Brain already covers the account facts.',
        chooseFramework: 'How the mail is built',
        chooseFrameworkHint: 'Pick the psychological structure. Tap again to clear.',
        fwGroupPlays: 'Signature plays 2026',
        fwGroupPlaysHint: 'Fillable, research-backed. Give-first CTAs instead of asking for a call.',
        fwGroupClassics: 'Classics',
        fwGroupClassicsHint: 'The proven structures. Prompt-only, no fields to fill.',
        fwSlotsTitle: 'Fill the play',
        fwSlotsHint: 'What you type becomes fixed text. What you leave empty becomes a smart AI instruction that gets resolved per lead from web research.',
        fwSlotsSuggest: 'Prefill from context',
        fwSlotsSuggesting: 'Thinking…',
        fwSlotsReset: 'Clear',
        fwSlotFixed: 'Fixed',
        fwSlotAuto: 'Dynamic',
        fwSkeleton: 'Structure preview',
        recentSetups: 'Recent setups',
        recentSetupsHint: 'Restores the full configuration of an earlier run — framework, fields, tone and context.',
        costEstimate: 'Estimated cost',
        credits: 'credits',
        variant: 'variant added',
        variants2: 'variants added',
        aiInstructions: 'AI instructions',
        genFailed: 'Generation failed',
        varNotInBody: 'Not resolved in the email body — subject lines only.',
        varMappedInBody: 'Mapped to the variable the email body actually resolves.',
        varBodyHint: 'The email body resolves fewer variables than the subject line. Greyed-out ones are skipped, the rest are mapped automatically.',
        sentences: 'sentences',
        lenTight: 'Tight', lenNatural: 'Natural', lenRoomy: 'Roomy',
        lengthFromFramework: 'scaled to the framework',
        lengthBeatsHint: 'Sentence budget per beat. Every beat stays in — a tighter setting shortens beats instead of dropping them.',
        lengthFreeHint: 'No framework selected, so the length is a plain sentence range.',
      },
      German: {
        aiContext: 'KI-Kontext', optional: 'Optional',
        language: 'Sprache',
        describeBusiness: 'Beschreibe dein Unternehmen', businessPlaceholder: 'z.B. Wir helfen lokalen Unternehmen, mehr Kunden über Google Ads und SEO zu gewinnen...',
        goalPurpose: 'Ziel / Zweck', goalPlaceholder: 'z.B. Unseren Service vorstellen...',
        targetAudience: 'Zielgruppe', audiencePlaceholder: 'z.B. B2B SaaS-Gründer...',
        keyPoints: 'Kernpunkte', keyPointsPlaceholder: 'z.B. Kostenlose Testphase erwähnen, ROI...',
        userNote: 'Deine Anweisungen', userNotePlaceholder: 'z.B. Fokus auf ROI, kostenlose Testphase erwähnen, sehr direkt halten...',
        tone: 'Tonalität', toneFormal: 'Formell', toneNeutral: 'Neutral', toneCasual: 'Locker',
        length: 'Länge',
        outreachStyle: 'Outreach-Stil', outreachHint: 'Wähle einen bewährten Ansatz — oder überspringe für einen allgemeinen Stil. Erneut tippen zum Abwählen.',
        creativity: 'Kreativität',
        creativityClose: 'Template-nah', creativityCloseDesc: 'Bleibt nah an deinem bestehenden Email-Stil & Aufbau',
        creativityBalanced: 'Ausgewogen', creativityBalancedDesc: 'Folgt deiner Richtung, bringt aber frische Ideen ein',
        creativityCreative: 'Kreativ', creativityCreativeDesc: 'Maximale Freiheit — neue Ansätze & überraschende Ideen',
        rewriteClose: 'Polieren', rewriteCloseDesc: 'Grammatik & Wording fixen, fast identisch behalten',
        rewriteBalanced: 'Überarbeiten', rewriteBalancedDesc: 'Kernbotschaft behalten, Struktur & Wirkung verbessern',
        rewriteCreative: 'Frei umschreiben', rewriteCreativeDesc: 'Nur die Intention behalten — alles andere darf sich ändern',
        variants: 'Varianten', useEmojis: 'Emojis verwenden',
        dynamicVars: 'Dynamische Variablen', dynamicVarsHint: 'Wähle, welche dynamischen Platzhalter die KI verwenden soll.',
        cancel: 'Abbrechen', rewrite: 'Umschreiben', generateSubjects: 'Betreffzeilen generieren', generateEmail: 'Text-Template generieren',
        originalRewrite: 'Originaltext wird umgeschrieben:',
        subjectLines: 'Betreffzeilen', variant: 'Variante', variants2: 'Varianten',
        smartSubjectTitle: 'Smarte Betreffzeile',
        smartSubjectDesc: 'KI erstellt pro Kontakt eine individuelle Betreffzeile anhand seiner Daten',
        smartSubjectStyleLabel: 'Stil',
        smartSubjectCuriosity: 'Neugier', smartSubjectCuriosityDesc: 'Weckt Interesse, macht neugierig',
        smartSubjectDirect: 'Direkt', smartSubjectDirectDesc: 'Klar & konkret, ohne Umwege',
        smartSubjectNeutral: 'Neutral', smartSubjectNeutralDesc: 'Professionell, ausgewogener Ton',
        smartSubjectHumorous: 'Humorvoll', smartSubjectHumorousDesc: 'Witzig, unerwartet, sticht heraus',
        smartSubjectTechnical: 'Technisch', smartSubjectTechnicalDesc: 'Datengetrieben, referenziert Spezifisches',
        smartSubjectPersonal: 'Persönlich', smartSubjectPersonalDesc: 'Wirkt 1:1, bezieht sich auf deren Welt',
        smartSubjectInstructionsLabel: 'Anweisungen für die KI',
        smartSubjectInstructionsPlaceholder: 'z.B. Wenn möglich auf ihr Produkt/Service eingehen. Unter 6 Wörtern halten. Kein Clickbait. Vornamen nutzen wenn verfügbar...',
        smartSubjectNote: 'Wenn aktiv, erstellt die KI beim Senden eine individuelle Betreffzeile pro Kontakt. Deine statischen Varianten unten dienen als Fallback, falls der KI-Aufruf fehlschlägt.',
        generalInfo: 'Allgemeine Info', aiContextBadge: 'KI-Kontext',
        aiTemperature: 'KI-Kreativität', aiTemperatureAuto: 'Auto',
        emailTexts: 'E-Mail-Texte', instruction: 'Anweisung', instructions2: 'Anweisungen',
        accountBrain: 'Account Brain',
        accountBrainHint: 'Nutzt die Notizen aus den World Settings als zentrale Wissensquelle.',
        accountBrainOn: 'An — Copywriting-Notizen fließen in jede Generierung',
        accountBrainOff: 'Aus — nur die Felder unten werden genutzt',
        accountBrainEmpty: 'Noch keine Copywriting-Notizen. Im Cockpit unter World Settings anlegen.',
        accountBrainNotes: 'Notizen',
        accountBrainFieldsHint: 'Optional für dieses Template — das Brain deckt die Account-Fakten schon ab.',
        chooseFramework: 'Wie die Mail aufgebaut wird',
        chooseFrameworkHint: 'Wähle den psychologischen Aufbau. Nochmal tippen zum Abwählen.',
        fwGroupPlays: 'Signature Plays 2026',
        fwGroupPlaysHint: 'Ausfüllbar, research-gestützt. Give-First-CTA statt Terminfrage.',
        fwGroupClassics: 'Klassiker',
        fwGroupClassicsHint: 'Die bewährten Strukturen. Nur Prompt, keine Felder.',
        fwSlotsTitle: 'Play ausfüllen',
        fwSlotsHint: 'Was du eintippst, wird fixer Text. Was du leer lässt, wird eine smarte KI-Anweisung, die pro Lead aus der Web Research gefüllt wird.',
        fwSlotsSuggest: 'Aus Kontext vorbefüllen',
        fwSlotsSuggesting: 'Denkt nach…',
        fwSlotsReset: 'Leeren',
        fwSlotFixed: 'Fix',
        fwSlotAuto: 'Dynamisch',
        fwSkeleton: 'Struktur-Vorschau',
        recentSetups: 'Letzte Setups',
        recentSetupsHint: 'Stellt die komplette Konfiguration eines früheren Laufs wieder her — Framework, Felder, Tonalität und Kontext.',
        costEstimate: 'Geschätzte Kosten',
        credits: 'Credits',
        variant: 'Variante erstellt',
        variants2: 'Varianten erstellt',
        aiInstructions: 'KI-Anweisungen',
        genFailed: 'Generierung fehlgeschlagen',
        varNotInBody: 'Wird im E-Mail-Text nicht aufgelöst — nur in der Betreffzeile.',
        varMappedInBody: 'Wird auf die Variable gemappt, die der E-Mail-Text wirklich auflöst.',
        varBodyHint: 'Der E-Mail-Text löst weniger Variablen auf als die Betreffzeile. Ausgegraute werden übersprungen, der Rest wird automatisch gemappt.',
        sentences: 'Sätze',
        lenTight: 'Knapp', lenNatural: 'Natürlich', lenRoomy: 'Ausführlich',
        lengthFromFramework: 'auf das Framework skaliert',
        lengthBeatsHint: 'Satz-Budget pro Beat. Jeder Beat bleibt drin — eine knappere Stufe kürzt die Beats, statt sie zu streichen.',
        lengthFreeHint: 'Kein Framework gewählt, deshalb gilt eine einfache Satz-Spanne.',
      },
      French: {
        aiContext: 'Contexte IA', optional: 'Optionnel',
        language: 'Langue',
        describeBusiness: 'Décrivez votre entreprise', businessPlaceholder: 'ex. Nous aidons les entreprises locales à attirer plus de clients via Google Ads et le SEO...',
        goalPurpose: 'Objectif / But', goalPlaceholder: 'ex. Présenter notre service...',
        targetAudience: 'Public cible', audiencePlaceholder: 'ex. Fondateurs B2B SaaS...',
        keyPoints: 'Points clés', keyPointsPlaceholder: 'ex. Mentionner l\'essai gratuit, le ROI...',
        tone: 'Ton', toneFormal: 'Formel', toneNeutral: 'Neutre', toneCasual: 'Décontracté',
        length: 'Longueur',
        outreachStyle: 'Style d\'approche', outreachHint: 'Choisissez une approche éprouvée — ou passez pour un style général. Tapez à nouveau pour désélectionner.',
        creativity: 'Créativité',
        creativityClose: 'Fidèle au modèle', creativityCloseDesc: 'Reste proche de votre style et structure existants',
        creativityBalanced: 'Équilibré', creativityBalancedDesc: 'Suit votre direction, apporte des idées fraîches',
        creativityCreative: 'Créatif', creativityCreativeDesc: 'Liberté maximale — nouvelles approches et idées surprenantes',
        rewriteClose: 'Polir', rewriteCloseDesc: 'Corriger grammaire et formulation, garder presque identique',
        rewriteBalanced: 'Retravailler', rewriteBalancedDesc: 'Garder le message principal, améliorer structure et impact',
        rewriteCreative: 'Réécriture libre', rewriteCreativeDesc: 'Ne garder que l\'intention — tout le reste peut changer',
        variants: 'Variantes', useEmojis: 'Utiliser des emojis',
        dynamicVars: 'Variables dynamiques', dynamicVarsHint: 'Sélectionnez les variables dynamiques que l\'IA doit utiliser.',
        cancel: 'Annuler', rewrite: 'Réécrire', generateSubjects: 'Générer des objets', generateEmail: 'Générer le text template',
        originalRewrite: 'Texte original en cours de réécriture :',
        aiTemperature: 'Créativité IA', aiTemperatureAuto: 'Auto',
      },
      Spanish: {
        aiContext: 'Contexto IA', optional: 'Opcional',
        language: 'Idioma',
        describeBusiness: 'Describe tu negocio', businessPlaceholder: 'ej. Ayudamos a negocios locales a conseguir más clientes con Google Ads y SEO...',
        goalPurpose: 'Objetivo / Propósito', goalPlaceholder: 'ej. Presentar nuestro servicio...',
        targetAudience: 'Público objetivo', audiencePlaceholder: 'ej. Fundadores de SaaS B2B...',
        keyPoints: 'Puntos clave', keyPointsPlaceholder: 'ej. Mencionar prueba gratuita, ROI...',
        tone: 'Tono', toneFormal: 'Formal', toneNeutral: 'Neutral', toneCasual: 'Informal',
        length: 'Extensión',
        outreachStyle: 'Estilo de contacto', outreachHint: 'Elige un enfoque probado — o sáltalo para un estilo general. Toca de nuevo para deseleccionar.',
        creativity: 'Creatividad',
        creativityClose: 'Fiel a plantilla', creativityCloseDesc: 'Se mantiene cerca de tu estilo y estructura existentes',
        creativityBalanced: 'Equilibrado', creativityBalancedDesc: 'Sigue tu dirección, aporta ideas frescas',
        creativityCreative: 'Creativo', creativityCreativeDesc: 'Máxima libertad — nuevos enfoques e ideas sorprendentes',
        rewriteClose: 'Pulir', rewriteCloseDesc: 'Corregir gramática y redacción, mantener casi idéntico',
        rewriteBalanced: 'Reelaborar', rewriteBalancedDesc: 'Mantener mensaje central, mejorar estructura e impacto',
        rewriteCreative: 'Reescritura libre', rewriteCreativeDesc: 'Solo conservar la intención — todo lo demás puede cambiar',
        variants: 'Variantes', useEmojis: 'Usar emojis',
        dynamicVars: 'Variables dinámicas', dynamicVarsHint: 'Selecciona qué marcadores dinámicos debe usar la IA.',
        cancel: 'Cancelar', rewrite: 'Reescribir', generateSubjects: 'Generar asuntos', generateEmail: 'Generar text template',
        originalRewrite: 'Texto original siendo reescrito:',
        aiTemperature: 'Creatividad IA', aiTemperatureAuto: 'Auto',
      },
      Italian: {
        aiContext: 'Contesto IA', optional: 'Opzionale',
        language: 'Lingua',
        describeBusiness: 'Descrivi la tua attività', businessPlaceholder: 'es. Aiutiamo le attività locali a ottenere più clienti tramite Google Ads e SEO...',
        goalPurpose: 'Obiettivo / Scopo', goalPlaceholder: 'es. Presentare il nostro servizio...',
        targetAudience: 'Pubblico target', audiencePlaceholder: 'es. Fondatori B2B SaaS...',
        keyPoints: 'Punti chiave', keyPointsPlaceholder: 'es. Menzionare prova gratuita, ROI...',
        tone: 'Tono', toneFormal: 'Formale', toneNeutral: 'Neutro', toneCasual: 'Informale',
        length: 'Lunghezza',
        outreachStyle: 'Stile di contatto', outreachHint: 'Scegli un approccio collaudato — o salta per uno stile generico. Tocca di nuovo per deselezionare.',
        creativity: 'Creatività',
        creativityClose: 'Fedele al modello', creativityCloseDesc: 'Resta vicino al tuo stile e struttura esistenti',
        creativityBalanced: 'Equilibrato', creativityBalancedDesc: 'Segue la tua direzione, aggiunge idee fresche',
        creativityCreative: 'Creativo', creativityCreativeDesc: 'Massima libertà — nuovi approcci e idee sorprendenti',
        rewriteClose: 'Lucidare', rewriteCloseDesc: 'Correggere grammatica e formulazione, mantenere quasi identico',
        rewriteBalanced: 'Rielaborare', rewriteBalancedDesc: 'Mantenere il messaggio chiave, migliorare struttura e impatto',
        rewriteCreative: 'Riscrittura libera', rewriteCreativeDesc: 'Conservare solo l\'intenzione — tutto il resto può cambiare',
        variants: 'Varianti', useEmojis: 'Usa emoji',
        dynamicVars: 'Variabili dinamiche', dynamicVarsHint: 'Seleziona quali segnaposto dinamici l\'IA dovrebbe usare.',
        cancel: 'Annulla', rewrite: 'Riscrivi', generateSubjects: 'Genera oggetti', generateEmail: 'Genera text template',
        originalRewrite: 'Testo originale in fase di riscrittura:',
        aiTemperature: 'Creatività IA', aiTemperatureAuto: 'Auto',
      },
      Portuguese: {
        aiContext: 'Contexto IA', optional: 'Opcional',
        language: 'Idioma',
        describeBusiness: 'Descreva o seu negócio', businessPlaceholder: 'ex. Ajudamos empresas locais a conseguir mais clientes com Google Ads e SEO...',
        goalPurpose: 'Objetivo / Propósito', goalPlaceholder: 'ex. Apresentar nosso serviço...',
        targetAudience: 'Público-alvo', audiencePlaceholder: 'ex. Fundadores de SaaS B2B...',
        keyPoints: 'Pontos-chave', keyPointsPlaceholder: 'ex. Mencionar teste grátis, ROI...',
        tone: 'Tom', toneFormal: 'Formal', toneNeutral: 'Neutro', toneCasual: 'Casual',
        length: 'Comprimento',
        outreachStyle: 'Estilo de abordagem', outreachHint: 'Escolha uma abordagem comprovada — ou pule para um estilo geral. Toque novamente para desselecionar.',
        creativity: 'Criatividade',
        creativityClose: 'Fiel ao modelo', creativityCloseDesc: 'Mantém-se próximo ao seu estilo e estrutura existentes',
        creativityBalanced: 'Equilibrado', creativityBalancedDesc: 'Segue sua direção, traz ideias frescas',
        creativityCreative: 'Criativo', creativityCreativeDesc: 'Máxima liberdade — novas abordagens e ideias surpreendentes',
        rewriteClose: 'Polir', rewriteCloseDesc: 'Corrigir gramática e redação, manter quase idêntico',
        rewriteBalanced: 'Retrabalhar', rewriteBalancedDesc: 'Manter mensagem central, melhorar estrutura e impacto',
        rewriteCreative: 'Reescrita livre', rewriteCreativeDesc: 'Preservar apenas a intenção — todo o resto pode mudar',
        variants: 'Variantes', useEmojis: 'Usar emojis',
        dynamicVars: 'Variáveis dinâmicas', dynamicVarsHint: 'Selecione quais marcadores dinâmicos a IA deve usar.',
        cancel: 'Cancelar', rewrite: 'Reescrever', generateSubjects: 'Gerar assuntos', generateEmail: 'Gerar text template',
        originalRewrite: 'Texto original sendo reescrito:',
        aiTemperature: 'Criatividade IA', aiTemperatureAuto: 'Auto',
      },
      Dutch: {
        aiContext: 'AI-context', optional: 'Optioneel',
        language: 'Taal',
        describeBusiness: 'Beschrijf je bedrijf', businessPlaceholder: 'bijv. Wij helpen lokale bedrijven meer klanten te krijgen via Google Ads en SEO...',
        goalPurpose: 'Doel / Reden', goalPlaceholder: 'bijv. Onze dienst voorstellen...',
        targetAudience: 'Doelgroep', audiencePlaceholder: 'bijv. B2B SaaS-oprichters...',
        keyPoints: 'Kernpunten', keyPointsPlaceholder: 'bijv. Gratis proefperiode noemen, ROI...',
        tone: 'Toon', toneFormal: 'Formeel', toneNeutral: 'Neutraal', toneCasual: 'Informeel',
        length: 'Lengte',
        outreachStyle: 'Benaderingsstijl', outreachHint: 'Kies een bewezen aanpak — of sla over voor een algemene stijl. Tik opnieuw om te deselecteren.',
        creativity: 'Creativiteit',
        creativityClose: 'Dicht bij sjabloon', creativityCloseDesc: 'Blijft dicht bij je bestaande e-mailstijl en structuur',
        creativityBalanced: 'Gebalanceerd', creativityBalancedDesc: 'Volgt je richting, brengt frisse ideeën',
        creativityCreative: 'Creatief', creativityCreativeDesc: 'Maximale vrijheid — nieuwe aanpakken en verrassende ideeën',
        rewriteClose: 'Oppoetsen', rewriteCloseDesc: 'Grammatica en formulering corrigeren, bijna identiek houden',
        rewriteBalanced: 'Herwerken', rewriteBalancedDesc: 'Kernboodschap behouden, structuur en impact verbeteren',
        rewriteCreative: 'Vrij herschrijven', rewriteCreativeDesc: 'Alleen de intentie behouden — al het andere mag veranderen',
        variants: 'Varianten', useEmojis: 'Emoji\'s gebruiken',
        dynamicVars: 'Dynamische variabelen', dynamicVarsHint: 'Selecteer welke dynamische placeholders de AI moet gebruiken.',
        cancel: 'Annuleren', rewrite: 'Herschrijven', generateSubjects: 'Onderwerpregels genereren', generateEmail: 'Text-template genereren',
        originalRewrite: 'Originele tekst wordt herschreven:',
        aiTemperature: 'AI-creativiteit', aiTemperatureAuto: 'Auto',
      },
      Polish: {
        aiContext: 'Kontekst AI', optional: 'Opcjonalnie',
        language: 'Język',
        describeBusiness: 'Opisz swoją firmę', businessPlaceholder: 'np. Pomagamy lokalnym firmom zdobyć więcej klientów przez Google Ads i SEO...',
        goalPurpose: 'Cel / Przeznaczenie', goalPlaceholder: 'np. Przedstawić naszą usługę...',
        targetAudience: 'Grupa docelowa', audiencePlaceholder: 'np. Założyciele B2B SaaS...',
        keyPoints: 'Kluczowe punkty', keyPointsPlaceholder: 'np. Wspomnieć o darmowym okresie próbnym, ROI...',
        tone: 'Ton', toneFormal: 'Formalny', toneNeutral: 'Neutralny', toneCasual: 'Swobodny',
        length: 'Długość',
        outreachStyle: 'Styl kontaktu', outreachHint: 'Wybierz sprawdzone podejście — lub pomiń, aby użyć ogólnego stylu. Kliknij ponownie, aby odznaczyć.',
        creativity: 'Kreatywność',
        creativityClose: 'Bliski szablonowi', creativityCloseDesc: 'Trzyma się blisko Twojego istniejącego stylu i struktury',
        creativityBalanced: 'Zbalansowany', creativityBalancedDesc: 'Podąża za Twoim kierunkiem, wnosi świeże pomysły',
        creativityCreative: 'Kreatywny', creativityCreativeDesc: 'Maksymalna swoboda — nowe podejścia i zaskakujące pomysły',
        rewriteClose: 'Szlifować', rewriteCloseDesc: 'Poprawić gramatykę i styl, zachować prawie identyczny',
        rewriteBalanced: 'Przerobić', rewriteBalancedDesc: 'Zachować kluczowy przekaz, poprawić strukturę i siłę',
        rewriteCreative: 'Swobodne przepisanie', rewriteCreativeDesc: 'Zachować tylko intencję — wszystko inne może się zmienić',
        variants: 'Warianty', useEmojis: 'Używaj emoji',
        dynamicVars: 'Zmienne dynamiczne', dynamicVarsHint: 'Wybierz, których dynamicznych znaczników AI ma użyć.',
        cancel: 'Anuluj', rewrite: 'Przepisz', generateSubjects: 'Generuj tematy', generateEmail: 'Generuj text template',
        originalRewrite: 'Oryginalny tekst jest przepisywany:',
        aiTemperature: 'Kreatywność AI', aiTemperatureAuto: 'Auto',
      },
      Turkish: {
        aiContext: 'AI Bağlamı', optional: 'İsteğe bağlı',
        language: 'Dil',
        describeBusiness: 'İşletmenizi tanımlayın', businessPlaceholder: 'ör. Yerel işletmelerin Google Ads ve SEO ile daha fazla müşteri kazanmasına yardımcı oluyoruz...',
        goalPurpose: 'Hedef / Amaç', goalPlaceholder: 'ör. Hizmetimizi tanıtmak...',
        targetAudience: 'Hedef Kitle', audiencePlaceholder: 'ör. B2B SaaS kurucuları...',
        keyPoints: 'Önemli Noktalar', keyPointsPlaceholder: 'ör. Ücretsiz deneme, ROI\'den bahset...',
        tone: 'Ton', toneFormal: 'Resmi', toneNeutral: 'Nötr', toneCasual: 'Rahat',
        length: 'Uzunluk',
        outreachStyle: 'İletişim Stili', outreachHint: 'Kanıtlanmış bir yaklaşım seçin — veya genel bir stil için atlayın. Seçimi kaldırmak için tekrar dokunun.',
        creativity: 'Yaratıcılık',
        creativityClose: 'Şablona yakın', creativityCloseDesc: 'Mevcut e-posta stilinize ve yapınıza yakın kalır',
        creativityBalanced: 'Dengeli', creativityBalancedDesc: 'Yönünüzü takip eder, taze fikirler ekler',
        creativityCreative: 'Yaratıcı', creativityCreativeDesc: 'Maksimum özgürlük — yeni yaklaşımlar ve sürpriz fikirler',
        rewriteClose: 'Cilala', rewriteCloseDesc: 'Dilbilgisi ve ifadeyi düzelt, neredeyse aynı tut',
        rewriteBalanced: 'Yeniden işle', rewriteBalancedDesc: 'Ana mesajı koru, yapıyı ve etkiyi geliştir',
        rewriteCreative: 'Serbest yeniden yaz', rewriteCreativeDesc: 'Sadece niyeti koru — geri kalan her şey değişebilir',
        variants: 'Varyantlar', useEmojis: 'Emoji kullan',
        dynamicVars: 'Dinamik Değişkenler', dynamicVarsHint: 'AI\'nin hangi dinamik yer tutucuları kullanacağını seçin.',
        cancel: 'İptal', rewrite: 'Yeniden yaz', generateSubjects: 'Konu satırları oluştur', generateEmail: 'Text template oluştur',
        originalRewrite: 'Yeniden yazılan orijinal metin:',
        aiTemperature: 'AI Yaratıcılığı', aiTemperatureAuto: 'Otomatik',
      },
      Swedish: {
        aiContext: 'AI-kontext', optional: 'Valfritt',
        language: 'Språk',
        describeBusiness: 'Beskriv ditt företag', businessPlaceholder: 't.ex. Vi hjälper lokala företag att få fler kunder genom Google Ads och SEO...',
        goalPurpose: 'Mål / Syfte', goalPlaceholder: 't.ex. Presentera vår tjänst...',
        targetAudience: 'Målgrupp', audiencePlaceholder: 't.ex. B2B SaaS-grundare...',
        keyPoints: 'Nyckelpunkter', keyPointsPlaceholder: 't.ex. Nämn gratis provperiod, ROI...',
        tone: 'Ton', toneFormal: 'Formell', toneNeutral: 'Neutral', toneCasual: 'Avslappnad',
        length: 'Längd',
        outreachStyle: 'Kontaktstil', outreachHint: 'Välj en beprövad metod — eller hoppa över för en allmän stil. Tryck igen för att avmarkera.',
        creativity: 'Kreativitet',
        creativityClose: 'Nära mallen', creativityCloseDesc: 'Håller sig nära din befintliga e-poststil och struktur',
        creativityBalanced: 'Balanserad', creativityBalancedDesc: 'Följer din riktning, tillför nya idéer',
        creativityCreative: 'Kreativ', creativityCreativeDesc: 'Maximal frihet — nya angreppssätt och överraskande idéer',
        rewriteClose: 'Putsa', rewriteCloseDesc: 'Fixa grammatik och formulering, behåll nästan identiskt',
        rewriteBalanced: 'Omarbeta', rewriteBalancedDesc: 'Behåll kärnbudskapet, förbättra struktur och genomslagskraft',
        rewriteCreative: 'Fri omskrivning', rewriteCreativeDesc: 'Bevara bara avsikten — allt annat kan ändras',
        variants: 'Varianter', useEmojis: 'Använd emojis',
        dynamicVars: 'Dynamiska variabler', dynamicVarsHint: 'Välj vilka dynamiska platshållare AI:n ska använda.',
        cancel: 'Avbryt', rewrite: 'Skriv om', generateSubjects: 'Generera ämnesrader', generateEmail: 'Generera text-template',
        originalRewrite: 'Originaltext skrivs om:',
        aiTemperature: 'AI-kreativitet', aiTemperatureAuto: 'Auto',
      },
      Japanese: {
        aiContext: 'AIコンテキスト', optional: '任意',
        language: '言語',
        describeBusiness: 'ビジネスの説明', businessPlaceholder: '例：Google広告とSEOで地域企業の集客を支援しています...',
        goalPurpose: '目標 / 目的', goalPlaceholder: '例：サービスを紹介する...',
        targetAudience: 'ターゲット', audiencePlaceholder: '例：B2B SaaS創業者...',
        keyPoints: '要点', keyPointsPlaceholder: '例：無料トライアル、ROIに言及...',
        tone: 'トーン', toneFormal: 'フォーマル', toneNeutral: 'ニュートラル', toneCasual: 'カジュアル',
        length: '長さ',
        outreachStyle: 'アウトリーチスタイル', outreachHint: '実績のあるアプローチを選択 — またはスキップして一般的なスタイルを使用。再度タップで選択解除。',
        creativity: '創造性',
        creativityClose: 'テンプレート準拠', creativityCloseDesc: '既存のメールスタイルと構造に近い',
        creativityBalanced: 'バランス', creativityBalancedDesc: '方向性に従いつつ新しいアイデアを追加',
        creativityCreative: '自由', creativityCreativeDesc: '最大限の自由 — 新しいアプローチと驚きのアイデア',
        rewriteClose: '修正', rewriteCloseDesc: '文法と表現を修正、ほぼ同一に保つ',
        rewriteBalanced: 'リワーク', rewriteBalancedDesc: 'コアメッセージを維持、構造と効果を改善',
        rewriteCreative: '自由書き換え', rewriteCreativeDesc: '意図のみ保持 — それ以外はすべて変更可能',
        variants: 'バリエーション', useEmojis: '絵文字を使う',
        dynamicVars: '動的変数', dynamicVarsHint: 'AIが使用する動的プレースホルダーを選択してください。',
        cancel: 'キャンセル', rewrite: '書き直す', generateSubjects: '件名を生成', generateEmail: 'テキストテンプレートを生成',
        originalRewrite: '書き直し中の元テキスト：',
        aiTemperature: 'AI創造性', aiTemperatureAuto: '自動',
      },
      Chinese: {
        aiContext: 'AI上下文', optional: '可选',
        language: '语言',
        describeBusiness: '描述您的业务', businessPlaceholder: '例如：我们帮助本地企业通过Google广告和SEO获得更多客户...',
        goalPurpose: '目标 / 目的', goalPlaceholder: '例如：介绍我们的服务...',
        targetAudience: '目标受众', audiencePlaceholder: '例如：B2B SaaS创始人...',
        keyPoints: '要点', keyPointsPlaceholder: '例如：提及免费试用、投资回报率...',
        tone: '语气', toneFormal: '正式', toneNeutral: '中性', toneCasual: '随意',
        length: '长度',
        outreachStyle: '外展风格', outreachHint: '选择一种经过验证的方法 — 或跳过使用通用风格。再次点击取消选择。',
        creativity: '创造力',
        creativityClose: '贴近模板', creativityCloseDesc: '紧贴您现有的邮件风格和结构',
        creativityBalanced: '平衡', creativityBalancedDesc: '遵循您的方向，加入新鲜想法',
        creativityCreative: '创意', creativityCreativeDesc: '最大自由 — 新方法和令人惊喜的想法',
        rewriteClose: '润色', rewriteCloseDesc: '修正语法和措辞，保持几乎相同',
        rewriteBalanced: '改写', rewriteBalancedDesc: '保留核心信息，改善结构和影响力',
        rewriteCreative: '自由改写', rewriteCreativeDesc: '仅保留意图 — 其他一切都可以改变',
        variants: '变体', useEmojis: '使用表情符号',
        dynamicVars: '动态变量', dynamicVarsHint: '选择AI应使用的动态占位符。',
        cancel: '取消', rewrite: '重写', generateSubjects: '生成主题行', generateEmail: '生成文本模板',
        originalRewrite: '正在重写的原始文本：',
        aiTemperature: 'AI创造力', aiTemperatureAuto: '自动',
      },
    };
    // Non-English/German locales only cover part of the key set — fall back per key.
    return { ...t.English, ...(t[aiContext.language] || {}) };
  })();

  // Email Personalities — imported from shared module (emailPersonalities.js)
  const [aiGenerating, setAiGenerating] = useState(null); // null | { type, step: 1|2, variant, variantTarget }
  const [slotSuggesting, setSlotSuggesting] = useState(false);
  const [skeletonOpen, setSkeletonOpen] = useState(false);
  // Outcome of the last run: credits actually charged + brace lint findings.
  const [aiGenResult, setAiGenResult] = useState(null);
  const [genPresets, setGenPresets] = useState([]);


  // Vereinfache die verfügbaren Topic-Typen
  const topicTypes = [
    { value: 'intro', label: 'Introduction Email' },
    { value: 'follow_up_1', label: 'First Follow-Up' },
    { value: 'follow_up_2', label: 'Second Follow-Up' },
    { value: 'follow_up_3', label: 'Third Follow-Up' },
    { value: 'custom_reply', label: 'Custom Template' }
  ];

  // Lade verfügbare Topics
  useEffect(() => {
    if (!userId) return undefined;
    let active = true;
    setTopicsReady(false);
    
    // Lade alle benötigten Daten in einer koordinierten Weise.
    // get() rejected bei permission_denied. onValue(..., { onlyOnce }) ohne
    // Cancel-Callback tut das nicht und lässt das Promise offen. Der Katalog
    // unter /WorldSettings/topicsTemplate ist admin-only — ein hängender Read
    // dort darf die eigenen Templates nicht blockieren.
    const readOnce = (dbRef) => get(dbRef).catch((error) => {
      console.warn("Topic-Read übersprungen:", error?.code || error?.message || error);
      return null;
    });

    const loadAllTopicData = async () => {
      try {
        const selectedTopicsRef = ref(database, `/World/${userId}/settings/selectedTopics`);
        const customTopicsRef = ref(database, `/World/${userId}/settings/customTopics`);

        const [selectedSnapshot, customSnapshot] = await Promise.all([
          readOnce(selectedTopicsRef),
          readOnce(customTopicsRef),
        ]);

        if (!active) return;

        if (selectedSnapshot?.exists()) {
          setSelectedTopics(selectedSnapshot.val());
        } else if (selectedSnapshot) {
          setSelectedTopics({});
        }

        if (customSnapshot?.exists()) {
          setCustomTopics(customSnapshot.val());
        } else if (customSnapshot) {
          setCustomTopics({});
        }

        console.log("Alle Topic-Daten erfolgreich geladen");
      } catch (error) {
        console.error("Fehler beim Laden der Topic-Daten:", error);
      } finally {
        if (active) setTopicsReady(true);
      }
    };
    
    // Initial alle Daten laden
    loadAllTopicData();
    
    // Dann Live-Updates für jede Datenquelle einrichten
    const availableTopicsRef = ref(database, '/WorldSettings/topicsTemplate');
    const selectedTopicsRef = ref(database, `/World/${userId}/settings/selectedTopics`);
    const customTopicsRef = ref(database, `/World/${userId}/settings/customTopics`);
    
    const unsubscribeAvailable = onValue(availableTopicsRef, (snapshot) => {
      if (snapshot.exists()) {
        setAvailableTopics(snapshot.val());
      }
    });
    
    const unsubscribeSelected = onValue(selectedTopicsRef, (snapshot) => {
      if (snapshot.exists()) {
        setSelectedTopics(snapshot.val());
      } else {
        setSelectedTopics({});
      }
    });
    
    const unsubscribeCustom = onValue(customTopicsRef, (snapshot) => {
      if (snapshot.exists()) {
        setCustomTopics(snapshot.val());
      } else {
        setCustomTopics({});
      }
    });
    
    return () => {
      active = false;
      unsubscribeAvailable();
      unsubscribeSelected();
      unsubscribeCustom();
    };
  }, [userId]);

  // Lade AI Tags aus der Datenbank
  useEffect(() => {
    if (!userId) return;
    
    const aiTagsRef = ref(database, `/World/${userId}/settings/aiTags`);
    const unsubscribe = onValue(aiTagsRef, (snapshot) => {
      if (snapshot.exists()) {
        const tagsData = [];
        snapshot.forEach((childSnapshot) => {
          tagsData.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        setAiTags(tagsData);
        console.log('🏷️ [TOPICS-SETTINGS] Loaded AI tags:', tagsData);
      } else {
        setAiTags([]);
      }
    });
    
    return () => unsubscribe();
  }, [userId]);

  // Aktualisiere die Funktion zum Gruppieren der Topics nach Typ
  const groupTopicsByType = () => {
    const groupedTopics = {
      intro: [],
      follow_up_1: [],
      follow_up_2: [],
      follow_up_3: [],
      custom_reply: []
    };

    // Füge nur benutzerdefinierte Topics hinzu
    Object.entries(customTopics).forEach(([id, topicData]) => {
      const type = topicData.type || 'intro';
      if (groupedTopics[type]) {
        groupedTopics[type].push({
          id,
          ...topicData,
          isCustom: true
        });
      }
    });

    return groupedTopics;
  };

  // Finde das ausgewählte Topic für einen bestimmten Typ
  const getSelectedTopicForType = (type) => {
    if (Object.keys(selectedTopics).length === 0) return null;
    
    if (type === 'custom_reply') {
      // Für custom_reply: Gib alle ausgewählten Topics zurück
      const customReplyTopics = Object.entries(selectedTopics)
        .filter(([_, data]) => data.type === 'custom_reply')
        .map(([id, _]) => id);
      
      return customReplyTopics.length > 0 ? customReplyTopics : null;
    } else {
      // Für andere Typen: Suche nach einem Topic mit dem angegebenen Typ
      const found = Object.entries(selectedTopics).find(([_, data]) => data.type === type);
      
      // Wenn gefunden, gib die ID zurück
      if (found) {
        return found[0];
      }
      
      return null;
    }
  };

  const handleTopicToggle = async (topicId, topicData) => {
    // Verhindere mehrfache schnelle Klicks
    if (isProcessing) return;
    setIsProcessing(true);
    
    try {
      const updatedTopics = { ...selectedTopics };
      
      // Normalize data to new format with IDs
      const normalizedSubjectLines = topicData.subjectLines 
        ? normalizeSubjectLines(topicData.subjectLines)
        : topicData.subjectLine 
          ? [{ id: generateLegacyId(topicData.subjectLine), text: topicData.subjectLine }]
          : [];
      
      const normalizedInstructions = topicData.topicSpecificNotes
        ? normalizeInstructions(topicData.topicSpecificNotes)
        : topicData.topicSpecificNote
          ? [{ id: generateLegacyId(topicData.topicSpecificNote), text: topicData.topicSpecificNote }]
          : [];
      
      // Unterschiedliche Logik für custom_reply und andere Topic-Typen
      if (topicData.type === 'custom_reply') {
        // Für custom_reply: Toggle-Verhalten (hinzufügen/entfernen)
        if (updatedTopics[topicId]) {
          // Wenn bereits ausgewählt, entferne es
          delete updatedTopics[topicId];
        } else {
          // Wenn nicht ausgewählt, füge es hinzu
          updatedTopics[topicId] = {
            topic: topicData.topic,
            ...(normalizedInstructions.length > 0 && { topicSpecificNotes: normalizedInstructions }),
            type: topicData.type,
            ...(normalizedSubjectLines.length > 0 && { subjectLines: normalizedSubjectLines }),
            ...(topicData.isCustom && { isCustom: true }),
            aiTagId: topicData.aiTagId
          };
        }
      } else {
        // Für andere Topic-Typen: Nur eines auswählen
        const currentSelectedTopicId = getSelectedTopicForType(topicData.type);
        
        // Wenn das gleiche Topic bereits ausgewählt ist, entferne es
        if (currentSelectedTopicId === topicId) {
          delete updatedTopics[topicId];
        } else {
          // Wenn ein anderes Topic des gleichen Typs ausgewählt ist, entferne es
          if (currentSelectedTopicId) {
            delete updatedTopics[currentSelectedTopicId];
          }
          
          // Füge das neue Topic hinzu
          updatedTopics[topicId] = {
            topic: topicData.topic,
            ...(normalizedInstructions.length > 0 && { topicSpecificNotes: normalizedInstructions }),
            type: topicData.type,
            ...(normalizedSubjectLines.length > 0 && { subjectLines: normalizedSubjectLines }),
            ...(topicData.isCustom && { isCustom: true }),
            aiTagId: topicData.aiTagId
          };
        }
      }
      
      // Optimistisches UI-Update
      setSelectedTopics(updatedTopics);
      
      // Batch-Update für die Datenbank
      const batch = {};
      
      // Speichere die ausgewählten Topics
      batch[`/World/${userId}/settings/selectedTopics`] = updatedTopics;
      
      // Erstelle Topic-Mapping
      const topicMapping = {};
      Object.entries(updatedTopics).forEach(([id, data]) => {
        // Für custom_reply: Speichere alle ausgewählten Topics in einem Array
        if (data.type === 'custom_reply') {
          if (!topicMapping[data.type]) {
            topicMapping[data.type] = [];
          }
          topicMapping[data.type].push(id);
        } else {
          // Für andere Typen: Speichere nur die ID
          topicMapping[data.type] = id;
        }
      });
      
      batch[`/World/${userId}/settings/topicMapping`] = topicMapping;
      
      // Führe alle Updates in einer Transaktion durch
      await update(ref(database), batch);
      
      setMessage(`Topic ${updatedTopics[topicId] ? 'selected' : 'deselected'} successfully.`);
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error updating topics:", error);
      setMessage("Error updating topics. Please try again.");
      
      // Bei Fehler den State zurücksetzen
      const userTopicsRef = ref(database, `/World/${userId}/settings/selectedTopics`);
      onValue(userTopicsRef, (snapshot) => {
        if (snapshot.exists()) {
          setSelectedTopics(snapshot.val());
        } else {
          setSelectedTopics({});
        }
      }, { onlyOnce: true });
    } finally {
      setIsProcessing(false);
    }
  };

  // Starte die Bearbeitung eines benutzerdefinierten Topics
  const handleEditCustomTopic = (topicId) => {
    const topicData = customTopics[topicId];
    if (topicData) {
      setEditingTopicId(topicId);
      // Normalize old data format to new format with IDs
      const subjectLines = topicData.subjectLines 
        ? normalizeSubjectLines(topicData.subjectLines)
        : topicData.subjectLine 
          ? [{ id: generateLegacyId(topicData.subjectLine), text: topicData.subjectLine }]
          : [createSubjectLine()];
      
      const topicSpecificNotes = topicData.topicSpecificNotes
        ? normalizeInstructions(topicData.topicSpecificNotes)
        : topicData.topicSpecificNote
          ? [{ id: generateLegacyId(topicData.topicSpecificNote), text: topicData.topicSpecificNote }]
          : [createInstruction()];
      
      setNewTopic({
        topic: topicData.topic,
        topicSpecificNotes,
        type: topicData.type || 'intro',
        subjectLines,
        aiTagId: topicData.aiTagId || ''
      });
      setShowModal(true);
    }
  };

  // Aktualisiere die Funktion zum Hinzufügen oder Aktualisieren eines benutzerdefinierten Topics
  const handleSaveCustomTopic = async (e) => {
    e.preventDefault();
    
    console.log('💾 [SAVE] Attempting to save topic:', newTopic);
    
    // Filtere leere Subject Lines und Instructions heraus (jetzt mit .text Property)
    const validSubjectLines = newTopic.subjectLines.filter(line => line.text && line.text.trim() !== '');
    const validInstructions = newTopic.topicSpecificNotes.filter(note => note.text && note.text.trim() !== '');
    
    console.log('💾 [SAVE] Valid subject lines:', validSubjectLines);
    console.log('💾 [SAVE] Valid instructions:', validInstructions);
    
    if (!newTopic.topic || validSubjectLines.length === 0 || validInstructions.length === 0) {
      console.log('❌ [SAVE] Validation failed:', {
        hasTopic: !!newTopic.topic,
        hasSubjectLines: validSubjectLines.length > 0,
        hasInstructions: validInstructions.length > 0
      });
      setMessage("Please fill in all required fields, add at least one subject line, and at least one instruction.");
      return;
    }

    try {
      console.log('✅ [SAVE] Validation passed, saving to database...');
      setIsProcessing(true);
      const customTopicsRef = ref(database, `/World/${userId}/settings/customTopics`);
      
      // Stelle sicher, dass der Topic-Typ einer der erlaubten Typen ist
      if (!['intro', 'follow_up_1', 'follow_up_2', 'follow_up_3', 'custom_reply'].includes(newTopic.type)) {
        newTopic.type = 'custom_reply'; // Standardmäßig auf custom_reply setzen
      }
      
      // Bestimme, ob ein bestehendes Topic bearbeitet oder ein neues erstellt wird
      const topicId = editingTopicId || `custom_${Date.now()}`;
      
      await update(customTopicsRef, {
        [topicId]: {
          topic: newTopic.topic,
          topicSpecificNotes: validInstructions, // Array mit {id, text} Objekten
          type: newTopic.type,
          subjectLines: validSubjectLines, // Array mit {id, text} Objekten
          aiTagId: newTopic.aiTagId || "",
          // Remove legacy fields (set to null to delete from Firebase)
          subjectLine: null,
          topicSpecificNote: null
        }
      });
      
      // Aktualisiere auch die ausgewählten Topics, falls das bearbeitete Topic ausgewählt war
      if (selectedTopics[topicId]) {
        const selectedTopicsRef = ref(database, `/World/${userId}/settings/selectedTopics/${topicId}`);
        await update(selectedTopicsRef, {
          topic: newTopic.topic,
          topicSpecificNotes: validInstructions, // Array mit {id, text} Objekten
          type: newTopic.type,
          subjectLines: validSubjectLines, // Array mit {id, text} Objekten
          isCustom: true,
          aiTagId: newTopic.aiTagId || "",
          // Remove legacy fields here too
          subjectLine: null,
          topicSpecificNote: null
        });
      }
      
      // Zurücksetzen der Formularfelder
      setNewTopic({
        topic: "",
        topicSpecificNotes: [createInstruction()],
        type: "intro",
        subjectLines: [createSubjectLine()],
        aiTagId: ""
      });
      
      setEditingTopicId(null);
      setShowModal(false);
      setMessage(editingTopicId ? "Topic successfully updated." : "Custom topic successfully added.");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error saving custom topic", error);
      setMessage("Error saving custom topic.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteCustomTopic = async (topicId) => {
    try {
      // Entferne das Topic aus den benutzerdefinierten Topics
      const customTopicsRef = ref(database, `/World/${userId}/settings/customTopics/${topicId}`);
      await remove(customTopicsRef);
      
      // Wenn es auch in den ausgewählten Topics ist, entferne es dort auch
      if (selectedTopics[topicId]) {
        const updatedSelectedTopics = { ...selectedTopics };
        delete updatedSelectedTopics[topicId];
        
        const selectedTopicsRef = ref(database, `/World/${userId}/settings/selectedTopics`);
        await update(selectedTopicsRef, updatedSelectedTopics);
        
        // Aktualisiere den lokalen State
        setSelectedTopics(updatedSelectedTopics);
        
        // Aktualisiere das Topic-Mapping
        const topicMappingRef = ref(database, `/World/${userId}/settings/topicMapping`);
        const topicMapping = {};
        
        // Erstelle eine Zuordnung von Typ zu ID
        Object.entries(updatedSelectedTopics).forEach(([id, data]) => {
          topicMapping[data.type] = id;
        });
        
        await update(topicMappingRef, topicMapping);
      }
      
      // Aktualisiere den lokalen State für benutzerdefinierte Topics
      const updatedCustomTopics = { ...customTopics };
      delete updatedCustomTopics[topicId];
      setCustomTopics(updatedCustomTopics);
      
      setMessage("Custom topic successfully deleted.");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting custom topic", error);
      setMessage("Error deleting custom topic.");
    }
  };

  // Dupliziert ein Custom-Template unter einem neuen Namen.
  // Es werden alle Settings übernommen, aber: das Duplikat wird NICHT aktiviert
  // (kein Eintrag in selectedTopics) und startet ohne Performance-Statistik.
  const handleDuplicateCustomTopic = async (sourceTopicId, rawName) => {
    const source = customTopics[sourceTopicId];
    if (!source) {
      setMessage("Template not found.");
      return;
    }

    const newName =
      (rawName || "").trim() || `${source.topic || "Template"} (Copy)`;

    setIsProcessing(true);
    try {
      const newTopicId = `custom_${Date.now()}`;

      // Tiefe Kopie aller Settings, dann Identitäts-/Laufzeitfelder entfernen,
      // damit das Duplikat sauber als neues Template startet.
      const clone = JSON.parse(JSON.stringify(source));
      delete clone.id; // Key wird nie im Knoten selbst gespeichert
      delete clone.stats; // frische Statistik für die Kopie

      // Voice-Clone-Templates tracken Reply-Rates pro Variante — die müssen
      // beim Duplizieren ebenfalls zurückgesetzt werden.
      if (clone.cloneConfig && Array.isArray(clone.cloneConfig.variants)) {
        clone.cloneConfig.variants = clone.cloneConfig.variants.map((variant) => {
          const { stats, ...rest } = variant;
          return rest;
        });
      }

      clone.topic = newName;
      clone.isCustom = true;

      const newRef = ref(
        database,
        `/World/${userId}/settings/customTopics/${newTopicId}`
      );
      await set(newRef, clone);

      // Optimistisches Update — der onValue-Listener synchronisiert ebenfalls.
      setCustomTopics((prev) => ({ ...prev, [newTopicId]: clone }));

      setMessage(`Template duplicated as "${newName}".`);
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error duplicating custom topic", error);
      setMessage("Error duplicating template.");
    } finally {
      setIsProcessing(false);
    }
  };

  // Funktion zum Abbrechen der Bearbeitung
  const handleCancelEdit = () => {
    setEditingTopicId(null);
    setNewTopic({
      topic: "",
      topicSpecificNotes: [createInstruction()],
      type: "intro",
      subjectLines: [createSubjectLine()],
      aiTagId: ""
    });
    setShowModal(false);
  };

  // Funktionen für das Verwalten der Subject Lines (mit ID-Objekten)
  const addSubjectLine = () => {
    setNewTopic({
      ...newTopic,
      subjectLines: [...newTopic.subjectLines, createSubjectLine()]
    });
  };

  const removeSubjectLine = (index) => {
    if (newTopic.subjectLines.length > 1) {
      const updatedLines = newTopic.subjectLines.filter((_, i) => i !== index);
      setNewTopic({
        ...newTopic,
        subjectLines: updatedLines
      });
    }
  };

  const updateSubjectLine = (index, value) => {
    const updatedLines = [...newTopic.subjectLines];
    updatedLines[index] = { ...updatedLines[index], text: value };
    setNewTopic({
      ...newTopic,
      subjectLines: updatedLines
    });
  };

  // Funktionen für das Verwalten der Instructions (mit ID-Objekten)
  const addInstruction = () => {
    setNewTopic({
      ...newTopic,
      topicSpecificNotes: [...newTopic.topicSpecificNotes, createInstruction()]
    });
  };

  const removeInstruction = (index) => {
    if (newTopic.topicSpecificNotes.length > 1) {
      const updatedNotes = newTopic.topicSpecificNotes.filter((_, i) => i !== index);
      setNewTopic({
        ...newTopic,
        topicSpecificNotes: updatedNotes
      });
    }
  };

  const updateInstruction = (index, value) => {
    const updatedNotes = [...newTopic.topicSpecificNotes];
    updatedNotes[index] = { ...updatedNotes[index], text: value };
    setNewTopic({
      ...newTopic,
      topicSpecificNotes: updatedNotes
    });
  };

  // Funktionen für Inline-Editing der Subject Lines (mit ID-Objekten)
  const addInlineSubjectLine = () => {
    setInlineEditData({
      ...inlineEditData,
      subjectLines: [...inlineEditData.subjectLines, createSubjectLine()]
    });
  };

  const removeInlineSubjectLine = (index) => {
    if (inlineEditData.subjectLines.length > 1) {
      const updatedLines = inlineEditData.subjectLines.filter((_, i) => i !== index);
      setInlineEditData({
        ...inlineEditData,
        subjectLines: updatedLines
      });
    }
  };

  const updateInlineSubjectLine = (index, value) => {
    const updatedLines = [...inlineEditData.subjectLines];
    updatedLines[index] = { ...updatedLines[index], text: value };
    setInlineEditData({
      ...inlineEditData,
      subjectLines: updatedLines
    });
  };

  // Funktionen für Inline-Editing der Instructions (mit ID-Objekten)
  const addInlineInstruction = () => {
    setInlineEditData({
      ...inlineEditData,
      topicSpecificNotes: [...inlineEditData.topicSpecificNotes, createInstruction()]
    });
  };

  const removeInlineInstruction = (index) => {
    if (inlineEditData.topicSpecificNotes.length > 1) {
      const updatedNotes = inlineEditData.topicSpecificNotes.filter((_, i) => i !== index);
      setInlineEditData({
        ...inlineEditData,
        topicSpecificNotes: updatedNotes
      });
    }
  };

  const updateInlineInstruction = (index, value) => {
    const updatedNotes = [...inlineEditData.topicSpecificNotes];
    updatedNotes[index] = { ...updatedNotes[index], text: value };
    setInlineEditData({
      ...inlineEditData,
      topicSpecificNotes: updatedNotes
    });
  };

  // ===== EMAIL DIRECTOR - Master Editor Functions =====
  
  const openMasterEditor = useCallback((topicId, topicData) => {
    const normalizedSubjectLines = topicData.subjectLines 
      ? normalizeSubjectLines(topicData.subjectLines)
      : topicData.subjectLine 
        ? [{ id: generateLegacyId(topicData.subjectLine), text: topicData.subjectLine }]
        : [createSubjectLine()];
    
    const normalizedInstructions = topicData.topicSpecificNotes
      ? normalizeInstructions(topicData.topicSpecificNotes)
      : topicData.topicSpecificNote
        ? [{ id: generateLegacyId(topicData.topicSpecificNote), text: topicData.topicSpecificNote }]
        : [createInstruction()];
    
    setMasterEditTopic({ id: topicId, ...topicData });
    const templateLanguage = topicData.language || null;
    setMasterEditData({
      topic: topicData.topic || '',
      subjectLines: normalizedSubjectLines,
      topicSpecificNotes: normalizedInstructions,
      aiTagId: topicData.aiTagId || '',
      type: topicData.type || 'intro',
      generalInfo: topicData.generalInfo || '',
      chanceRadarId: topicData.chanceRadarId || '',
      aiAdherence: topicData.aiAdherence || 'exact',
      formattingStyle: topicData.formattingStyle || 'as-written',
      model: topicData.model || 'gpt-4.1-mini',
      maxTokens: topicData.maxTokens || 500,
      writingLevel: topicData.writingLevel || 'middle_school',
      aiTemperature: topicData.aiTemperature != null ? topicData.aiTemperature : null,
      hyperIntelligence: topicData.hyperIntelligence || false,
      ultraMode: topicData.ultraMode || false,
      personalizationGuide: topicData.personalizationGuide || '',
      referenceFile: topicData.referenceFile || null,
      smartGreeting: topicData.smartGreeting || false,
      greetingCasualPrefixes: Array.isArray(topicData.greetingCasualPrefixes) ? topicData.greetingCasualPrefixes : (Array.isArray(topicData.greetingPrefixes) ? topicData.greetingPrefixes : ['Hey', 'Hi', 'Hello']),
      greetingFormalPrefixes: Array.isArray(topicData.greetingFormalPrefixes) ? topicData.greetingFormalPrefixes : ['Dear'],
      smartSubjectLine: topicData.smartSubjectLine || false,
      smartSubjectStyle: topicData.smartSubjectStyle || 'neutral',
      smartSubjectInstructions: topicData.smartSubjectInstructions || '',
      subjectMode: topicData.subjectMode === 'reply_to_previous' ? 'reply_to_previous' : 'new',
      replyPrefix: ['AW', 'RE', 'custom'].includes(topicData.replyPrefix) ? topicData.replyPrefix : 'AW',
      replyPrefixCustom: topicData.replyPrefixCustom || '',
      emailPreference: topicData.emailPreference || 'auto',
      dmFallback: topicData.dmFallback === 'strict' ? 'strict' : 'info',
      callContext: normalizeCallContext(topicData.callContext),
      language: templateLanguage || 'German',
    });
    if (templateLanguage) {
      setAiContext(prev => ({ ...prev, language: templateLanguage }));
    }
    setMasterSaved(false);
    setValidationErrors({ topic: false, subjectLines: [], instructions: [] });
    setAiAssistOpen(false);

    // Topic-ID in die URL schreiben
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('topicId', topicId);
      return next;
    });
  }, [setSearchParams]);

  // ===== Voice Clone — new template creation dispatcher =====
  // Called from the ModePickerModal after the user picks Standard / Voice Clone.
  // Standard mode reproduces the original "+ Add New" inline logic; Voice
  // Clone creates a brand-new shape and immediately opens the dedicated editor.
  // Compute a non-colliding default template name for a given type.
  const computeDefaultTopicName = useCallback((type) => {
    const typeLabel = topicTypes.find(t => t.value === type)?.label || 'Template';
    const baseName = `New ${typeLabel}`;
    const allCustom = Object.values(customTopics || {});
    const existingNames = new Set(allCustom.map(t => t.topic));
    let defaultTopicName = baseName;
    if (existingNames.has(defaultTopicName)) {
      let counter = 1;
      while (existingNames.has(`${baseName} ${counter}`)) counter++;
      defaultTopicName = `${baseName} ${counter}`;
    }
    return defaultTopicName;
  }, [topicTypes, customTopics]);

  const handleNewTemplateMode = useCallback((mode) => {
    const type = pendingNewType || 'intro';
    setShowModeModal(false);

    if (mode === TEMPLATE_MODES.VOICE_CLONE) {
      // CopyTwin Mode — no info entered yet → let the user pick a CopyTwin and
      // optionally auto-setup the whole template in one click. We KEEP
      // pendingNewType so the auto-setup callback knows the template type.
      setShowAutoSetupModal(true);
      return;
    }

    setPendingNewType(null);

    // Standard Mode — same shape as before, opened in the legacy master editor.
    const newTopicId = `custom_${Date.now()}`;
    const newTopicData = {
      topic: computeDefaultTopicName(type),
      topicSpecificNotes: [createInstruction()],
      type,
      subjectLines: [createSubjectLine()],
      aiTagId: '',
      isCustom: true,
      mode: TEMPLATE_MODES.STANDARD,
    };
    openMasterEditor(newTopicId, newTopicData);
  }, [pendingNewType, computeDefaultTopicName, openMasterEditor]);

  // CopyTwin auto-setup callback — fired from CopyTwinAutoSetupModal once the
  // user either ran the one-click auto-setup (setup != null) or chose to set
  // things up manually (setup == null). Builds the template (factory defaults
  // + any derived brief/language/structure) and opens the dedicated editor.
  const handleCloneTemplateReady = useCallback(({ cloneId, setup }) => {
    const type = pendingNewType || 'intro';
    setShowAutoSetupModal(false);
    setPendingNewType(null);

    const newTopicId = `custom_${Date.now()}`;
    let newCloneTopic = createVoiceCloneTemplate({
      type,
      topic: computeDefaultTopicName(type),
    });

    if (setup) {
      // One-click auto-setup — overlay the derived language/structure/brief.
      newCloneTopic = applyAutoSetupToCloneTemplate(newCloneTopic, { cloneId, setup });
    } else if (cloneId) {
      // Manual path but a CopyTwin was selected — pre-fill just the reference
      // so the editor's auto-save unlocks and they configure the rest.
      newCloneTopic = {
        ...newCloneTopic,
        cloneConfig: { ...newCloneTopic.cloneConfig, cloneId },
      };
    }

    setMasterEditTopic({ id: newTopicId, ...newCloneTopic });
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('topicId', newTopicId);
      return next;
    });
  }, [pendingNewType, computeDefaultTopicName, setSearchParams]);

  const closeMasterEditor = useCallback(() => {
    setMasterEditTopic(null);
    setMasterEditData({
      topic: '',
      subjectLines: [createSubjectLine()],
      topicSpecificNotes: [createInstruction()],
      aiTagId: '',
      type: 'intro',
      generalInfo: '',
      chanceRadarId: '',
      aiAdherence: 'exact',
      formattingStyle: 'as-written',
      aiTemperature: null,
      hyperIntelligence: false,
      ultraMode: false,
      personalizationGuide: '',
      referenceFile: null,
      smartGreeting: false,
      greetingCasualPrefixes: ['Hey', 'Hi', 'Hello'],
      greetingFormalPrefixes: ['Dear'],
      smartSubjectLine: false,
      smartSubjectStyle: 'neutral',
      smartSubjectInstructions: '',
      subjectMode: 'new',
      replyPrefix: 'AW',
      replyPrefixCustom: '',
      emailPreference: 'auto',
      dmFallback: 'info',
      callContext: DEFAULT_CALL_CONTEXT,
      language: 'German',
    });
    setValidationErrors({ topic: false, subjectLines: [], instructions: [] });
    setMasterSaved(false);
    setAiAssistOpen(false);
    setTopbarMenuOpen(false);

    // Topic-ID aus der URL entfernen
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('topicId');
      return next;
    });
  }, [setSearchParams]);

  // Auto-open Topic aus URL wenn topicId vorhanden ist
  // Voice Clone templates skip the standard openMasterEditor — they get
  // rendered by VoiceCloneTemplateEditor directly from masterEditTopic.
  useEffect(() => {
    const topicIdFromUrl = searchParams.get('topicId');
    if (!topicIdFromUrl || masterEditTopic) return;

    if (customTopics[topicIdFromUrl]) {
      const topicData = customTopics[topicIdFromUrl];
      const merged = { ...topicData, isCustom: true };
      if (isVoiceCloneTemplate(merged)) {
        setMasterEditTopic({ id: topicIdFromUrl, ...merged });
      } else {
        openMasterEditor(topicIdFromUrl, merged);
      }
      return;
    }

    if (availableTopics[topicIdFromUrl]) {
      const topicData = availableTopics[topicIdFromUrl];
      if (isVoiceCloneTemplate(topicData)) {
        setMasterEditTopic({ id: topicIdFromUrl, ...topicData });
      } else {
        openMasterEditor(topicIdFromUrl, topicData);
      }
      return;
    }
  }, [searchParams, customTopics, availableTopics, masterEditTopic, openMasterEditor]);

  // Master Editor - Subject Line Helpers
  const masterAddSubjectLine = () => {
    setMasterEditData(prev => ({
      ...prev,
      subjectLines: [...prev.subjectLines, createSubjectLine()]
    }));
  };

  const masterRemoveSubjectLine = (index) => {
    if (masterEditData.subjectLines.length > 1) {
      setMasterEditData(prev => ({
        ...prev,
        subjectLines: prev.subjectLines.filter((_, i) => i !== index)
      }));
    }
  };

  const masterUpdateSubjectLine = (index, value) => {
    setMasterEditData(prev => {
      const updated = [...prev.subjectLines];
      updated[index] = { ...updated[index], text: value };
      return { ...prev, subjectLines: updated };
    });
  };

  // Master Editor - Instruction Helpers
  const masterAddInstruction = () => {
    setMasterEditData(prev => ({
      ...prev,
      topicSpecificNotes: [...prev.topicSpecificNotes, createInstruction()]
    }));
  };

  const masterRemoveInstruction = (index) => {
    if (masterEditData.topicSpecificNotes.length > 1) {
      setMasterEditData(prev => ({
        ...prev,
        topicSpecificNotes: prev.topicSpecificNotes.filter((_, i) => i !== index)
      }));
    }
  };

  const masterUpdateInstruction = (index, value) => {
    setMasterEditData(prev => {
      const updated = [...prev.topicSpecificNotes];
      updated[index] = { ...updated[index], text: value };
      return { ...prev, topicSpecificNotes: updated };
    });
  };

  const [expandedPacingIndex, setExpandedPacingIndex] = useState(null);

  const masterUpdateInstructionPacing = (index, pacing) => {
    setMasterEditData(prev => {
      const updated = [...prev.topicSpecificNotes];
      const current = updated[index].pacing;
      updated[index] = { ...updated[index], pacing: current === pacing ? null : pacing };
      return { ...prev, topicSpecificNotes: updated };
    });
    setMasterSaved(false);
  };

  // ===== AI GENERATION FUNCTIONS =====
  // `avoidTexts` carries the variants generated earlier in the SAME run, so
  // variant 2 and 3 can be told what variant 1 already said.
  const buildAiPrompt = (type, rewriteText = null, avoidTexts = []) => {
    const existingSubjects = masterEditData.subjectLines.map(s => s.text).filter(Boolean).join('\n- ');
    const existingInstructions = [
      ...masterEditData.topicSpecificNotes.map(n => n.text),
      ...avoidTexts,
    ].filter(Boolean).join('\n---\n');
    const templateName = masterEditData.topic || 'email template';
    const lang = aiContext.language || 'German';
    
    const toneMap = {
      formal: 'formal and business-appropriate — structured sentences, respectful distance, professional salutations (e.g. "Dear Mr./Ms.")',
      neutral: 'neutral and balanced — professional but approachable, neither too stiff nor too casual, natural business tone',
      casual: 'casual and conversational — like texting a smart colleague, relaxed but still competent, first-name basis'
    };
    const toneDesc = toneMap[aiContext.tone] || toneMap.neutral;
    const emojiRule = aiContext.useEmojis ? '- Feel free to include relevant emojis to make it more engaging' : '- Do NOT use any emojis';
    const langEnforcement = `\n\n=== LANGUAGE REQUIREMENT (MANDATORY) ===\nYou MUST write ALL output text in ${lang}. Every single word of the generated content must be in ${lang}. Do NOT mix languages. Do NOT write any part in English unless the target language IS English. This is non-negotiable.`;
    
    // Personality + Framework prompt injection
    const activePersonality = emailPersonalities.find(p => p.id === aiContext.personality);
    const activeFramework = getFrameworkById((aiContext.selectedFrameworks || [])[0]);
    // Length is scaled from the framework's own beat structure, so a four-beat
    // play never gets asked for two sentences.
    const lengthDesc = buildLengthRule(activeFramework, aiContext.length);
    const personalityBlock = (() => {
      if (activeFramework) {
        return buildFrameworkPromptBlock(
          activeFramework,
          (aiContext.frameworkSlots || {})[activeFramework.id] || {},
          {
            language: aiContext.language,
            targetLanguage: lang,
            forSubject: type === 'subject',
            lengthScale: aiContext.length,
          },
        );
      }
      if (!activePersonality) return '';
      const parts = [`=== EMAIL PERSONALITY: ${activePersonality.name.toUpperCase()} ===\n${activePersonality.corePhilosophyTemplate || activePersonality.corePhilosophy}`];
      const selectedFw = (aiContext.selectedFrameworks || [])
        .map(fwId => activePersonality.frameworks.find(f => f.id === fwId))
        .filter(Boolean);
      if (selectedFw.length > 0) {
        parts.push(selectedFw.map(fw => fw.promptTemplate || fw.prompt).join('\n\n'));
      }
      return parts.join('\n\n');
    })();
    
    // Build dynamic keyword instructions.
    //
    // The variable picker is written for SUBJECT lines, where replacePlaceholders()
    // resolves a large catalogue. The email BODY goes through a much smaller map in
    // WorldEmailCreator, and anything outside it is replaced with an empty string —
    // so a body prompt offering {{lead_firstname}} produces "Hallo ," at send time.
    // Map onto the surviving vocabulary before it ever reaches the model.
    const isBodyText = type === 'instruction';
    const activeKws = [];
    const seenKw = new Set();
    (aiContext.activeKeywords || []).forEach((key) => {
      const bare = key.replace(/[{}]/g, '');
      let mapped = key;
      if (isBodyText && !SEND_TIME_BODY_VARIABLES.includes(bare)) {
        const alias = BODY_VARIABLE_ALIASES[bare];
        // No body equivalent — drop it rather than let the model emit a dud.
        if (!alias) return;
        mapped = `{{${alias}}}`;
      }
      if (seenKw.has(mapped)) return;
      seenKw.add(mapped);
      const source = dynamicKeywords.find(dk => dk.key === key);
      activeKws.push(source?.description ? `${mapped} = ${source.description}` : mapped);
    });
    const firstKw = activeKws[0] ? activeKws[0].split(' = ')[0] : '{{business_name}}';
    const personalizationRule = activeKws.length > 0
      ? `- USE these dynamic template placeholders where appropriate in the text:\n${activeKws.map(l => `  ${l}`).join('\n')}\n- These are literal placeholders - write them exactly as shown (e.g. ${firstKw}, NOT the actual value)\n- Do NOT invent any other {{variable}}. Any placeholder outside this list is deleted at send time, leaving a gap in the sentence.`
      : '- Do NOT use any dynamic placeholders or personalization variables';
    const userNoteBlock = aiContext.userNote?.trim()
      ? `\n=== USER INSTRUCTIONS (HIGH PRIORITY) ===\nThe user explicitly asked for the following. Treat this as a top-priority directive:\n${aiContext.userNote.trim()}\n===`
      : '';
    const count = Math.min(Math.max(aiContext.variantCount || 2, 1), 5);

    // ── General Info context block ──
    const adherenceMap = {
      'exact': 'You MUST follow the template text almost word-for-word. Only fix grammar or minor wording. Do NOT add, remove, or rearrange sentences. Preserve the exact structure, arguments, and flow.',
      'close': 'Stay very close to the template text. You may slightly rephrase sentences for natural flow, but keep the same structure, arguments, and key phrases. Do not add new ideas or remove existing points.',
      'balanced': 'Use the template as a strong guide. You may rephrase, reorder, or slightly expand/condense sections, but keep the core message, all key arguments, and overall intent intact.',
      'loose': 'Use the template as loose inspiration. Keep the core topic and intent, but approach it from a DIFFERENT ANGLE — restructure the argument, find a fresh perspective, rephrase heavily, and add your own ideas. Do NOT just rephrase the template sentence by sentence — rethink the approach.',
      'creative': 'The template is ONLY a topic hint. Write a completely new email with a DIFFERENT PERSPECTIVE, different arguments, and a fresh creative approach. Invent your own angle, your own hook, your own flow. Only the overarching goal/topic must remain — everything else should be freshly conceived, not just reworded.',
    };
    const formattingMap = {
      'as-written': 'Keep the formatting exactly as it is in the template. Do not change line breaks, paragraph structure, or spacing.',
      'sentence-per-line': 'Format the email so that each sentence stands on its own line with a blank line between them. This creates a very scannable, easy-to-read layout — almost like a chat message. Example:\n\nHi {{lead_firstname}},\n\nI noticed your company is growing fast.\n\nWe helped a similar business increase their leads by 40%.\n\nWould it make sense to chat for 15 minutes this week?',
      'short-paragraphs': 'Break the email into short paragraphs of 1-2 sentences each, separated by blank lines. This creates a clean, professional look that is easy to scan.',
      'bullet-points': 'Where appropriate, use bullet points or dashes to list key benefits, features, or points. Keep intro and outro as normal sentences. Bullets make information easy to digest.',
      'conversational': 'Write in a natural, flowing conversational style with no rigid structure. Mix short and medium sentences. Use line breaks where a natural pause would occur, like in a real message to a colleague.',
    };
    const adherenceRule = adherenceMap[masterEditData.aiAdherence] || adherenceMap['exact'];
    const formattingRule = formattingMap[masterEditData.formattingStyle] || formattingMap['as-written'];

    const greetingToneContext = masterEditData.smartGreeting
      ? `- GREETING TONE: This template uses a {{greeting}} placeholder whose tone is driven by THIS VARIANT's tone setting. Formal variants use greetings like "${(masterEditData.greetingFormalPrefixes || ['Dear'])[0]} Mr. Schmidt,", casual variants use greetings like "${(masterEditData.greetingCasualPrefixes || ['Hey'])[0]} Sarah,", and "Auto" variants automatically match the greeting to the formality of the email body you write (e.g. German "Sie" → formal greeting, "du" → casual greeting). Keep the body's level of formality consistent.`
      : '';

    const templateSyntaxNote = `- CRITICAL — TEMPLATE SYNTAX (you are creating TEMPLATES, not final emails):
  This system has TWO placeholder layers:

  1. {{DOUBLE BRACES}} = Dynamic variables — replaced with real lead data automatically (e.g. {{business_name}}, {{lead_firstname}}, {{greeting}}). Keep them EXACTLY as-is. Never invent new {{double brace}} variables.

  2. {SINGLE BRACES} = AI generation prompts — instructions for a SECOND AI that runs later, per lead, with access to that lead's specific data (their products, problems, context fields, etc.). The content inside {single braces} is NEVER shown to the recipient — it tells the AI what to generate at that spot.

  HOW TO WRITE {single brace} INSTRUCTIONS:
  - Be specific and descriptive: explain exactly what the AI should output
  - Reference which context fields to use (e.g. "from the storefront data", "from the company description")
  - Include formatting rules (e.g. "max 3 words", "written as a word not a number")
  - Include examples of good vs bad output so the AI understands the quality bar
  - Keep instructions in the same language as the email

  EXAMPLE 1 (e-commerce, uses product/storefront data):
  {{greeting}}
  ich war gerade kurz auf Ihrem Amazon-Store.
  Bei Produkten wie {Name eines Produktes der Firma, runtergebrochen auf max. 2–3 Wörter. zB statt "Livolo Spreizkrallen Nachrüstung LG-01" = "Livolo Spreizkrallen". Infos aus dem Kontext-Feld oder Web Research.} ist mir ein Detail aufgefallen.
  Auf den ersten Seiten Ihres Shops sind mir {Anzahl der betroffenen ASINs als Wort geschrieben, z.B. "zehn" statt "10". Aus dem Web Research oder Kontext entnehmen.} ASINs begegnet, bei denen im Titel Sonderzeichen nicht sauber gelistet werden.

  EXAMPLE 2 (B2B SaaS, uses web research + custom fields):
  {{greeting}}
  {Schau in die Web Research Daten und finde ein konkretes Feature oder eine Seite auf deren Website die zeigt was sie machen — beschreibe es in max. 8 Wörtern.}
  Kurze Frage dazu: {Basierend auf deren Branche aus den Custom Fields und was die Web Research über deren Challenges zeigt, formuliere eine spezifische Frage die ein echtes Problem anspricht das sie haben könnten — max. 1 Satz.}
  Wir helfen Firmen wie {{business_name}} dabei, genau das zu lösen.

  EXAMPLE 3 (data-driven, leverages multiple sources):
  Hi {{lead_firstname}},
  {Analysiere die Web Research Ergebnisse und identifiziere EINEN konkreten Aspekt ihres Online-Auftritts der verbessert werden könnte. Nenne das Problem spezifisch mit Zahlen wenn verfügbar, z.B. "Ihre Ladezeit liegt bei 4.2s" oder "3 von 5 Produktseiten haben keine Meta-Descriptions". Max. 1–2 Sätze.}
  Das kostet euch vermutlich {Schätze basierend auf ihrer Branche und Größe (aus Custom Fields) einen realistischen Business Impact in einem konkreten Bereich, z.B. "15–20% eurer organischen Sichtbarkeit" — nicht übertreiben, lieber konservativ.}.
  Macht es Sinn, kurz drüber zu schauen?

  DATA SOURCES available to the downstream AI (reference these in your {instructions}):
  - Lead context / description — general info about the lead's business, scraped or manually entered
  - Web research data — results from automated web crawling (website analysis, social media, products, reviews, problems found, etc.)
  - Custom fields — user-curated business intelligence fields (industry, revenue, specific metrics, etc.)
  - Storefront/product data — for e-commerce leads: product titles, ASINs, listing issues, etc.
  - Decision maker info — name, role, LinkedIn, social profiles
  - All {{double brace}} variable values (business_name, lead_firstname, etc.)

  KEY RULES:
  - You SHOULD use {single brace AI prompts} to make the template dynamic and personalized per lead
  - The best templates mix fixed text with {AI instruction blocks} — this creates emails that feel individually written
  - {AI instructions} can be short (a word/phrase) or multi-sentence (for complex logic)
  - Reference SPECIFIC data sources in your instructions (e.g. "from the web research data", "from their product listings", "from the custom fields")
  - The downstream AI is intelligent — give it smart instructions and it will produce personalized, context-aware output
  - Think of {instructions} as your way to make EVERY email feel hand-written, even at scale
  - All other text outside braces = actual email content sent as-is to the recipient

  CRITICAL — DO NOT WRAP STATIC TEXT IN {SINGLE BRACES}:
  - {Single braces} are EXCLUSIVELY for AI generation instructions — commands that tell the downstream AI what to research and produce.
  - Regular email sentences that should be sent as-is to the recipient MUST be written as plain text WITHOUT any braces.
  - If a sentence is fixed content (not dynamically generated per lead), it goes OUTSIDE braces — even if it contains conditional-sounding language like "If you're interested..." or "Let me know if..."
  - WRONG: {If you're interested, I can add it to our playlist.}  ← This is static text, NOT an AI instruction!
  - RIGHT: If you're interested, I can add it to our playlist.  ← Plain text, sent as-is
  - WRONG: {Thanks for your time, looking forward to hearing from you.}  ← Static closing, NOT an instruction!
  - RIGHT: Thanks for your time, looking forward to hearing from you.  ← Plain text
  - ONLY put text in {braces} if it is a COMMAND for the AI to generate something unique per lead based on their data.

  QUALITY BAR FOR {AI INSTRUCTIONS} — Be specific, not lazy:
  - Every {instruction} must specify: (1) what data source to use, (2) what to output, (3) length/format constraints, (4) an example of good output.
  - VAGUE (bad): {Check their Instagram and mention a recent post}
  - SPECIFIC (good): {Schau in die Instagram-Daten und nenne einen konkreten aktuellen Post oder eine Aktivität. Erwähne spezifisch was sie gepostet haben (z.B. "dein Studio-Session Video von letzter Woche" oder "dein neues Album-Cover"). Max. 10 Wörter. Wenn keine Instagram-Daten vorhanden, lasse diesen Satz komplett weg.}
  - VAGUE (bad): {Adjust the closing line based on their style}
  - SPECIFIC (good): {Passe den Abschlusssatz an ihren Musikstil an: Für Rap/Hip-Hop nutze lockere Sprache wie "Stay lit" oder "Keep grinding", für Rock/Indie eher "Rock on" oder "Keep creating", für Pop/Electronic "Keep shining". Genau 2–3 Wörter.}`;

    const generalInfoBlock = [
      masterEditData.generalInfo ? `- GENERAL TEMPLATE CONTEXT (always consider this): ${masterEditData.generalInfo}` : '',
      `- TEMPLATE ADHERENCE: ${adherenceRule}`,
      `- FORMATTING STYLE: ${formattingRule}`,
      greetingToneContext,
      templateSyntaxNote,
    ].filter(Boolean).join('\n');

    const brainActive = !!(aiContext.useAccountBrain && vaultCopyPromptBlock);
    const accountBrainBlock = brainActive ? vaultCopyPromptBlock : '';
    const sessionFactLines = [
      aiContext.businessDescription ? `- About the sender's business${brainActive ? ' (this template)' : ''}: ${aiContext.businessDescription}` : '',
      aiContext.purpose ? `- Email purpose: ${aiContext.purpose}` : '',
      aiContext.targetAudience ? `- Target audience${brainActive ? ' (this template)' : ''}: ${aiContext.targetAudience}` : '',
      aiContext.keyPoints ? `- Key points${brainActive ? ' to cover (this template)' : ''}: ${aiContext.keyPoints}` : '',
    ].filter(Boolean).join('\n');
    const extraContext = [accountBrainBlock, sessionFactLines].filter(Boolean).join('\n');
    
    // === REWRITE MODE ===
    if (rewriteText) {
      const deviationMap = {
        close: 'Make only minimal changes — fix grammar, polish wording, but keep the text almost identical to the original. Preserve structure and phrasing.',
        balanced: 'Moderately rework the text — keep the core message and intent, but freely rephrase sentences, improve structure, and increase impact.',
        creative: 'Freely rewrite the text — only preserve the core intent/topic. Everything else (structure, wording, arguments, approach) can change completely.'
      };
      const deviation = aiContext.creativity;
      const deviationDesc = deviationMap[deviation] || deviationMap.balanced;

      if (type === 'subject') {
        return {
          systemPrompt: `You are an expert email copywriter. Rewrite and improve an existing email subject line.
RULES:
- DEVIATION LEVEL (${deviation}): ${deviationDesc}
- Keep it concise (under 60 characters ideally)
- Tone: ${toneDesc}${userNoteBlock}
${emojiRule}
${personalizationRule}
${generalInfoBlock}
- Return ONLY the rewritten subject line, nothing else
- No numbering, no bullets, no quotes - just the plain text
- Return exactly 1 rewritten version
${extraContext}${langEnforcement}`,
          userPrompt: `Rewrite this subject line:\n"${rewriteText}"\n\nImprove it according to the deviation level.\n\nREMINDER: Write in ${lang}.`
        };
      } else {
        return {
          systemPrompt: `You are an expert cold email copywriter specializing in high-reply-rate outreach. Rewrite and improve an existing email text.
RULES:
- DEVIATION LEVEL (${deviation}): ${deviationDesc}
- Tone: ${toneDesc}${userNoteBlock}
${emojiRule}
${personalizationRule}
${generalInfoBlock}
- Write the actual email body text, NOT meta-instructions
- Make it personal, genuine, and human — it must NOT feel like a marketing email
- Avoid any spam trigger phrases, over-promising, or aggressive CTAs
- Do NOT include subject line
- Do NOT invent a sign-off like "Best regards, {{user_name}}". If the email should close with a mailbox-specific snippet, end with {{footer_text}} — that is body text from the sending account, not the HTML footer.
- Return ONLY the rewritten email body text, nothing else
${extraContext}${langEnforcement}`,
          userPrompt: `Rewrite this email text:\n---\n${rewriteText}\n---\n\nImprove it according to the deviation level.\n\nREMINDER: Write the ENTIRE output in ${lang}. Not a single sentence in any other language.`
        };
      }
    }

    // === GENERATE NEW MODE ===
    if (type === 'subject') {
      return {
        systemPrompt: `You are an expert email copywriter. Generate creative, compelling email subject lines.
RULES:
- Generate exactly ${count} new subject line variant${count > 1 ? 's' : ''}
- Each should be unique and different from existing ones
- Keep them concise (under 60 characters ideally)
- Tone: ${toneDesc}
${personalityBlock ? `${personalityBlock}${activeFramework ? '' : "\nAdapt the personality's approach for subject lines specifically."}` : ''}${userNoteBlock}
${emojiRule}
${personalizationRule}
${generalInfoBlock}
- Return ONLY the subject lines, one per line, nothing else
- No numbering, no bullets, no quotes - just the plain text
${extraContext}${langEnforcement}`,
        userPrompt: `Template: "${templateName}"
${existingSubjects ? `\nExisting subject lines (create different ones!):\n- ${existingSubjects}` : ''}

Generate ${count} new subject line variant${count > 1 ? 's' : ''}.\n\nREMINDER: Write in ${lang}.`
      };
    } else {
      return {
        systemPrompt: `You are an expert cold email template architect specializing in high-reply-rate outreach. You create smart email TEMPLATES — not final emails.

IMPORTANT: You are building a TEMPLATE that will later be processed by an AI for each individual lead. This means you should use {single brace AI instructions} wherever the email should be personalized based on the lead's specific data.

The downstream AI has access to RICH DATA for each lead:
• Web research results — automated website analysis, social media, products, reviews, competitive insights
• Custom fields — user-curated business intelligence (industry, revenue, metrics, etc.)
• Lead context — business descriptions, notes, observations
• Product/storefront data — for e-commerce: product titles, listings, issues found
• Decision maker profiles — name, role, LinkedIn, social presence

Your {AI instructions} should LEVERAGE these data sources. Instead of writing generic filler, write smart instructions that tell the downstream AI exactly what to look for and how to use it. Think of yourself as a strategist briefing a researcher — the more specific your instructions, the more personalized the final email will feel.

RULES:
- Generate exactly 1 new email template variant
- Tone: ${toneDesc}
- Length: ${lengthDesc}
${personalityBlock}${userNoteBlock}
${emojiRule}
${personalizationRule}
${generalInfoBlock}
- Create a template that mixes fixed text with {AI instruction blocks} where personalization makes sense
- The fixed text should sound natural, genuine, and human — NOT like a marketing email
- NEVER wrap regular email text in {single braces} — only AI generation commands go in braces. Static sentences like "If you're interested..." or "Let me know..." are plain text, NOT instructions.
- {AI instructions} must be highly specific: state (1) which data source, (2) what to output, (3) max length, (4) an example of good output
- Good: {Aus der Web Research: Nenne EIN konkretes Produkt oder Feature von deren Website und beschreibe ein Problem damit in max. 8 Wörtern, z.B. "Ihre Produktseite für X lädt in 4.2 Sekunden"}
- Bad: {Write something personalized about their company}
- Bad: {Mention something from their website} ← too vague, will produce generic output
- Avoid any spam trigger phrases, over-promising, or aggressive CTAs
- Do NOT include subject line
- Do NOT invent a sign-off like "Best regards, {{user_name}}". If the template should close with a mailbox-specific snippet, end with {{footer_text}} — that is body text from the sending account, not the HTML footer.
- Return ONLY the email template text, nothing else
${extraContext}${langEnforcement}`,
        userPrompt: `Template: "${templateName}"
Type: ${masterEditData.type}
${existingInstructions ? `\nExisting email texts (create a different variant!):\n---\n${existingInstructions}\n---` : ''}

Generate 1 new email text variant.\n\nREMINDER: Write the ENTIRE output in ${lang}. Not a single sentence in any other language.`
      };
    }
  };
  
  const buildRefinementPrompt = (draftTemplate, lang) => {
    return {
      systemPrompt: `You are a template syntax expert. Your ONLY job is to review and fix an email template's {single brace} usage.

CONTEXT: This system has two placeholder layers:
- {{DOUBLE BRACES}} = dynamic variables replaced with real data (e.g. {{business_name}}). NEVER touch these.
- {SINGLE BRACES} = AI generation prompts — instructions for a downstream AI that runs per lead. The content inside tells the AI what to generate. These are NEVER shown to the recipient.

YOUR TASK — Review the draft template and fix these issues:

1. REMOVE BRACES FROM STATIC TEXT:
   Any sentence that is regular email content (meant to be sent as-is to the recipient) must NOT be in {braces}.
   - WRONG: {If you're interested, I can add it to our playlist.}
   - FIXED: If you're interested, I can add it to our playlist.
   - WRONG: {Let me know if any of these options resonate with you!}
   - FIXED: Let me know if any of these options resonate with you!
   - WRONG: {Thanks for reaching out!}
   - FIXED: Thanks for reaching out!
   Rule: If the text is a complete, finished sentence that doesn't need AI generation — remove the braces.

2. MAKE AI INSTRUCTIONS MORE SPECIFIC:
   Every {instruction} that IS a real AI prompt must specify:
   (a) Which data source to use (web research, Instagram data, custom fields, product data, etc.)
   (b) Exactly what to output (not just "mention something")
   (c) A max word/character limit
   (d) What to do if the data is unavailable (omit sentence, use fallback, etc.)
   (e) An example of good output

   - VAGUE: {Check their Instagram and mention a recent post}
   - SPECIFIC: {Schaue in die Instagram-Daten und nenne einen konkreten Post oder Aktivität — z.B. "dein neues Studio-Session Video" oder "der Clip von deinem letzten Auftritt". Max 10 Wörter. Wenn keine Instagram-Daten vorhanden sind, lasse diesen Satz komplett weg.}

   - VAGUE: {Adjust the closing line based on their style}
   - SPECIFIC: {Passe den Abschlusssatz an deren Musikstil an: Für Rap/Hip-Hop nutze "Stay lit" oder "Keep grinding", für Rock/Indie "Rock on" oder "Keep creating". Genau 2–4 Wörter.}

   - VAGUE: {an absolute standout}
   - SPECIFIC: {Wähle basierend auf dem Genre aus den Custom Fields ein passendes Kompliment: für Rock "a true masterpiece", für Rap "pure fire", für Pop "an absolute gem", für Electronic "a sonic journey". Max 4 Wörter.}

3. REMOVE SIGNATURE/SIGN-OFF IF PRESENT:
   - If the template ends with a sign-off name like "Thanks,\n{{user_name}}", "Best,\nJohn", "Cheers,\n{{user_name}}" — REMOVE it entirely (the system adds the signature automatically).
   - The template should end with the last content sentence or CTA, not with a name.

4. PRESERVE EVERYTHING ELSE:
   - Keep all {{double brace}} variables exactly as they are
   - Keep the email structure, tone, and flow intact
   - Keep all plain text as-is
   - Do NOT add new content or remove existing ideas
   - Do NOT change the email's meaning or strategy

RETURN ONLY the fixed template. No explanations, no comments, no markdown — just the corrected email template text.`,
      userPrompt: `Review and fix this email template draft:\n---\n${draftTemplate}\n---\n\nFix all {brace} issues according to the rules. Return ONLY the corrected template.\n\nIMPORTANT: Keep the template in ${lang}. Do NOT translate or change the language.`
    };
  };

  // ═══ Framework plays — slot state ═══
  // A filled slot becomes fixed copy in the template. An empty one is compiled
  // into a {single brace AI instruction} the per-lead AI resolves at send time.
  const activeFrameworkId = (aiContext.selectedFrameworks || [])[0] || '';
  const activeFrameworkRaw = activeFrameworkId ? getFrameworkById(activeFrameworkId) : null;
  const activeFrameworkSlotValues = (aiContext.frameworkSlots || {})[activeFrameworkId] || {};
  const activeSkeleton = activeFrameworkRaw
    ? resolveSkeleton(activeFrameworkRaw, activeFrameworkSlotValues, aiContext.language === 'German' ? 'German' : 'English')
    : null;

  const setFrameworkSlot = (frameworkId, slotKey, value) => {
    setAiContext(prev => ({
      ...prev,
      frameworkSlots: {
        ...(prev.frameworkSlots || {}),
        [frameworkId]: {
          ...((prev.frameworkSlots || {})[frameworkId] || {}),
          [slotKey]: value,
        },
      },
    }));
  };

  const clearFrameworkSlots = (frameworkId) => {
    setAiContext(prev => ({
      ...prev,
      frameworkSlots: { ...(prev.frameworkSlots || {}), [frameworkId]: {} },
    }));
  };

  // Prefills only the slots that describe the SENDER (proof, asset, peer group).
  // Lead-specific slots are deliberately left open so they stay dynamic per lead.
  const handleSuggestFrameworkSlots = async () => {
    if (slotSuggesting || !activeFrameworkRaw?.slots?.length) return;
    setSlotSuggesting(true);
    try {
      const lang = aiContext.language || 'German';
      const brainBlock = aiContext.useAccountBrain && vaultCopyPromptBlock ? vaultCopyPromptBlock : '';
      const contextLines = [
        aiContext.businessDescription ? `Sender's business / offer: ${aiContext.businessDescription}` : '',
        aiContext.purpose ? `Email purpose: ${aiContext.purpose}` : '',
        aiContext.targetAudience ? `Target audience: ${aiContext.targetAudience}` : '',
        aiContext.keyPoints ? `Key points to cover: ${aiContext.keyPoints}` : '',
        masterEditData.topic ? `Template name: ${masterEditData.topic}` : '',
        masterEditData.generalInfo ? `Template context: ${masterEditData.generalInfo}` : '',
        aiContext.userNote?.trim() ? `User instruction: ${aiContext.userNote.trim()}` : '',
      ].filter(Boolean).join('\n');

      const slotSpec = activeFrameworkRaw.slots
        .map(s => `"${s.key}" — ${s.label.English}. Should contain: ${s.auto}`)
        .join('\n');
      const alreadyFilled = activeFrameworkRaw.slots
        .filter(s => (activeFrameworkSlotValues[s.key] || '').trim())
        .map(s => `"${s.key}": ${activeFrameworkSlotValues[s.key].trim()}`)
        .join('\n');

      const systemPrompt = `You prefill the slots of a cold-email framework for a sales team.

FRAMEWORK: ${activeFrameworkRaw.name.English}
${activeFrameworkRaw.bestFor ? `Built for: ${activeFrameworkRaw.bestFor.English}` : ''}

SLOTS:
${slotSpec}

THE ONE RULE THAT MATTERS:
Only propose a value for a slot when it is a fact about the SENDER that stays identical for every recipient — their offer, their peer proof, their outcome, the asset they can send, the peer group they serve, the trade-off they remove.
If a slot depends on the individual recipient (their signal, their strategic objective, their incumbent vendor, the gap on their website, their specific risk), return null for it. Those slots stay dynamic and get resolved per lead from web research later. Guessing them here would produce generic mail.

OUTPUT:
Strict JSON object only. Keys = slot keys. Values = a short phrase in ${lang} (a sentence fragment, no full sentence, no trailing period, no quotes) or null.
No markdown, no code fences, no commentary.`;

      const userPrompt = `Sender context:
${contextLines || '(no context provided — return null for every slot that is not obviously derivable)'}
${brainBlock ? `\n${brainBlock}` : ''}
${alreadyFilled ? `\nAlready filled by the user (improve only if clearly wrong, otherwise repeat as-is):\n${alreadyFilled}` : ''}

Return the JSON object now. Values in ${lang}.`;

      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.generateTemplateAI(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ purpose: 'templateSlotPrefill', systemPrompt, userPrompt, temperature: 0.5, userId }),
      });
      const data = await response.json();
      const raw = (data.content || '').replace(/```(?:json)?/gi, '').trim();
      const start = raw.indexOf('{');
      const end = raw.lastIndexOf('}');
      if (start === -1 || end === -1) return;

      const parsed = JSON.parse(raw.slice(start, end + 1));
      const validKeys = new Set(activeFrameworkRaw.slots.map(s => s.key));
      const next = { ...activeFrameworkSlotValues };
      Object.entries(parsed).forEach(([key, value]) => {
        if (!validKeys.has(key)) return;
        if (typeof value !== 'string') return;
        const clean = value.trim();
        if (clean && clean.toLowerCase() !== 'null') next[key] = clean;
      });
      setAiContext(prev => ({
        ...prev,
        frameworkSlots: { ...(prev.frameworkSlots || {}), [activeFrameworkRaw.id]: next },
      }));
    } catch (error) {
      console.error('Framework slot suggestion failed:', error);
    } finally {
      setSlotSuggesting(false);
    }
  };

  // ═══ Generation presets ═══
  // A run involves a dozen settings across two panels. Losing them after one
  // click means rebuilding the whole setup by hand, so the last few runs are
  // kept on the account (not the browser) and can be restored in one tap.
  const GEN_PRESET_PATH = userId ? `/World/${userId}/settings/ai/templateGenPresets` : null;

  useEffect(() => {
    if (!GEN_PRESET_PATH || !masterEditorOpen) {
      setGenPresets([]);
      return undefined;
    }
    const presetRef = ref(database, GEN_PRESET_PATH);
    const unsubscribe = onValue(presetRef, (snap) => {
      const value = snap.val();
      setGenPresets(Array.isArray(value) ? value.filter(Boolean) : []);
    });
    return () => unsubscribe();
  }, [GEN_PRESET_PATH, masterEditorOpen]);

  const rememberGenerationPreset = (variantsProduced) => {
    if (!GEN_PRESET_PATH) return;
    const frameworkId = (aiContext.selectedFrameworks || [])[0] || '';
    const framework = frameworkId ? getFrameworkById(frameworkId) : null;
    const preset = {
      id: `${Date.now()}`,
      savedAt: new Date().toISOString(),
      templateName: masterEditData.topic || '',
      variantsProduced,
      label: framework
        ? (framework.name[aiContext.language === 'German' ? 'German' : 'English'] || framework.name.English)
        : (masterEditData.topic || 'Freie Generierung'),
      context: {
        language: aiContext.language,
        tone: aiContext.tone,
        length: aiContext.length,
        creativity: aiContext.creativity,
        useEmojis: !!aiContext.useEmojis,
        variantCount: aiContext.variantCount,
        activeKeywords: aiContext.activeKeywords || [],
        useAccountBrain: !!aiContext.useAccountBrain,
        personality: aiContext.personality || '',
        selectedFrameworks: aiContext.selectedFrameworks || [],
        // Only the active framework's slots — the rest is noise in the preset.
        frameworkSlots: frameworkId
          ? { [frameworkId]: (aiContext.frameworkSlots || {})[frameworkId] || {} }
          : {},
        userNote: aiContext.userNote || '',
        businessDescription: aiContext.businessDescription || '',
        purpose: aiContext.purpose || '',
        targetAudience: aiContext.targetAudience || '',
        keyPoints: aiContext.keyPoints || '',
      },
    };
    const next = [preset, ...genPresets.filter(p => p && p.id !== preset.id)].slice(0, MAX_GEN_PRESETS);
    set(ref(database, GEN_PRESET_PATH), next).catch((err) =>
      console.warn('Could not store generation preset:', err.message));
  };

  const applyGenerationPreset = (preset) => {
    if (!preset?.context) return;
    setSkeletonOpen(false);
    setAiGenResult(null);
    setAiContext(prev => ({
      ...prev,
      ...preset.context,
      // Merge rather than replace so slots of other frameworks survive.
      frameworkSlots: { ...(prev.frameworkSlots || {}), ...(preset.context.frameworkSlots || {}) },
    }));
  };

  /**
   * One AI call. The server owns the model and the price — we only declare the
   * purpose. Returns the text plus what it actually cost.
   */
  const callTemplateAi = async ({ purpose, systemPrompt, userPrompt, temperature }) => {
    const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.generateTemplateAI(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ purpose, systemPrompt, userPrompt, temperature, userId }),
    });
    const data = await response.json();
    if (response.status === 402) {
      const err = new Error('insufficient_credits');
      err.code = 'insufficient_credits';
      err.requiredCredits = data.requiredCredits;
      err.availableCredits = data.availableCredits;
      throw err;
    }
    if (!response.ok || data.error) {
      throw new Error(data.details || data.error || 'Generation failed');
    }
    return { content: (data.content || '').trim(), credits: Number(data.credits?.charged) || 0 };
  };

  const handleAiGenerate = async (popupConfig) => {
    if (aiGenerating) return;
    const { type, mode, rewriteIndex, rewriteText } = popupConfig;
    const isRewrite = mode === 'rewrite';
    const isNewText = type === 'instruction' && !isRewrite;
    // Only fresh email text supports multiple variants; a rewrite targets one
    // specific block and subject lines come back as several lines from one call.
    const variantTarget = isNewText ? clampTextVariants(aiContext.variantCount) : 1;

    setAiGenResult(null);
    setAiGenerating({ type, step: 1, variant: 1, variantTarget });

    let creditsSpent = 0;
    const generated = [];

    try {
      if (type === 'subject') {
        const { systemPrompt, userPrompt } = buildAiPrompt(type, isRewrite ? rewriteText : null);
        const { content, credits } = await callTemplateAi({
          purpose: 'templateSubject', systemPrompt, userPrompt,
        });
        creditsSpent += credits;

        const lines = content.split('\n').map(l => l.trim()).filter(l => l.length > 0 && l.length < 120);
        if (isRewrite) {
          if (lines[0]) {
            setMasterEditData(prev => ({
              ...prev,
              subjectLines: prev.subjectLines.map((sl, i) => (i === rewriteIndex ? { ...sl, text: lines[0] } : sl)),
            }));
          }
        } else {
          const picked = lines.slice(0, Math.min(Math.max(aiContext.variantCount || 2, 1), 5));
          if (picked.length) {
            setMasterEditData(prev => ({
              ...prev,
              subjectLines: [...prev.subjectLines, ...picked.map(text => createSubjectLine(text))],
            }));
          }
        }
        setAiGenResult({ kind: 'subject', added: isRewrite ? 1 : lines.length, credits: creditsSpent, issues: [] });
        setMasterSaved(false);
        return;
      }

      for (let i = 0; i < variantTarget; i += 1) {
        setAiGenerating({ type, step: 1, variant: i + 1, variantTarget });

        const { systemPrompt, userPrompt } = buildAiPrompt(type, isRewrite ? rewriteText : null, generated);
        const draft = await callTemplateAi({
          purpose: isRewrite ? 'templateRewrite' : 'templateWriter',
          systemPrompt,
          userPrompt,
        });
        creditsSpent += draft.credits;
        let finalText = draft.content;

        // Second pass only for fresh templates: it enforces the {brace} rules on
        // a finished draft, which a rewrite of existing copy does not need.
        if (isNewText && finalText) {
          setAiGenerating({ type, step: 2, variant: i + 1, variantTarget });
          const { systemPrompt: refineSystem, userPrompt: refineUser } =
            buildRefinementPrompt(finalText, aiContext.language || 'German');
          try {
            const refined = await callTemplateAi({
              purpose: 'templateRefine', systemPrompt: refineSystem, userPrompt: refineUser, temperature: 0.2,
            });
            creditsSpent += refined.credits;
            // The polish pass can over-apply its "unwrap static text" rule and
            // strip every brace, which turns the AI instructions into literal
            // email prose. Keep the draft when that happens.
            const verdict = refinementIsSafe(finalText, refined.content);
            if (verdict.safe) {
              finalText = refined.content;
            } else {
              console.warn(
                `Refinement rejected (${verdict.reason}): ${verdict.draftCount} instructions in, `
                + `${verdict.refinedCount} out. Keeping the draft.`,
              );
            }
          } catch (refineError) {
            // The draft is usable without the polish pass — don't lose it.
            console.warn('Refinement pass failed, keeping the draft:', refineError.message);
          }
        }

        if (finalText) generated.push(finalText);
      }

      if (generated.length) {
        if (isRewrite) {
          setMasterEditData(prev => ({
            ...prev,
            topicSpecificNotes: prev.topicSpecificNotes.map((note, i) =>
              (i === rewriteIndex ? { ...note, text: generated[0] } : note)),
          }));
        } else {
          setMasterEditData(prev => ({
            ...prev,
            topicSpecificNotes: [...prev.topicSpecificNotes, ...generated.map(t => createInstruction(t))],
          }));
          rememberGenerationPreset(generated.length);
        }
        setMasterSaved(false);
      }

      // Lint what we produced: unresolvable braces and dead variables fail
      // silently at send time, so they have to surface here.
      const checkOptions = {
        customFieldKeys: cfContextFields.map(f => f.key),
        smartGreeting: !!masterEditData.smartGreeting,
      };
      const checks = generated.map(t => checkTemplateBraces(t, checkOptions));
      const issues = [];
      const seenCodes = new Set();
      checks.forEach(c => c.issues.forEach((issue) => {
        const dedupeKey = `${issue.code}|${issue.detail}`;
        if (seenCodes.has(dedupeKey)) return;
        seenCodes.add(dedupeKey);
        issues.push(issue);
      }));

      setAiGenResult({
        kind: 'text',
        added: generated.length,
        credits: creditsSpent,
        instructionCount: checks.reduce((sum, c) => sum + c.instructionCount, 0),
        issues,
      });
    } catch (error) {
      if (error.code === 'insufficient_credits') {
        setAiGenResult({
          kind: 'error',
          added: generated.length,
          credits: creditsSpent,
          issues: [{
            level: 'error',
            code: 'insufficient_credits',
            detail: `Not enough credits — ${error.requiredCredits} needed, ${error.availableCredits ?? 0} available.`,
          }],
        });
      } else {
        console.error('AI generation error:', error);
        setAiGenResult({
          kind: 'error',
          added: generated.length,
          credits: creditsSpent,
          issues: [{ level: 'error', code: 'failed', detail: error.message || 'Generation failed.' }],
        });
      }
    } finally {
      setAiGenerating(null);
    }
  };

  /**
   * Variant Forge — derive 1–3 siblings from one variant the user already likes.
   *
   * Unlike fresh generation this runs a single writer call per variant, with no
   * refinement pass: the source is already polished and serves as the syntax
   * reference, and the polish prompt is explicitly told to reword vague
   * instructions — which would quietly break a "keep the {AI prompts} 1:1"
   * promise. auditDerivedVariant checks the result against the source instead.
   */
  const handleVariantForge = async (config) => {
    if (aiGenerating || !variantForge) return;

    const sourceText = variantForge.text;
    // A twin of a formal variant is still formal, so the greeting tone carries over.
    const sourcePacing = masterEditData.topicSpecificNotes[variantForge.index]?.pacing || null;
    // Snapshot the siblings now. Variants get appended as they arrive, so reading
    // this out of state inside the loop would mean reading a stale copy anyway.
    const existingSiblings = masterEditData.topicSpecificNotes
      .map(n => n.text)
      .filter(text => text && text !== sourceText);
    const variantTarget = config.count;

    setVariantForge(null);
    setAiGenResult(null);
    setAiGenerating({ type: 'instruction', mode: 'forge', step: 1, steps: 1, variant: 1, variantTarget });

    let creditsSpent = 0;
    const generated = [];

    try {
      for (let i = 0; i < variantTarget; i += 1) {
        setAiGenerating({ type: 'instruction', mode: 'forge', step: 1, steps: 1, variant: i + 1, variantTarget });

        const { systemPrompt, userPrompt } = buildVariantForgePrompt({
          sourceText,
          config,
          language: aiContext.language || 'German',
          templateName: masterEditData.topic,
          templateType: masterEditData.type,
          // Every sibling that already exists, plus the ones from this run — the
          // source itself is the blueprint, not something to stay away from.
          avoidTexts: [...existingSiblings, ...generated],
          generalInfo: masterEditData.generalInfo,
          smartGreeting: !!masterEditData.smartGreeting,
        });

        const { content, credits } = await callTemplateAi({
          purpose: 'templateWriter', systemPrompt, userPrompt,
        });
        creditsSpent += credits;
        if (!content) continue;

        // Append each variant as it lands. If call 3 of 3 fails, the two the
        // user already paid for are on the page instead of thrown away.
        generated.push(content);
        setMasterEditData(prev => ({
          ...prev,
          topicSpecificNotes: [...prev.topicSpecificNotes, createInstruction(content, sourcePacing)],
        }));
        setMasterSaved(false);
      }

      // Two layers of checking: the generic brace lint every generated template
      // gets, plus how far this one drifted from the variant it was derived from.
      const checkOptions = {
        customFieldKeys: cfContextFields.map(f => f.key),
        smartGreeting: !!masterEditData.smartGreeting,
      };
      const issues = [];
      const seenCodes = new Set();
      let instructionCount = 0;
      generated.forEach((text) => {
        const lint = checkTemplateBraces(text, checkOptions);
        instructionCount += lint.instructionCount;
        const audit = auditDerivedVariant(sourceText, text, config);
        [...lint.issues, ...audit.issues].forEach((issue) => {
          const dedupeKey = `${issue.code}|${issue.detail}`;
          if (seenCodes.has(dedupeKey)) return;
          seenCodes.add(dedupeKey);
          issues.push(issue);
        });
      });

      setAiGenResult({
        kind: 'text',
        added: generated.length,
        credits: Math.round(creditsSpent * 100) / 100,
        instructionCount,
        issues,
      });
    } catch (error) {
      if (error.code === 'insufficient_credits') {
        setAiGenResult({
          kind: 'error',
          added: generated.length,
          credits: Math.round(creditsSpent * 100) / 100,
          issues: [{
            level: 'error',
            code: 'insufficient_credits',
            detail: `Not enough credits — ${error.requiredCredits} needed, ${error.availableCredits ?? 0} available.`,
          }],
        });
      } else {
        console.error('Variant forge error:', error);
        setAiGenResult({
          kind: 'error',
          added: generated.length,
          credits: Math.round(creditsSpent * 100) / 100,
          issues: [{ level: 'error', code: 'failed', detail: error.message || 'Generation failed.' }],
        });
      }
    } finally {
      setAiGenerating(null);
    }
  };

  const handleMasterSave = async () => {
    if (!masterEditTopic) return;
    
    // Validate and track errors
    const topicError = !masterEditData.topic || masterEditData.topic.trim() === '';
    const subjectLineErrors = masterEditData.subjectLines.map((line, index) => 
      !line.text || line.text.trim() === '' ? index : null
    ).filter(err => err !== null);
    const instructionErrors = masterEditData.topicSpecificNotes.map((note, index) => 
      !note.text || note.text.trim() === '' ? index : null
    ).filter(err => err !== null);
    
    const validSubjectLines = masterEditData.subjectLines.filter(line => line.text && line.text.trim() !== '');
    const validInstructions = masterEditData.topicSpecificNotes.filter(note => note.text && note.text.trim() !== '');
    
    // Set validation errors
    setValidationErrors({
      topic: topicError,
      subjectLines: subjectLineErrors,
      instructions: instructionErrors
    });
    
    // Follow-up templates in "reply_to_previous" mode do not need subject line
    // variants — they derive the subject from the contact's previous email.
    const isReplyToPrevious = masterEditData.subjectMode === 'reply_to_previous' && masterEditData.type !== 'intro';
    const subjectLinesRequired = !isReplyToPrevious;

    if (topicError || (subjectLinesRequired && validSubjectLines.length === 0) || validInstructions.length === 0) {
      setMessage(subjectLinesRequired
        ? "Please fill in all required fields, add at least one subject line, and at least one instruction."
        : "Please fill in the template name and add at least one instruction.");
      // Scroll to first error after a brief delay to allow state update
      setTimeout(() => {
        if (topicError) {
          const topicInput = document.querySelector('.ed-topic-name-input');
          if (topicInput) topicInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (subjectLineErrors.length > 0) {
          const firstError = document.querySelector(`.ed-subject-line-card:nth-child(${subjectLineErrors[0] + 1})`);
          if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (instructionErrors.length > 0) {
          const firstError = document.querySelector(`.ed-instruction-card:nth-child(${instructionErrors[0] + 1})`);
          if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }
    
    // Clear validation errors on successful validation
    setValidationErrors({ topic: false, subjectLines: [], instructions: [] });
    
    setIsProcessing(true);
    
    try {
      const updateRef = masterEditTopic.isCustom
        ? ref(database, `/World/${userId}/settings/customTopics/${masterEditTopic.id}`)
        : ref(database, `/WorldSettings/topicsTemplate/${masterEditTopic.id}`);
      
      const effectiveSubjectMode = (masterEditData.type !== 'intro' && masterEditData.subjectMode === 'reply_to_previous')
        ? 'reply_to_previous'
        : 'new';

      await update(updateRef, {
        subjectLines: validSubjectLines,
        topicSpecificNotes: validInstructions,
        topic: masterEditData.topic,
        aiTagId: masterEditData.aiTagId,
        type: masterEditData.type,
        generalInfo: masterEditData.generalInfo || '',
        chanceRadarId: masterEditData.chanceRadarId || '',
        aiAdherence: masterEditData.aiAdherence || 'exact',
        formattingStyle: masterEditData.formattingStyle || 'as-written',
        model: masterEditData.model || 'gpt-4.1-mini',
        maxTokens: masterEditData.maxTokens || 500,
        writingLevel: masterEditData.writingLevel || 'middle_school',
        aiTemperature: masterEditData.aiTemperature != null ? masterEditData.aiTemperature : null,
        hyperIntelligence: masterEditData.hyperIntelligence || false,
        ultraMode: masterEditData.ultraMode || false,
        personalizationGuide: masterEditData.personalizationGuide || '',
        referenceFile: masterEditData.referenceFile || null,
        smartGreeting: masterEditData.smartGreeting || false,
        greetingCasualPrefixes: masterEditData.greetingCasualPrefixes || ['Hey', 'Hi', 'Hello'],
        greetingFormalPrefixes: masterEditData.greetingFormalPrefixes || ['Dear'],
        smartSubjectLine: masterEditData.smartSubjectLine || false,
        smartSubjectStyle: masterEditData.smartSubjectStyle || 'neutral',
        smartSubjectInstructions: masterEditData.smartSubjectInstructions || '',
        subjectMode: effectiveSubjectMode,
        replyPrefix: masterEditData.replyPrefix || 'AW',
        replyPrefixCustom: masterEditData.replyPrefixCustom || '',
        emailPreference: masterEditData.emailPreference || 'auto',
        dmFallback: masterEditData.dmFallback === 'strict' ? 'strict' : 'info',
        callContext: normalizeCallContext(masterEditData.callContext),
        language: masterEditData.language || 'German',
        subjectLine: null,
        topicSpecificNote: null
      });
      
      if (selectedTopics[masterEditTopic.id]) {
        const selectedRef = ref(database, `/World/${userId}/settings/selectedTopics/${masterEditTopic.id}`);
        await update(selectedRef, {
          topic: masterEditData.topic,
          subjectLines: validSubjectLines,
          topicSpecificNotes: validInstructions,
          type: masterEditData.type,
          isCustom: masterEditTopic.isCustom,
          aiTagId: masterEditData.aiTagId,
          generalInfo: masterEditData.generalInfo || '',
        chanceRadarId: masterEditData.chanceRadarId || '',
          aiAdherence: masterEditData.aiAdherence || 'exact',
          formattingStyle: masterEditData.formattingStyle || 'as-written',
          model: masterEditData.model || 'gpt-4.1-mini',
          maxTokens: masterEditData.maxTokens || 500,
          writingLevel: masterEditData.writingLevel || 'middle_school',
          aiTemperature: masterEditData.aiTemperature != null ? masterEditData.aiTemperature : null,
          hyperIntelligence: masterEditData.hyperIntelligence || false,
          ultraMode: masterEditData.ultraMode || false,
          personalizationGuide: masterEditData.personalizationGuide || '',
          referenceFile: masterEditData.referenceFile || null,
          smartGreeting: masterEditData.smartGreeting || false,
          greetingCasualPrefixes: masterEditData.greetingCasualPrefixes || ['Hey', 'Hi', 'Hello'],
          greetingFormalPrefixes: masterEditData.greetingFormalPrefixes || ['Dear'],
          smartSubjectLine: masterEditData.smartSubjectLine || false,
          smartSubjectStyle: masterEditData.smartSubjectStyle || 'neutral',
          smartSubjectInstructions: masterEditData.smartSubjectInstructions || '',
          subjectMode: effectiveSubjectMode,
          replyPrefix: masterEditData.replyPrefix || 'AW',
          replyPrefixCustom: masterEditData.replyPrefixCustom || '',
          emailPreference: masterEditData.emailPreference || 'auto',
          dmFallback: masterEditData.dmFallback === 'strict' ? 'strict' : 'info',
          callContext: normalizeCallContext(masterEditData.callContext),
          language: masterEditData.language || 'German',
          subjectLine: null,
          topicSpecificNote: null
        });
      }
      
      setMasterSaved(true);
      setTimeout(() => setMasterSaved(false), 3000);
      
      // Check if this is a new template (ID starts with 'custom_' and doesn't exist in customTopics yet)
      const isNewTemplate = masterEditTopic.isCustom && masterEditTopic.id.startsWith('custom_') && !customTopics[masterEditTopic.id];
      setMessage(isNewTemplate ? "Template successfully created." : "Template successfully updated.");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error('Error saving template:', error);
      setMessage("Error saving template. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  // ===== END Master Editor Functions =====

  if (!userId) {
    return <p className="errorMessage">Please login to save settings.</p>;
  }

  const groupedTopics = groupTopicsByType();

  // ===== PDF Reference File Upload (Hyper-Intelligence) =====
  const handleReferenceFileUpload = async (file) => {
    if (!file || !userId) return;
    if (file.type !== 'application/pdf') {
      setMessage('Only PDF files are supported.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessage('File too large (max 10 MB).');
      return;
    }

    setRefFileUploading(true);
    try {
      const storage = getStorage();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `reference_files/${userId}/${Date.now()}_${safeName}`;
      const fileRef = storageRef(storage, storagePath);
      await uploadBytes(fileRef, file);
      // Convenience URL only — the backend reads the file via `storagePath`.
      // Team members upload into the world owner's folder and cannot read it
      // back, so this must never abort the upload.
      let downloadURL = null;
      try {
        downloadURL = await getDownloadURL(fileRef);
      } catch (urlErr) {
        console.warn('[REF-FILE] Download URL unavailable, continuing with storagePath:', urlErr?.message);
      }

      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.uploadReferenceFile(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storagePath, fileName: file.name, userId }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Upload processing failed');
      }

      const result = await response.json();
      setMasterEditData(prev => ({
        ...prev,
        referenceFile: {
          fileName: file.name,
          storageUrl: downloadURL,
          storagePath,
          claudeFileId: result.claudeFileId || null,
          extractedText: result.extractedText || '',
          uploadedAt: new Date().toISOString(),
          fileSizeBytes: result.fileSizeBytes || file.size,
        },
      }));
      setMasterSaved(false);
    } catch (err) {
      console.error('Reference file upload failed:', err);
      setMessage('Upload failed: ' + err.message);
    } finally {
      setRefFileUploading(false);
    }
  };

  const handleReferenceFileDelete = async () => {
    const rf = masterEditData.referenceFile;
    if (!rf) return;

    try {
      if (rf.storagePath) {
        const storage = getStorage();
        const fileRef = storageRef(storage, rf.storagePath);
        await deleteObject(fileRef).catch(() => {});
      }
      if (rf.claudeFileId) {
        await authenticatedFetch(CLOUD_FUNCTION_URLS.deleteReferenceFile(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ claudeFileId: rf.claudeFileId }),
        }).catch(() => {});
      }
    } catch (err) {
      console.warn('Reference file cleanup error:', err.message);
    }

    setMasterEditData(prev => ({ ...prev, referenceFile: null }));
    setMasterSaved(false);
  };

  // ===== RENDER: Master Editor View =====
  const renderMasterEditor = () => {
    if (!masterEditTopic) return null;
    
    const isSelected = !!selectedTopics[masterEditTopic.id];
    const topicTypeLabel = topicTypes.find(t => t.value === masterEditData.type)?.label || masterEditData.type;
    const stats = masterEditTopic.stats || {};
    const totalSent = stats.totalSent || 0;
    const totalReplies = stats.totalReplies || 0;
    const replyRate = totalSent > 0 ? ((totalReplies / totalSent) * 100).toFixed(1) : '0.0';
    const rateClass = totalSent === 0 ? 'neutral' : parseFloat(replyRate) >= 10 ? 'good' : parseFloat(replyRate) >= 5 ? 'average' : 'low';
    
    return (
      <div className="ed-master-overlay">
        <div className="ed-master-container">
          {/* ===== TOP BAR ===== */}
          <div className="ed-topbar">
            <div className="ed-topbar-left">
              <button className="ed-back-button" onClick={closeMasterEditor} title="Back to Templates">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
              <div className="ed-topbar-divider"></div>
              <div className="ed-topbar-title">
                <input
                  type="text"
                  className={`ed-topbar-name-input ${validationErrors.topic ? 'ed-validation-error' : ''}`}
                  value={masterEditData.topic}
                  onChange={(e) => {
                    setMasterEditData(prev => ({ ...prev, topic: e.target.value }));
                    if (validationErrors.topic) {
                      setValidationErrors(prev => ({ ...prev, topic: false }));
                    }
                  }}
                  placeholder="Template name..."
                />
                <span className="ed-topbar-badge">{topicTypeLabel}</span>
              </div>
            </div>
            <div className="ed-topbar-right">
              <button 
                className={`ed-activate-button ${isSelected ? 'active' : ''}`}
                onClick={() => handleTopicToggle(masterEditTopic.id, { ...masterEditTopic, ...masterEditData })}
                disabled={isProcessing}
              >
                <span className={`ed-activate-dot ${isSelected ? 'active' : ''}`}></span>
                <span>{isSelected ? 'Active' : 'Inactive'}</span>
              </button>
              <button 
                className={`ed-save-button ${masterSaved ? 'saved' : ''}`}
                onClick={handleMasterSave}
                disabled={isProcessing || masterSaved}
              >
                {masterSaved ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    <span>Saved</span>
                  </>
                ) : isProcessing ? (
                  <>
                    <div className="ed-spinner"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                      <polyline points="17 21 17 13 7 13 7 21"></polyline>
                      <polyline points="7 3 7 8 15 8"></polyline>
                    </svg>
                    <span>Save</span>
                  </>
                )}
              </button>
              <div className="ed-topbar-kebab-wrap">
                <button 
                  className={`ed-topbar-kebab ${topbarMenuOpen ? 'active' : ''}`}
                  onClick={() => setTopbarMenuOpen(!topbarMenuOpen)}
                  title="More actions"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
                </button>
                {topbarMenuOpen && (
                  <>
                    <div className="ed-topbar-kebab-overlay" onClick={() => setTopbarMenuOpen(false)}></div>
                    <div className="ed-topbar-kebab-menu">
                      <button 
                        className={`ed-kebab-item ${aiAssistOpen ? 'active' : ''}`}
                        onClick={() => { setAiAssistOpen(!aiAssistOpen); setTopbarMenuOpen(false); }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2a4 4 0 0 1 4 4v1a3 3 0 0 1 3 3v1a2 2 0 0 1-2 2h-1v2a4 4 0 0 1-8 0v-2H7a2 2 0 0 1-2-2v-1a3 3 0 0 1 3-3V6a4 4 0 0 1 4-4z"/>
                          <circle cx="9" cy="10" r="1"/>
                          <circle cx="15" cy="10" r="1"/>
                          <path d="M9.5 15a3.5 3.5 0 0 0 5 0"/>
                        </svg>
                        <span>{aiAssistOpen ? 'Close AI Assist' : 'AI Assist'}</span>
                      </button>
                      {masterEditTopic.isCustom && (
                        <button 
                          className="ed-kebab-item ed-kebab-danger"
                          onClick={() => {
                            setTopbarMenuOpen(false);
                            setConfirmModal({
                              open: true,
                              title: "Delete Template",
                              message: `Delete "${masterEditData.topic}"? This cannot be undone.`,
                              onConfirm: () => {
                                setConfirmModal(prev => ({ ...prev, open: false }));
                                handleDeleteCustomTopic(masterEditTopic.id);
                                closeMasterEditor();
                              }
                            });
                          }}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                          <span>Delete Template</span>
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ===== MAIN CONTENT ===== */}
          <div className="ed-main">
            {/* ===== SIDEBAR ===== */}
            <div className="ed-sidebar">
              {/* ── Performance Strip (nur wenn Daten vorhanden) ── */}
              {totalSent > 0 && (
                <div className="ed-sb-perf">
                  <div className="ed-sb-stat">
                    <div className="ed-sb-stat-circle">
                      <span className="ed-sb-stat-val">{totalSent}</span>
                    </div>
                    <span className="ed-sb-stat-lbl">Sent</span>
                  </div>
                  <div className="ed-sb-stat">
                    <div className="ed-sb-stat-circle">
                      <span className="ed-sb-stat-val">{totalReplies}</span>
                    </div>
                    <span className="ed-sb-stat-lbl">Replies</span>
                  </div>
                  <div className={`ed-sb-stat ed-sb-stat-accent ed-rate-bg-${rateClass}`}>
                    <div className="ed-sb-stat-circle">
                      <span className={`ed-sb-stat-val ed-rate-${rateClass}`}>{replyRate}%</span>
                    </div>
                    <span className="ed-sb-stat-lbl">Reply Rate</span>
                  </div>
                </div>
              )}

              {/* ── Settings Row ── */}
              <div className="ed-sb-row">
                <div className="ed-sb-field">
                  <label className="ed-sb-label">Type</label>
                  <select
                    value={masterEditData.type}
                    onChange={(e) => setMasterEditData(prev => ({ ...prev, type: e.target.value }))}
                    className="ed-sb-select"
                  >
                    {topicTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div className="ed-sb-field">
                  <label className="ed-sb-label">AI Tag</label>
                  <select
                    value={masterEditData.aiTagId}
                    onChange={(e) => setMasterEditData(prev => ({ ...prev, aiTagId: e.target.value }))}
                    className="ed-sb-select"
                  >
                    <option value="">None</option>
                    {aiTags.map(tag => (
                      <option key={tag.id} value={tag.id}>{tag.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* ── AI Model Config ── */}
              <div className="ed-sb-row">
                <div className="ed-sb-field">
                  <label className="ed-sb-label">AI Model</label>
                  <select
                    value={masterEditData.model}
                    onChange={(e) => setMasterEditData(prev => ({ ...prev, model: e.target.value }))}
                    className="ed-sb-select"
                  >
                    <optgroup label="GPT-5.4 (Newest)">
                      <option value="gpt-5.4-mini">5.4 Mini — Best Value</option>
                      <option value="gpt-5.4-nano">5.4 Nano — Fastest</option>
                    </optgroup>
                    <optgroup label="Claude (Anthropic)">
                      <option value="claude-haiku-4-5">Claude Haiku 4.5 — Natural Writing</option>
                    </optgroup>
                    <optgroup label="GPT-4 (Legacy)">
                      <option value="gpt-4.1-mini">4.1 Mini</option>
                      <option value="gpt-4.1-nano">4.1 Nano</option>
                      <option value="gpt-4o-mini">4o Mini</option>
                    </optgroup>
                  </select>
                </div>
                <div className="ed-sb-field">
                  <label className="ed-sb-label">Max Tokens</label>
                  <input
                    type="number"
                    value={masterEditData.maxTokens}
                    onChange={(e) => setMasterEditData(prev => ({ ...prev, maxTokens: parseInt(e.target.value) || 500 }))}
                    className="ed-sb-select"
                    min="100"
                    max="4000"
                    step="100"
                  />
                </div>
              </div>

              {/* ── Writing Level ── */}
              <div className="ed-sb-row">
                <div className="ed-sb-field" style={{ flex: 1 }}>
                  <label className="ed-sb-label">
                    Language Complexity
                    <span
                      className="ed-sb-label-info"
                      title="Controls vocabulary range and sentence complexity of AI-generated text. Lower levels use simpler, more common words. Higher levels allow richer, more sophisticated vocabulary. All levels maintain a professional business tone."
                    >
                      <i className="fa-solid fa-circle-info" />
                    </span>
                  </label>
                  <div className="ed-writing-level-selector">
                    {[
                      { value: 'elementary', label: 'L1', desc: 'Basic', icon: '🟢' },
                      { value: 'middle_school', label: 'L2', desc: 'Simple', icon: '🔵' },
                      { value: 'high_school', label: 'L3', desc: 'Standard', icon: '🟣' },
                      { value: 'college', label: 'L4', desc: 'Advanced', icon: '🟠' },
                      { value: 'native', label: 'L5', desc: 'Expert', icon: '🔴' },
                    ].map(level => (
                      <button
                        key={level.value}
                        type="button"
                        className={`ed-writing-level-btn ${masterEditData.writingLevel === level.value ? 'active' : ''}`}
                        onClick={() => setMasterEditData(prev => ({ ...prev, writingLevel: level.value }))}
                        title={level.desc}
                      >
                        <span className="ed-wl-grade">{level.label}</span>
                        <span className="ed-wl-name">{level.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Email Preference ── */}
              <div className="ed-sb-row">
                <div className="ed-sb-field" style={{ flex: 1 }}>
                  <label className="ed-sb-label">
                    Email Preference
                    <span
                      className="ed-sb-label-info"
                      title="Which address this template writes to. Auto picks the best one. General uses the company inbox. Decision Maker prefers a personal contact, and uses the company inbox when that contact has no address unless you turn that off below."
                    >
                      <i className="fa-solid fa-circle-info" />
                    </span>
                  </label>
                  <select
                    value={masterEditData.emailPreference || 'auto'}
                    onChange={(e) => setMasterEditData(prev => ({ ...prev, emailPreference: e.target.value }))}
                    className="ed-sb-select"
                  >
                    <option value="auto">Auto — Selects the best choice available</option>
                    <option value="general">General — Main business email (info@, contact@)</option>
                    <option value="decision_maker">Decision Maker — Personal contact at the company</option>
                  </select>
                  {(masterEditData.emailPreference || 'auto') === 'decision_maker' && (
                    <>
                      <label className="ed-sb-label" style={{ marginTop: 12 }} htmlFor="ed-dm-fallback">
                        If no decision-maker address
                        <span
                          className="ed-sb-label-info"
                          title="Default: write the company inbox (info@, contact@) when the decision maker has no email. Skip leaves that contact out of the agent."
                        >
                          <i className="fa-solid fa-circle-info" />
                        </span>
                      </label>
                      <select
                        id="ed-dm-fallback"
                        value={masterEditData.dmFallback === 'strict' ? 'strict' : 'info'}
                        onChange={(e) => setMasterEditData(prev => ({ ...prev, dmFallback: e.target.value === 'strict' ? 'strict' : 'info' }))}
                        className="ed-sb-select"
                      >
                        <option value="info">Use the company email</option>
                        <option value="strict">Skip this contact</option>
                      </select>
                    </>
                  )}
                </div>
              </div>

              {/* ── AI Creativity (Temperature) ── */}
              <div className="ed-sb-temperature-section">
                <label className="ed-sb-label">
                  {aiLabels.aiTemperature || 'AI Creativity'}
                  <span className="ed-sb-temperature-value">
                    {masterEditData.aiTemperature != null ? masterEditData.aiTemperature.toFixed(1) : (aiLabels.aiTemperatureAuto || 'Auto')}
                  </span>
                </label>
                <input
                  type="range"
                  min="0.1"
                  max="1.0"
                  step="0.1"
                  value={masterEditData.aiTemperature != null ? masterEditData.aiTemperature : 0.7}
                  onChange={(e) => {
                    setMasterEditData(prev => ({ ...prev, aiTemperature: parseFloat(e.target.value) }));
                    setMasterSaved(false);
                  }}
                  className="ed-sb-temperature-slider"
                />
                {masterEditData.aiTemperature != null && (
                  <button
                    type="button"
                    className="ed-sb-temperature-reset"
                    onClick={() => {
                      setMasterEditData(prev => ({ ...prev, aiTemperature: null }));
                      setMasterSaved(false);
                    }}
                  >
                    ↺ {aiLabels.aiTemperatureAuto || 'Auto'}
                  </button>
                )}
              </div>

              {/* ── AI Context (collapsible) ── */}
              <div className="ed-sb-ai-section">
                <div className="ed-sb-ai-header" onClick={() => setAiContextOpen(prev => !prev)}>
                  <div className="ed-sb-ai-header-left">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                    <span className="ed-sb-ai-header-label">{aiLabels.aiContext}</span>
                    <span className="ed-sb-ai-header-tag">{aiLabels.optional}</span>
                  </div>
                  <svg 
                    width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                    className={`ed-sb-ai-chevron ${aiContextOpen ? 'open' : ''}`}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
                <div className={`ed-sb-ai-context ${aiContextOpen ? 'expanded' : 'collapsed'}`}>
                  {/* Language selector — always on top */}
                  <div className="ed-ai-field">
                    <label className="ed-ai-field-label"><i className="fa fa-globe"></i> {aiLabels.language}</label>
                    <div className="ed-ai-lang-grid">
                      {[
                        { value: 'German', label: 'DE', flag: '🇩🇪' },
                        { value: 'English', label: 'EN', flag: '🇬🇧' },
                        { value: 'French', label: 'FR', flag: '🇫🇷' },
                        { value: 'Spanish', label: 'ES', flag: '🇪🇸' },
                        { value: 'Italian', label: 'IT', flag: '🇮🇹' },
                        { value: 'Portuguese', label: 'PT', flag: '🇵🇹' },
                        { value: 'Dutch', label: 'NL', flag: '🇳🇱' },
                        { value: 'Polish', label: 'PL', flag: '🇵🇱' },
                        { value: 'Turkish', label: 'TR', flag: '🇹🇷' },
                        { value: 'Swedish', label: 'SV', flag: '🇸🇪' },
                        { value: 'Japanese', label: 'JA', flag: '🇯🇵' },
                        { value: 'Chinese', label: 'ZH', flag: '🇨🇳' },
                      ].map(lang => (
                        <button
                          key={lang.value}
                          className={`ed-ai-lang-btn ${aiContext.language === lang.value ? 'active' : ''}`}
                          onClick={() => {
                            setAiContext(prev => ({ ...prev, language: lang.value }));
                            setMasterEditData(prev => ({ ...prev, language: lang.value }));
                          }}
                          type="button"
                          title={lang.value}
                        >
                          <span className="ed-ai-lang-flag">{lang.flag}</span>
                          <span className="ed-ai-lang-code">{lang.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="ed-ai-divider"></div>

                  {/* Account Brain — reuse World Settings vault as source of truth */}
                  <div className={`ed-ai-brain ${aiContext.useAccountBrain && brainHasCopyNotes ? 'is-on' : ''} ${vaultLoaded && !brainHasCopyNotes ? 'is-empty' : ''}`}>
                    <div
                      className="ed-ai-brain-row"
                      onClick={() => brainHasCopyNotes && setAiContext(prev => ({ ...prev, useAccountBrain: !prev.useAccountBrain }))}
                      onKeyDown={(e) => {
                        if (!brainHasCopyNotes) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setAiContext(prev => ({ ...prev, useAccountBrain: !prev.useAccountBrain }));
                        }
                      }}
                      role={brainHasCopyNotes ? 'switch' : undefined}
                      tabIndex={brainHasCopyNotes ? 0 : undefined}
                      aria-checked={brainHasCopyNotes ? !!aiContext.useAccountBrain : undefined}
                    >
                      <div className="ed-ai-brain-copy">
                        <span className="ed-ai-brain-title">
                          <i className="fa fa-brain"></i>
                          {aiLabels.accountBrain || 'Account Brain'}
                        </span>
                        <span className="ed-ai-brain-hint">
                          {!vaultLoaded
                            ? (aiLabels.accountBrainHint || 'Reuse notes from World Settings as the account source of truth.')
                            : !brainHasCopyNotes
                              ? (aiLabels.accountBrainEmpty || 'No copywriting notes yet. Add them in World Settings → Cockpit.')
                              : aiContext.useAccountBrain
                                ? (aiLabels.accountBrainOn || 'On — copywriting notes go into every generation')
                                : (aiLabels.accountBrainOff || 'Off — only the fields below are used')}
                        </span>
                      </div>
                      {brainHasCopyNotes && (
                        <div className={`ed-ai-toggle-switch ${aiContext.useAccountBrain ? 'on' : ''}`}>
                          <div className="ed-ai-toggle-knob"></div>
                        </div>
                      )}
                    </div>
                    {brainHasCopyNotes && (
                      <div className="ed-ai-brain-meta">
                        <span className="ed-ai-brain-count">
                          {vaultCopySummary.count} {aiLabels.accountBrainNotes || 'notes'}
                        </span>
                        <div className="ed-ai-brain-chips">
                          {vaultCopySummary.categories.map((cat) => (
                            <span key={cat.id} className="ed-ai-brain-chip">{cat.label}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {aiContext.useAccountBrain && brainHasCopyNotes && (
                    <p className="ed-ai-brain-fields-hint">{aiLabels.accountBrainFieldsHint}</p>
                  )}

                  {/* Text inputs */}
                  <div className="ed-ai-field">
                    <label className="ed-ai-field-label"><i className="fa fa-store"></i> {aiLabels.describeBusiness}</label>
                    <textarea
                      className="ed-ai-field-input ed-ai-field-textarea"
                      placeholder={aiLabels.businessPlaceholder}
                      value={aiContext.businessDescription}
                      onChange={(e) => setAiContext(prev => ({ ...prev, businessDescription: e.target.value }))}
                      rows={3}
                    />
                  </div>
                  <div className="ed-ai-field">
                    <label className="ed-ai-field-label"><i className="fa fa-crosshairs"></i> {aiLabels.goalPurpose}</label>
                    <input
                      type="text"
                      className="ed-ai-field-input"
                      placeholder={aiLabels.goalPlaceholder}
                      value={aiContext.purpose}
                      onChange={(e) => setAiContext(prev => ({ ...prev, purpose: e.target.value }))}
                    />
                  </div>
                  <div className="ed-ai-field">
                    <label className="ed-ai-field-label"><i className="fa fa-users"></i> {aiLabels.targetAudience}</label>
                    <input
                      type="text"
                      className="ed-ai-field-input"
                      placeholder={aiLabels.audiencePlaceholder}
                      value={aiContext.targetAudience}
                      onChange={(e) => setAiContext(prev => ({ ...prev, targetAudience: e.target.value }))}
                    />
                  </div>
                  <div className="ed-ai-field">
                    <label className="ed-ai-field-label"><i className="fa fa-key"></i> {aiLabels.keyPoints}</label>
                    <input
                      type="text"
                      className="ed-ai-field-input"
                      placeholder={aiLabels.keyPointsPlaceholder}
                      value={aiContext.keyPoints}
                      onChange={(e) => setAiContext(prev => ({ ...prev, keyPoints: e.target.value }))}
                    />
                  </div>

                </div>
              </div>
            </div>

            {/* ===== EDITOR AREA ===== */}
            <div className="ed-editor">

              {/* ===== GENERAL INFO SECTION ===== */}
              <div className="ed-section ed-general-info-section">
                <div className="ed-section-header">
                  <div className="ed-section-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  </div>
                  <h3>{aiLabels.generalInfo}</h3>
                  <span className="ed-section-badge">{aiLabels.aiContextBadge}</span>
                </div>

                {/* General context textarea */}
                <div className="ed-gi-field">
                  <label className="ed-gi-label">
                    <i className="fa fa-align-left"></i>
                    <span>Additional Context</span>
                    <span className="ed-gi-hint">This info is always passed to the AI alongside your templates</span>
                  </label>
                  <textarea
                    className="ed-gi-textarea"
                    placeholder="e.g. We always mention our 30-day money-back guarantee. Our USP is the personal onboarding call. Never be too salesy..."
                    value={masterEditData.generalInfo}
                    onChange={(e) => setMasterEditData(prev => ({ ...prev, generalInfo: e.target.value }))}
                    rows={3}
                  />
                </div>

                <ChanceRadarTemplateField
                  userId={userId}
                  value={masterEditData.chanceRadarId || ""}
                  onChange={(chanceRadarId) => setMasterEditData((prev) => ({ ...prev, chanceRadarId }))}
                />

                {/* AI Adherence — 5 level buttons (dynamic labels when PDF is present) */}
                <div className="ed-gi-field">
                  <label className="ed-gi-label">
                    <i className="fa fa-sliders-h"></i>
                    <span>{masterEditData.referenceFile ? 'Style Range' : 'Template Adherence'}</span>
                    <span className="ed-gi-hint">
                      {masterEditData.referenceFile
                        ? 'How much should the AI lean toward your PDF writing style vs. the template?'
                        : 'How closely should the AI follow the template text?'}
                    </span>
                  </label>
                  {masterEditData.referenceFile && (
                    <div className="ed-gi-adherence-range-labels">
                      <span className="ed-gi-range-label template-side"><i className="fa fa-file-alt"></i> Template</span>
                      <span className="ed-gi-range-arrow">←→</span>
                      <span className="ed-gi-range-label pdf-side"><i className="fa fa-file-pdf"></i> PDF Style</span>
                    </div>
                  )}
                  <div className="ed-gi-adherence-grid">
                    {(masterEditData.referenceFile ? [
                      { value: 'exact', label: 'Template Only', icon: 'fa-lock', desc: 'Follow template exactly. PDF ignored.' },
                      { value: 'close', label: 'Template + Hints', icon: 'fa-thumbtack', desc: 'Template text, subtle PDF tone.' },
                      { value: 'balanced', label: 'Blend', icon: 'fa-balance-scale', desc: 'Template message, PDF writing style.' },
                      { value: 'loose', label: 'PDF-Led', icon: 'fa-feather-alt', desc: 'Topic from template, new angle & PDF voice.' },
                      { value: 'creative', label: 'Full PDF Voice', icon: 'fa-paint-brush', desc: 'New perspective, new ideas, full PDF voice.' },
                    ] : [
                      { value: 'exact', label: 'Exact', icon: 'fa-lock', desc: 'Word-for-word. Only fix typos.' },
                      { value: 'close', label: 'Close', icon: 'fa-thumbtack', desc: 'Same structure, light rephrasing.' },
                      { value: 'balanced', label: 'Balanced', icon: 'fa-balance-scale', desc: 'Keep core message, may reorder.' },
                      { value: 'loose', label: 'Loose', icon: 'fa-feather-alt', desc: 'Same topic, new angle & structure.' },
                      { value: 'creative', label: 'Creative', icon: 'fa-paint-brush', desc: 'New perspective & ideas. Topic only.' },
                    ]).map(level => (
                      <button
                        key={level.value}
                        type="button"
                        className={`ed-gi-adherence-btn ${masterEditData.aiAdherence === level.value ? 'active' : ''}`}
                        onClick={() => setMasterEditData(prev => ({ ...prev, aiAdherence: level.value }))}
                      >
                        <i className={`fa ${level.icon}`}></i>
                        <span className="ed-gi-adherence-name">{level.label}</span>
                        <span className="ed-gi-adherence-desc">{level.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Formatting Style */}
                <div className="ed-gi-field">
                  <label className="ed-gi-label">
                    <i className="fa fa-paragraph"></i>
                    <span>Formatting Style</span>
                    <span className="ed-gi-hint">How should the AI format the email output?</span>
                  </label>
                  <div className="ed-gi-format-grid">
                    {[
                      { value: 'as-written', label: 'As Written', icon: 'fa-file-alt', desc: 'Keep original formatting intact' },
                      { value: 'sentence-per-line', label: 'One Liner', icon: 'fa-bars', desc: 'Each sentence on its own line — super scannable' },
                      { value: 'short-paragraphs', label: 'Short Paragraphs', icon: 'fa-align-left', desc: '1–2 sentences per paragraph, clean look' },
                      { value: 'bullet-points', label: 'Bullet Points', icon: 'fa-list-ul', desc: 'Key points as bullets, intro/outro normal' },
                      { value: 'conversational', label: 'Conversational', icon: 'fa-comments', desc: 'Natural flow, like a real message' },
                    ].map(fmt => (
                      <button
                        key={fmt.value}
                        type="button"
                        className={`ed-gi-format-btn ${masterEditData.formattingStyle === fmt.value ? 'active' : ''}`}
                        onClick={() => setMasterEditData(prev => ({ ...prev, formattingStyle: fmt.value }))}
                      >
                        <i className={`fa ${fmt.icon}`}></i>
                        <span className="ed-gi-format-name">{fmt.label}</span>
                        <span className="ed-gi-format-desc">{fmt.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* AI Intelligence Features */}
                <div className="wst-ai-section">
                  <div className="wst-ai-section-header">
                    <div className="wst-ai-section-icon">
                      <i className="fas fa-brain" />
                    </div>
                    <span>AI Intelligence</span>
                  </div>

                  <div className="wst-ai-feature">
                    <div className="wst-ai-feature-row">
                      <div className="wst-ai-feature-icon"><i className="fas fa-bolt" /></div>
                      <div className="wst-ai-feature-info">
                        <span className="wst-ai-feature-name">Hyper-Intelligence <span className="wst-ai-credit-badge">{emailGenerationCost({ hyperIntelligence: true, hasReferenceFile: !!masterEditData.referenceFile })} Credits / Email</span></span>
                        <span className="wst-ai-feature-hint">3-step AI chain — deeper personalization per contact (standard: {emailGenerationCost()})</span>
                      </div>
                      <button type="button" className={`wst-hyper-toggle-btn ${masterEditData.hyperIntelligence ? 'active' : ''}`} onClick={() => setMasterEditData(prev => ({ ...prev, hyperIntelligence: !prev.hyperIntelligence }))}>
                        <span className="wst-hyper-toggle-knob" />
                      </button>
                    </div>

                    {masterEditData.hyperIntelligence && (<>
                      <div className="wst-ref-file-section">
                        <div className="wst-ref-file-header">
                          <div className="wst-ref-file-header-left">
                            <i className="fas fa-file-pdf" />
                            <div className="wst-ref-file-header-text">
                              <span className="wst-ref-file-title">Reference Writing Samples</span>
                              <span className="wst-ref-file-hint">Upload a PDF with example emails — AI matches your writing style (+2 Credits). Best results with 2–14 pages of real writing samples.</span>
                            </div>
                          </div>
                        </div>

                        {masterEditData.referenceFile ? (
                          <div className="wst-ref-file-card">
                            <div className="wst-ref-file-card-icon">
                              <i className="fas fa-file-pdf" />
                            </div>
                            <div className="wst-ref-file-card-info">
                              <span className="wst-ref-file-card-name">{masterEditData.referenceFile.fileName}</span>
                              <span className="wst-ref-file-card-meta">
                                {masterEditData.referenceFile.fileSizeBytes
                                  ? `${(masterEditData.referenceFile.fileSizeBytes / 1024).toFixed(0)} KB`
                                  : ''}
                                {masterEditData.referenceFile.claudeFileId && (
                                  <span className="wst-ref-file-card-status ok"><i className="fas fa-check-circle" /> Linked</span>
                                )}
                                {!masterEditData.referenceFile.claudeFileId && masterEditData.referenceFile.extractedText && (
                                  <span className="wst-ref-file-card-status warn"><i className="fas fa-exclamation-circle" /> Text only</span>
                                )}
                                {masterEditData.referenceFile.extractedText && (
                                  <span className="wst-ref-file-card-words">
                                    {masterEditData.referenceFile.extractedText.split(/\s+/).filter(Boolean).length.toLocaleString()} words extracted
                                    {masterEditData.referenceFile.extractedText.endsWith('[... truncated]') && ' (truncated)'}
                                  </span>
                                )}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="wst-ref-file-delete-btn"
                              onClick={handleReferenceFileDelete}
                              title="Remove reference file"
                            >
                              <i className="fas fa-trash-alt" />
                            </button>
                          </div>
                        ) : (
                          <label className={`wst-ref-file-upload-zone ${refFileUploading ? 'uploading' : ''}`}>
                            <input
                              type="file"
                              accept="application/pdf"
                              className="wst-ref-file-input"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleReferenceFileUpload(file);
                                e.target.value = '';
                              }}
                              disabled={refFileUploading}
                            />
                            {refFileUploading ? (
                              <div className="wst-ref-file-uploading">
                                <div className="ed-spinner" />
                                <span>Processing PDF...</span>
                              </div>
                            ) : (
                              <>
                                <i className="fas fa-cloud-upload-alt" />
                                <span>Upload PDF</span>
                                <span className="wst-ref-file-upload-hint">Max 10 MB · up to ~14 pages</span>
                              </>
                            )}
                          </label>
                        )}
                      </div>

                      <div className="wst-ultra-mode-row">
                        <label className="wst-ultra-mode-label">
                          <input
                            type="checkbox"
                            checked={masterEditData.ultraMode}
                            onChange={() => setMasterEditData(prev => ({ ...prev, ultraMode: !prev.ultraMode }))}
                          />
                          <span className="wst-ultra-mode-check" />
                          <i className="fas fa-gem" />
                          <span className="wst-ultra-mode-text">
                            <span className="wst-ultra-mode-name">Ultra Mode</span>
                            <span className="wst-ultra-mode-hint">Uses Claude Opus — maximum writing quality (5x cost)</span>
                          </span>
                        </label>
                      </div>
                    </>)}
                  </div>

                  <div className="wst-ai-feature">
                    <div className="wst-ai-feature-row">
                      <div className="wst-ai-feature-icon"><i className="fas fa-comment-dots" /></div>
                      <div className="wst-ai-feature-info">
                        <span className="wst-ai-feature-name">Smart Greeting <span className="wst-ai-credit-badge free">Included</span></span>
                        <span className="wst-ai-feature-hint">Use <code>{'{{greeting}}'}</code> — AI picks the right salutation per lead, no extra cost</span>
                      </div>
                      <button type="button" className={`wst-hyper-toggle-btn ${masterEditData.smartGreeting ? 'active' : ''}`} onClick={() => setMasterEditData(prev => ({ ...prev, smartGreeting: !prev.smartGreeting }))}>
                        <span className="wst-hyper-toggle-knob" />
                      </button>
                    </div>

                    {masterEditData.smartGreeting && (
                      <div className="wst-greeting-config">
                        <div className="wst-greeting-cols">
                          <div className="wst-greeting-col casual">
                            <div className="wst-greeting-col-label">
                              <i className="fa-solid fa-face-smile" />
                              <span>Casual</span>
                            </div>
                            <div className="wst-greeting-chips">
                              {(masterEditData.greetingCasualPrefixes || []).map((prefix, idx) => (
                                <span key={idx} className="wst-greeting-chip casual">
                                  {prefix}
                                  <button type="button" onClick={() => {
                                    setMasterEditData(prev => ({ ...prev, greetingCasualPrefixes: prev.greetingCasualPrefixes.filter((_, i) => i !== idx) }));
                                  }}><i className="fa-solid fa-xmark" /></button>
                                </span>
                              ))}
                              <input
                                className="wst-greeting-chip-input"
                                placeholder="e.g. Hallo, Moin…"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ',') {
                                    e.preventDefault();
                                    const val = e.target.value.trim().replace(/,$/,'');
                                    if (val && !(masterEditData.greetingCasualPrefixes || []).includes(val)) {
                                      setMasterEditData(prev => ({ ...prev, greetingCasualPrefixes: [...(prev.greetingCasualPrefixes || []), val] }));
                                      e.target.value = '';
                                    }
                                  }
                                }}
                              />
                            </div>
                          </div>

                          <div className="wst-greeting-col formal">
                            <div className="wst-greeting-col-label">
                              <i className="fa-solid fa-user-tie" />
                              <span>Formal</span>
                            </div>
                            <div className="wst-greeting-chips">
                              {(masterEditData.greetingFormalPrefixes || []).map((prefix, idx) => (
                                <span key={idx} className="wst-greeting-chip formal">
                                  {prefix}
                                  <button type="button" onClick={() => {
                                    setMasterEditData(prev => ({ ...prev, greetingFormalPrefixes: prev.greetingFormalPrefixes.filter((_, i) => i !== idx) }));
                                  }}><i className="fa-solid fa-xmark" /></button>
                                </span>
                              ))}
                              <input
                                className="wst-greeting-chip-input"
                                placeholder="e.g. Sehr geehrte/r…"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ',') {
                                    e.preventDefault();
                                    const val = e.target.value.trim().replace(/,$/,'');
                                    if (val && !(masterEditData.greetingFormalPrefixes || []).includes(val)) {
                                      setMasterEditData(prev => ({ ...prev, greetingFormalPrefixes: [...(prev.greetingFormalPrefixes || []), val] }));
                                      e.target.value = '';
                                    }
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="wst-greeting-tip">
                          <i className="fa-solid fa-info-circle" />
                          The greeting follows the tone of the email variation that gets used: Formal uses a formal greeting, Casual a casual one, and Auto detects it from your email text (e.g. "Sie" → formal, "du" → casual). Comma is added automatically.
                        </div>
                      </div>
                    )}
                  </div>

                  <CallContextSettings
                    value={masterEditData.callContext}
                    onChange={(callContext) => setMasterEditData(prev => ({ ...prev, callContext }))}
                  />
                </div>

                {(masterEditData.subjectLines.some(l => l.text?.trim()) ||
                  masterEditData.topicSpecificNotes.some(n => n.text?.trim())) && (
                  <div style={{ padding: '0 2px', marginTop: 8 }}>
                    <TemplateDeliverabilityChecker
                      variants={masterEditData.topicSpecificNotes}
                      subjectVariants={masterEditData.subjectLines}
                    />
                    <EmailComplianceChecker
                      emailText={masterEditData.topicSpecificNotes.map(n => n.text).join('\n')}
                      subjectLine={masterEditData.subjectLines.map(l => l.text).join('\n')}
                    />
                  </div>
                )}

              </div>

              {/* Subject Lines Section */}
              <div className="ed-section">
                <div className="ed-section-header">
                  <div className="ed-section-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="14" y2="15"/><path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/></svg>
                  </div>
                  <h3>{aiLabels.subjectLines}</h3>
                  <span className="ed-section-count">{masterEditData.subjectLines.length} {masterEditData.subjectLines.length !== 1 ? aiLabels.variants2 : aiLabels.variant}</span>
                </div>

                {/* Follow-Up Subject Mode (only for non-intro templates) */}
                {masterEditData.type !== 'intro' && (() => {
                  const isReply = masterEditData.subjectMode === 'reply_to_previous';
                  const lang = masterEditData.language || 'English';
                  const isGerman = /^german$|^deutsch$|^de$/i.test(lang);
                  return (
                    <div className={`wst-subj-mode-card ${isReply ? 'reply-active' : ''}`}>
                      <div className="wst-subj-mode-header">
                        <div className="wst-subj-mode-title-wrap">
                          <div className="wst-subj-mode-icon"><i className="fa fa-reply"></i></div>
                          <div className="wst-subj-mode-title-text">
                            <span className="wst-subj-mode-title">Subject line mode</span>
                            <span className="wst-subj-mode-desc">
                              {isGerman
                                ? 'Neue Betreffzeilen oder an die vorherige Mail anknüpfen?'
                                : 'Use fresh subject lines or thread onto the previous email?'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="wst-subj-mode-options">
                        <button
                          type="button"
                          className={`wst-subj-mode-option ${!isReply ? 'active' : ''}`}
                          onClick={() => setMasterEditData(prev => ({ ...prev, subjectMode: 'new' }))}
                        >
                          <div className="wst-subj-mode-option-head">
                            <i className="fa fa-pen"></i>
                            <span className="wst-subj-mode-option-name">New subject line</span>
                            {!isReply && <span className="ed-ai-persona-check"><i className="fa fa-check"></i></span>}
                          </div>
                          <span className="wst-subj-mode-option-desc">
                            Pick a variant from the list below (AI-smart or classic).
                          </span>
                        </button>
                        <button
                          type="button"
                          className={`wst-subj-mode-option ${isReply ? 'active' : ''}`}
                          onClick={() => setMasterEditData(prev => ({ ...prev, subjectMode: 'reply_to_previous' }))}
                        >
                          <div className="wst-subj-mode-option-head">
                            <i className="fa fa-reply"></i>
                            <span className="wst-subj-mode-option-name">Reply to previous email</span>
                            {isReply && <span className="ed-ai-persona-check"><i className="fa fa-check"></i></span>}
                          </div>
                          <span className="wst-subj-mode-option-desc">
                            Reuse the last subject sent to the contact, prefixed with "Re:" / "AW:".
                          </span>
                        </button>
                      </div>

                      {isReply && (
                        <div className="wst-subj-mode-config">
                          <div className="wst-subj-mode-prefix-row">
                            <label className="wst-smart-subject-label">
                              <i className="fa fa-tag"></i> Prefix
                            </label>
                            <div className="wst-subj-mode-prefix-pills">
                              {[
                                { value: 'AW', label: 'AW:', desc: 'German convention' },
                                { value: 'RE', label: 'Re:', desc: 'English / international' },
                                { value: 'custom', label: 'Custom', desc: 'Define your own' },
                              ].map(p => (
                                <button
                                  key={p.value}
                                  type="button"
                                  className={`wst-subj-mode-prefix-pill ${masterEditData.replyPrefix === p.value ? 'active' : ''}`}
                                  onClick={() => setMasterEditData(prev => ({ ...prev, replyPrefix: p.value }))}
                                  title={p.desc}
                                >
                                  {p.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          {masterEditData.replyPrefix === 'custom' && (
                            <input
                              type="text"
                              className="wst-subj-mode-custom-input"
                              value={masterEditData.replyPrefixCustom}
                              onChange={(e) => setMasterEditData(prev => ({ ...prev, replyPrefixCustom: e.target.value.slice(0, 16) }))}
                              placeholder="e.g. Antw, FWD"
                              maxLength={16}
                            />
                          )}
                          <div className="wst-subj-mode-hint">
                            <i className="fa fa-info-circle"></i>
                            <span>
                              {isGerman
                                ? 'Wir verwenden die Betreffzeile der zuletzt gesendeten E-Mail an diesen Kontakt. Vorhandene "Re:" / "AW:" Prefixe werden automatisch bereinigt.'
                                : 'We use the subject of the last email sent to this contact. Existing "Re:" / "AW:" prefixes are cleaned up automatically.'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Variants wrapper — dimmed when reply-to-previous is active */}
                <div className={`wst-subj-variants-wrap ${masterEditData.type !== 'intro' && masterEditData.subjectMode === 'reply_to_previous' ? 'dimmed' : ''}`}>
                {masterEditData.type !== 'intro' && masterEditData.subjectMode === 'reply_to_previous' && (
                  <div className="wst-subj-variants-dim-note">
                    <i className="fa fa-info-circle"></i>
                    <span>Subject variants are paused while "Reply to previous email" is active. They stay saved and will be used again if you switch back.</span>
                  </div>
                )}
                {/* Smart Subject Line Feature */}
                <div className="wst-smart-subject-card">
                  <div className="wst-smart-subject-header">
                    <div className="wst-smart-subject-header-left">
                      <div className="wst-smart-subject-icon-wrap">
                        <i className="fa fa-brain"></i>
                      </div>
                      <div className="wst-smart-subject-header-text">
                        <span className="wst-smart-subject-title">{aiLabels.smartSubjectTitle}</span>
                        <span className="wst-smart-subject-desc">{aiLabels.smartSubjectDesc}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={`wst-hyper-toggle-btn ${masterEditData.smartSubjectLine ? 'active' : ''}`}
                      onClick={() => setMasterEditData(prev => ({ ...prev, smartSubjectLine: !prev.smartSubjectLine }))}
                    >
                      <span className="wst-hyper-toggle-knob" />
                    </button>
                  </div>

                  {(() => {
                    const smartStats = masterEditTopic.stats?.subjectLineStats?.['smart_subject'];
                    const resolvedStats = masterEditTopic.stats?.subjectLineStats?.['resolved_subject'];
                    const aiSent = (smartStats?.sent || 0) + (resolvedStats?.sent || 0);
                    const aiReplies = (smartStats?.replies || 0) + (resolvedStats?.replies || 0);
                    if (aiSent === 0) return null;
                    return (
                      <div className="wst-smart-subject-stats">
                        <AnalyticsRow sent={aiSent} replies={aiReplies} />
                      </div>
                    );
                  })()}

                  {masterEditData.smartSubjectLine && (
                    <div className="wst-smart-subject-config">
                      <div className="wst-smart-subject-field">
                        <label className="wst-smart-subject-label"><i className="fa fa-palette"></i> {aiLabels.smartSubjectStyleLabel}</label>
                        <div className="wst-smart-subject-styles">
                          {[
                            { value: 'curiosity', icon: 'fa-question-circle', label: aiLabels.smartSubjectCuriosity, desc: aiLabels.smartSubjectCuriosityDesc },
                            { value: 'direct', icon: 'fa-bullseye', label: aiLabels.smartSubjectDirect, desc: aiLabels.smartSubjectDirectDesc },
                            { value: 'neutral', icon: 'fa-handshake', label: aiLabels.smartSubjectNeutral, desc: aiLabels.smartSubjectNeutralDesc },
                            { value: 'humorous', icon: 'fa-laugh', label: aiLabels.smartSubjectHumorous, desc: aiLabels.smartSubjectHumorousDesc },
                            { value: 'technical', icon: 'fa-cogs', label: aiLabels.smartSubjectTechnical, desc: aiLabels.smartSubjectTechnicalDesc },
                            { value: 'personal', icon: 'fa-user', label: aiLabels.smartSubjectPersonal, desc: aiLabels.smartSubjectPersonalDesc },
                          ].map(s => (
                            <button
                              key={s.value}
                              type="button"
                              className={`wst-smart-subject-style-btn ${masterEditData.smartSubjectStyle === s.value ? 'active' : ''}`}
                              onClick={() => setMasterEditData(prev => ({ ...prev, smartSubjectStyle: s.value }))}
                            >
                              <i className={`fa ${s.icon}`}></i>
                              <div className="wst-smart-subject-style-text">
                                <span className="wst-smart-subject-style-name">{s.label}</span>
                                <span className="wst-smart-subject-style-desc">{s.desc}</span>
                              </div>
                              {masterEditData.smartSubjectStyle === s.value && <span className="ed-ai-persona-check"><i className="fa fa-check"></i></span>}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="wst-smart-subject-field">
                        <label className="wst-smart-subject-label"><i className="fa fa-pen"></i> {aiLabels.smartSubjectInstructionsLabel}</label>
                        <textarea
                          className="wst-smart-subject-instructions"
                          value={masterEditData.smartSubjectInstructions}
                          onChange={(e) => setMasterEditData(prev => ({ ...prev, smartSubjectInstructions: e.target.value }))}
                          placeholder={aiLabels.smartSubjectInstructionsPlaceholder}
                          rows={3}
                        />
                      </div>
                      <div className="wst-smart-subject-note">
                        <i className="fa fa-info-circle"></i>
                        <span>{aiLabels.smartSubjectNote}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="ed-subject-lines">
                  {masterEditData.subjectLines.map((subjectLine, index) => {
                    // Empty starter rows sit above the skeleton and look like a
                    // second place to type while AI is already writing. Keep
                    // filled variants visible so existing copy stays on screen.
                    if (aiGenerating?.type === 'subject' && !(subjectLine.text || '').trim()) {
                      return null;
                    }
                    return (
                    <div key={subjectLine.id} className={`ed-subject-line-card ${validationErrors.subjectLines.includes(index) ? 'ed-validation-error' : ''}`}>
                      <div className="ed-subject-line-number">{index + 1}</div>
                      <div className="ed-subject-line-content">
                        <input
                          type="text"
                          value={subjectLine.text}
                          onChange={(e) => {
                            masterUpdateSubjectLine(index, e.target.value);
                            if (validationErrors.subjectLines.includes(index)) {
                              setValidationErrors(prev => ({
                                ...prev,
                                subjectLines: prev.subjectLines.filter(i => i !== index)
                              }));
                            }
                          }}
                          placeholder="Enter subject line..."
                          className={`ed-subject-input ${validationErrors.subjectLines.includes(index) ? 'ed-validation-error' : ''}`}
                        />
                        <div className="ed-subject-line-meta">
                          <div className="ed-subject-line-stats">
                            <SubjectLineStats subjectLineId={subjectLine.id} stats={masterEditTopic.stats} />
                          </div>
                          <InlineComplianceBadge text={subjectLine.text} />
                        </div>
                      </div>
                      <div className="ed-card-actions">
                        {subjectLine.text?.trim() && (
                          <button
                            type="button"
                            className="ed-rewrite-btn"
                            onClick={() => setAiGenPopup({ type: 'subject', mode: 'rewrite', rewriteIndex: index, rewriteText: subjectLine.text })}
                            disabled={!!aiGenerating}
                            title="Rewrite with AI"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            <span>Rewrite</span>
                          </button>
                        )}
                        {masterEditData.subjectLines.length > 1 && (
                          <button
                            type="button"
                            className="ed-remove-btn"
                            onClick={() => masterRemoveSubjectLine(index)}
                            title="Remove subject line"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        )}
                      </div>
                    </div>
                    );
                  })}
                  {aiGenerating?.type === 'subject' && (
                    <div className="ed-skeleton-card ed-skeleton-subject">
                      <div className="ed-skeleton-content">
                        <div className="ed-skeleton-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                        </div>
                        <div className="ed-skeleton-lines">
                          <div className="ed-skeleton-line" style={{width: '75%', animationDelay: '0s'}}></div>
                          <div className="ed-skeleton-line short" style={{width: '45%', animationDelay: '0.15s'}}></div>
                        </div>
                      </div>
                      <div className="ed-skeleton-status">
                        <div className="ed-skeleton-dot-pulse">
                          <span></span><span></span><span></span>
                        </div>
                        <span className="ed-skeleton-label">Crafting subject lines</span>
                      </div>
                      <div className="ed-skeleton-shimmer"></div>
                    </div>
                  )}
                  <div className="ed-add-row">
                    <button
                      type="button"
                      className="ed-add-btn"
                      onClick={masterAddSubjectLine}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      <span>Add Variant</span>
                    </button>
                    <button
                      type="button"
                      className={`ed-ai-gen-btn ${aiGenerating?.type === 'subject' ? 'generating' : ''}`}
                      onClick={() => setAiGenPopup({ type: 'subject', mode: 'generate' })}
                      disabled={!!aiGenerating}
                    >
                      {aiGenerating?.type === 'subject' ? (
                        <>
                          <div className="ed-ai-gen-spinner"></div>
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                          <span>AI Subject Lines</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
                </div>
              </div>

              {/* Email Texts Section */}
              <div className="ed-section">
                <div className="ed-section-header">
                  <div className="ed-section-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                  </div>
                  <h3>{aiLabels.emailTexts}</h3>
                  <span className="ed-section-count">{masterEditData.topicSpecificNotes.length} {masterEditData.topicSpecificNotes.length !== 1 ? aiLabels.instructions2 : aiLabels.instruction}</span>
                </div>
                <div className="ed-instructions">
                  {masterEditData.topicSpecificNotes.map((instruction, index) => {
                    // Hide blank starter cards while AI writes a variant. Filled
                    // cards stay so already-saved templates remain visible.
                    if (aiGenerating?.type === 'instruction' && !(instruction.text || '').trim()) {
                      return null;
                    }
                    return (
                    <div key={instruction.id} className={`ed-instruction-card ${validationErrors.instructions.includes(index) ? 'ed-validation-error' : ''}`}>
                      <div className="ed-instruction-header">
                        <div className="ed-instruction-number">
                          <span>Variant {index + 1}</span>
                        </div>
                        {(() => {
                          const wc = (instruction.text || '').replace(/\{\{[^{}]+\}\}/g, '').replace(/\{[^{}]+\}/g, '').trim().split(/\s+/).filter(Boolean).length;
                          const wcClass = wc < 100 ? 'green' : wc < 300 ? 'blue' : 'red';
                          return <span className={`ed-word-count ${wcClass}`}>{wc}w</span>;
                        })()}
                        <div className="ed-instruction-stats">
                          <InstructionStats instructionId={instruction.id} stats={masterEditTopic.stats} />
                        </div>
                        <div className={`ed-pacing-selector ${expandedPacingIndex === index ? 'expanded' : ''}`}>
                          <button
                            type="button"
                            className={`ed-pacing-toggle ${(instruction.pacing === 'neutral' ? 'auto' : instruction.pacing) || ''}`}
                            onClick={() => setExpandedPacingIndex(expandedPacingIndex === index ? null : index)}
                            title="Set pacing"
                          >
                            {instruction.pacing ? (
                              <>
                                <svg className="ed-pacing-check" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                {(() => { const p = instruction.pacing === 'neutral' ? 'auto' : instruction.pacing; return p.charAt(0).toUpperCase() + p.slice(1); })()}
                              </>
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                            )}
                          </button>
                          {expandedPacingIndex === index && (
                            <div className="ed-pacing-pills">
                              {['formal', 'auto', 'casual'].map(p => {
                                const active = instruction.pacing === p || (p === 'auto' && instruction.pacing === 'neutral');
                                return (
                                <button
                                  key={p}
                                  type="button"
                                  className={`ed-pacing-pill ${p} ${active ? 'active' : ''}`}
                                  onClick={() => {
                                    masterUpdateInstructionPacing(index, p);
                                    setExpandedPacingIndex(null);
                                  }}
                                  title={`${p.charAt(0).toUpperCase() + p.slice(1)} tone`}
                                >
                                  {p.charAt(0).toUpperCase() + p.slice(1)}
                                </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="ed-card-actions">
                          {instruction.text?.trim() && (
                            <>
                              <button
                                type="button"
                                className="ed-rewrite-btn ed-forge-btn"
                                onClick={() => setVariantForge({ index, text: instruction.text })}
                                disabled={!!aiGenerating}
                                title="Derive similar variants from this one"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                                <span>Similar</span>
                              </button>
                              <button
                                type="button"
                                className="ed-rewrite-btn"
                                onClick={() => setAiGenPopup({ type: 'instruction', mode: 'rewrite', rewriteIndex: index, rewriteText: instruction.text })}
                                disabled={!!aiGenerating}
                                title="Rewrite with AI"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                                <span>Rewrite</span>
                              </button>
                            </>
                          )}
                          {masterEditData.topicSpecificNotes.length > 1 && (
                            <button
                              type="button"
                              className="ed-remove-btn"
                              onClick={() => masterRemoveInstruction(index)}
                              title="Remove instruction"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="ed-textarea-wrap">
                        <div
                          className="ed-textarea-backdrop"
                          ref={el => { backdropRefs.current[index] = el; }}
                          aria-hidden="true"
                        >
                          <div className="ed-textarea-backdrop-content">
                            {renderHighlightedText(instruction.text)}
                          </div>
                        </div>
                        <textarea
                          ref={el => { instructionRefs.current[index] = el; }}
                          value={instruction.text}
                          onChange={(e) => {
                            masterUpdateInstruction(index, e.target.value);
                            if (validationErrors.instructions.includes(index)) {
                              setValidationErrors(prev => ({
                                ...prev,
                                instructions: prev.instructions.filter(i => i !== index)
                              }));
                            }
                          }}
                          onKeyDown={(e) => handleFormattingKeyDown(e, (v) => masterUpdateInstruction(index, v))}
                          onScroll={syncScroll(index)}
                          placeholder="Write your email instruction or text here..."
                          className={`ed-instruction-textarea ${validationErrors.instructions.includes(index) ? 'ed-validation-error' : ''}`}
                          rows={6}
                        />
                      </div>
                      <div className="ed-var-toolbar">
                        <div className="ed-var-toolbar-header">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>
                          <span>Insert Dynamic Variable</span>
                          <button
                            type="button"
                            className="ed-var-info-btn"
                            onClick={() => setVarRefOpen(true)}
                            title="Variable reference"
                          >
                            <i className="fa fa-info-circle"></i>
                          </button>
                        </div>
                        <div className="ed-var-chips">
                          {(showAllVars ? [...pinnedInsertVars, ...extraInsertVars] : pinnedInsertVars).map(kw => (
                            <button
                              key={kw.key}
                              type="button"
                              className="ed-var-chip"
                              onClick={() => insertVariable(kw.key, index)}
                              title={`${kw.key} — ${kw.description}`}
                            >
                              <i className={`fa ${kw.icon}`}></i>
                              <span>{kw.label}</span>
                            </button>
                          ))}
                          {extraInsertVars.length > 0 && (
                            <button
                              type="button"
                              className="ed-var-chip ed-var-more"
                              onClick={() => setShowAllVars(open => !open)}
                              title={showAllVars ? 'Hide extra variables' : 'Show more variables'}
                              aria-expanded={showAllVars}
                            >
                              <i className={`fa ${showAllVars ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden></i>
                              <span>{showAllVars ? 'Show less' : `+${extraInsertVars.length} more`}</span>
                            </button>
                          )}
                        </div>
                        <div className="ed-var-toolbar-header ed-var-toolbar-header--snippet">
                          <i className="fa fa-wand-magic-sparkles" aria-hidden></i>
                          <span>Insert AI-Prompt</span>
                        </div>
                        <div className="ed-var-chips">
                          {DYNAMIC_SNIPPETS.map(snippet => (
                            <button
                              key={snippet.id}
                              type="button"
                              className="ed-var-chip ed-var-chip--snippet"
                              onClick={() => insertVariable(snippetInsertText(snippet), index)}
                              title={`${snippetInsertText(snippet)} — ${snippet.description}`}
                            >
                              <i className={`fa ${snippet.icon}`}></i>
                              <span>{snippet.label}</span>
                            </button>
                          ))}
                        </div>
                        {imageLabAssets.length > 0 && (
                          <>
                            <div className="ed-var-toolbar-header ed-var-toolbar-header--image">
                              <i className="fa fa-image" aria-hidden></i>
                              <span>Insert Image</span>
                            </div>
                            <div className="ed-var-chips">
                              {imageLabAssets.map(asset => (
                                <button
                                  key={asset.token}
                                  type="button"
                                  className="ed-var-chip ed-var-chip--image"
                                  onClick={() => insertVariable(`{{image:${asset.token}}}`, index)}
                                  title={`{{image:${asset.token}}} — ${asset.name || asset.token}`}
                                >
                                  <i className="fa fa-image"></i>
                                  <span>{asset.name || asset.token}</span>
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    );
                  })}
                  {aiGenerating?.type === 'instruction' && (
                    <div className="ed-skeleton-card ed-skeleton-instruction">
                      <div className="ed-skeleton-content">
                        <div className="ed-skeleton-icon">
                          {aiGenerating.step === 2 ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                          ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                          )}
                        </div>
                        <div className="ed-skeleton-lines">
                          <div className="ed-skeleton-line" style={{width: '90%', animationDelay: '0s'}}></div>
                          <div className="ed-skeleton-line" style={{width: '80%', animationDelay: '0.1s'}}></div>
                          <div className="ed-skeleton-line" style={{width: '65%', animationDelay: '0.2s'}}></div>
                          <div className="ed-skeleton-line short" style={{width: '40%', animationDelay: '0.3s'}}></div>
                        </div>
                      </div>
                      <div className="ed-skeleton-status">
                        <div className="ed-skeleton-dot-pulse">
                          <span></span><span></span><span></span>
                        </div>
                        <span className="ed-skeleton-label">
                          {aiGenerating.mode === 'forge'
                            ? 'Deriving a similar variant'
                            : (aiGenerating.step === 2 ? 'Refining template syntax' : 'Writing email template')}
                          {aiGenerating.variantTarget > 1
                            && ` — variant ${aiGenerating.variant}/${aiGenerating.variantTarget}`}
                        </span>
                        <span className="ed-skeleton-step-badge">
                          Step {aiGenerating.step}/{aiGenerating.steps || 2}
                        </span>
                      </div>
                      <div className="ed-skeleton-shimmer"></div>
                    </div>
                  )}

                  {/* Result of the last run. Lives here rather than in the popup
                      because the popup closes the moment generation starts. */}
                  {aiGenResult && !aiGenerating && (
                    <div className={`ed-gen-result ${aiGenResult.issues.some(i => i.level === 'error') ? 'has-error' : ''}`}>
                      <div className="ed-gen-result-head">
                        <span className="ed-gen-result-summary">
                          <i className={`fa ${aiGenResult.kind === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'}`} aria-hidden></i>
                          {aiGenResult.kind === 'error'
                            ? aiLabels.genFailed
                            : `${aiGenResult.added} ${aiGenResult.added === 1 ? aiLabels.variant : aiLabels.variants2}`}
                          {aiGenResult.instructionCount != null && aiGenResult.kind !== 'error'
                            && ` · ${aiGenResult.instructionCount} ${aiLabels.aiInstructions}`}
                        </span>
                        <span className="ed-gen-result-right">
                          {aiGenResult.credits > 0 && (
                            <span className="ed-gen-result-credits">
                              <i className="fa fa-coins" aria-hidden></i> {aiGenResult.credits} {aiLabels.credits}
                            </span>
                          )}
                          <button
                            type="button"
                            className="ed-gen-result-close"
                            onClick={() => setAiGenResult(null)}
                            aria-label="Dismiss"
                          >
                            <i className="fa fa-times" aria-hidden></i>
                          </button>
                        </span>
                      </div>
                      {aiGenResult.issues.map((issue, i) => (
                        <div key={`${issue.code}-${i}`} className={`ed-gen-issue is-${issue.level}`}>
                          <i className={`fa ${issue.level === 'error' ? 'fa-times-circle' : 'fa-exclamation-triangle'}`} aria-hidden></i>
                          <span>
                            {issue.detail}
                            {issue.fix && <em className="ed-gen-issue-fix">{issue.fix}</em>}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="ed-add-row">
                    <button
                      type="button"
                      className="ed-add-btn"
                      onClick={masterAddInstruction}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      <span>Add Variant</span>
                    </button>
                    <button
                      type="button"
                      className={`ed-ai-gen-btn ${aiGenerating?.type === 'instruction' ? 'generating' : ''}`}
                      onClick={() => setAiGenPopup({ type: 'instruction', mode: 'generate' })}
                      disabled={!!aiGenerating}
                    >
                      {aiGenerating?.type === 'instruction' ? (
                        <>
                          <div className="ed-ai-gen-spinner"></div>
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                          <span>AI Email Text</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== AI ASSIST PANEL ===== */}
            {aiAssistOpen && (
              <div className="ed-ai-panel">
                <div className="ed-ai-panel-header">
                  <div className="ed-ai-panel-title">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2a4 4 0 0 1 4 4v1a3 3 0 0 1 3 3v1a2 2 0 0 1-2 2h-1v2a4 4 0 0 1-8 0v-2H7a2 2 0 0 1-2-2v-1a3 3 0 0 1 3-3V6a4 4 0 0 1 4-4z"/>
                      <circle cx="9" cy="10" r="1"/>
                      <circle cx="15" cy="10" r="1"/>
                      <path d="M9.5 15a3.5 3.5 0 0 0 5 0"/>
                    </svg>
                    <h3>AI Assistant</h3>
                  </div>
                  <button className="ed-ai-panel-close" onClick={() => setAiAssistOpen(false)}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
                <div className="ed-ai-panel-body">
                  <div className="ed-ai-suggestions">
                    <p className="ed-ai-intro">Quick actions for this template:</p>
                    <div className="ed-ai-quick-actions">
                      <button 
                        className={`ed-ai-chip ${aiGenerating?.type === 'subject' ? 'ed-ai-chip-active' : ''}`}
                        onClick={() => setAiGenPopup({ type: 'subject', mode: 'generate' })}
                        disabled={!!aiGenerating}
                      >
                        {aiGenerating?.type === 'subject' ? (
                          <div className="ed-ai-gen-spinner" style={{width: 14, height: 14, borderWidth: 1.5}}></div>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                        )}
                        {aiGenerating?.type === 'subject' ? 'Generating...' : 'Generate Subject Lines'}
                      </button>
                      <button 
                        className={`ed-ai-chip ${aiGenerating?.type === 'instruction' ? 'ed-ai-chip-active' : ''}`}
                        onClick={() => setAiGenPopup({ type: 'instruction', mode: 'generate' })}
                        disabled={!!aiGenerating}
                      >
                        {aiGenerating?.type === 'instruction' ? (
                          <div className="ed-ai-gen-spinner" style={{width: 14, height: 14, borderWidth: 1.5}}></div>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        )}
                        {aiGenerating?.type === 'instruction' ? 'Generating...' : 'Generate Email Text'}
                      </button>
                    </div>
                    
                    {/* Inline AI Context Summary */}
                    <div className="ed-ai-context-summary">
                      <div className="ed-ai-context-summary-header">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                        <span>AI uses your sidebar context + per-generation settings</span>
                      </div>
                      {(aiContext.useAccountBrain && brainHasCopyNotes) || aiContext.businessDescription || aiContext.purpose || aiContext.targetAudience || aiContext.keyPoints ? (
                        <div className="ed-ai-context-tags">
                          {aiContext.useAccountBrain && brainHasCopyNotes && (
                            <span className="ed-ai-context-tag ed-ai-context-tag--brain">
                              <i className="fa fa-brain"></i> {aiLabels.accountBrain || 'Account Brain'} · {vaultCopySummary.count}
                            </span>
                          )}
                          {aiContext.businessDescription && <span className="ed-ai-context-tag"><i className="fa fa-store"></i> {aiContext.businessDescription}</span>}
                          {aiContext.purpose && <span className="ed-ai-context-tag"><i className="fa fa-crosshairs"></i> {aiContext.purpose}</span>}
                          {aiContext.targetAudience && <span className="ed-ai-context-tag"><i className="fa fa-users"></i> {aiContext.targetAudience}</span>}
                          {aiContext.keyPoints && <span className="ed-ai-context-tag"><i className="fa fa-key"></i> {aiContext.keyPoints}</span>}
                        </div>
                      ) : (
                        <p className="ed-ai-context-hint">Fill in the AI Context card on the left sidebar for better results.</p>
                      )}
                      <div className="ed-ai-context-tags">
                        <span className="ed-ai-context-tag"><i className="fa fa-globe"></i> {aiContext.language}</span>
                      </div>
                      <p className="ed-ai-context-hint" style={{marginTop: '6px'}}>Tone, style, creativity & more are configured per generation in the popup.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ===== VARIANT FORGE — derive siblings from one variant ===== */}
        {variantForge && (
          <VariantForgeModal
            sourceText={variantForge.text}
            sourceNumber={variantForge.index + 1}
            language={aiContext.language === 'German' ? 'German' : 'English'}
            creditsPerVariant={costOf('templateWriter')}
            onGenerate={handleVariantForge}
            onClose={() => setVariantForge(null)}
          />
        )}

        {/* ===== AI GENERATION POPUP ===== */}
        {aiGenPopup && createPortal(
          <div className="ed-gen-popup-overlay bcScrim" onClick={() => setAiGenPopup(null)}>
            <div className={`ed-gen-popup ${aiGenPopup.mode !== 'rewrite' && aiGenPopup.type !== 'subject' ? 'two-panel' : ''}`} onClick={(e) => e.stopPropagation()}>
              <div className="ed-gen-popup-header">
                <div className="ed-gen-popup-title">
                  <h3>{aiGenPopup.mode === 'rewrite' 
                    ? (aiGenPopup.type === 'subject' ? `${aiLabels.rewrite} Subject` : `${aiLabels.rewrite} Email`)
                    : (aiGenPopup.type === 'subject' ? aiLabels.generateSubjects : aiLabels.generateEmail)
                  }</h3>
                </div>
                <button className="ed-gen-popup-close" onClick={() => setAiGenPopup(null)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>

              {/* Rewrite: show original text prominently at top */}
              {aiGenPopup.mode === 'rewrite' && aiGenPopup.rewriteText && (
                <div className="ed-gen-popup-rewrite-preview top">
                  <div className="ed-gen-popup-rewrite-label">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    <span>{aiLabels.originalRewrite}</span>
                  </div>
                  <p className="ed-gen-popup-rewrite-text">{aiGenPopup.rewriteText}</p>
                </div>
              )}

              <div className="ed-gen-popup-content">
                {/* ══════ LEFT PANEL — Personalities (email text only) ══════ */}
                {aiGenPopup.mode !== 'rewrite' && aiGenPopup.type !== 'subject' && (
                  <div className="ed-gen-popup-left">
                    <div className="ed-gen-panel-title">
                      <i className="fa fa-layer-group"></i> {aiLabels.chooseFramework || 'How the mail is built'}
                    </div>
                    <p className="ed-fw-pick-hint">{aiLabels.chooseFrameworkHint}</p>
                    {[
                      { tier: 'play' },
                      { tier: 'classic', label: aiLabels.fwGroupClassics, hint: aiLabels.fwGroupClassicsHint },
                    ].map((group) => (
                      <div className="ed-fw-group" key={group.tier}>
                        {group.label && (
                          <div className="ed-fw-group-head">
                            <span className="ed-fw-group-label">{group.label}</span>
                            {group.hint && <span className="ed-fw-group-hint">{group.hint}</span>}
                          </div>
                        )}
                        <div className="ed-fw-pick-list">
                          {emailFrameworks
                            .filter((raw) => (raw.tier || 'classic') === group.tier)
                            .map((raw) => {
                              const fw = localizeFramework(raw, aiContext.language);
                              const fwActive = (aiContext.selectedFrameworks || [])[0] === fw.id;
                              const slotValues = (aiContext.frameworkSlots || {})[fw.id] || {};
                              const fixedCount = (fw.slots || []).filter(s => (slotValues[s.key] || '').trim()).length;
                              return (
                                <div className="ed-fw-pick-wrap" key={fw.id}>
                                  <button
                                    type="button"
                                    className={`ed-fw-pick-card ${fwActive ? 'is-on' : ''} ${fwActive && (fw.slots || []).length > 0 ? 'has-slots' : ''}`}
                                    onClick={() => {
                                      setSkeletonOpen(false);
                                      setAiContext(prev => ({
                                        ...prev,
                                        personality: '',
                                        selectedFrameworks: fwActive ? [] : [fw.id],
                                      }));
                                    }}
                                  >
                                    <div className="ed-fw-pick-top">
                                      <div className="ed-fw-pick-copy">
                                        <span className="ed-fw-pick-name">{fw.name}</span>
                                        <span className="ed-fw-pick-why">{fw.why}</span>
                                      </div>
                                      <span className={`ed-fw-pick-radio ${fwActive ? 'is-on' : ''}`} aria-hidden>
                                        {fwActive && <span className="ed-fw-pick-radio-dot" />}
                                      </span>
                                    </div>
                                    <div className="ed-fw-pick-stack">
                                      {fw.steps.map((step, i) => (
                                        <span
                                          key={`${fw.id}-${i}`}
                                          className="ed-fw-pick-block"
                                          style={{ flexGrow: step.weight || 2, flexBasis: 0 }}
                                        >
                                          <span className="ed-fw-pick-block-n">{i + 1}</span>
                                          <span className="ed-fw-pick-block-label">{step.label}</span>
                                        </span>
                                      ))}
                                    </div>
                                  </button>

                                  {fwActive && (fw.slots || []).length > 0 && (
                                    <div className="ed-fw-slots">
                                      <div className="ed-fw-slots-head">
                                        <span className="ed-fw-slots-title">
                                          <i className="fa fa-sliders-h" aria-hidden></i> {aiLabels.fwSlotsTitle}
                                        </span>
                                        <button
                                          type="button"
                                          className="ed-fw-slots-suggest"
                                          onClick={handleSuggestFrameworkSlots}
                                          disabled={slotSuggesting}
                                        >
                                          {slotSuggesting
                                            ? <span className="ed-fw-slots-spinner" aria-hidden />
                                            : <i className="fa fa-magic" aria-hidden></i>}
                                          {slotSuggesting ? aiLabels.fwSlotsSuggesting : aiLabels.fwSlotsSuggest}
                                        </button>
                                      </div>
                                      <p className="ed-fw-slots-hint">{aiLabels.fwSlotsHint}</p>
                                      <div className="ed-fw-slot-grid">
                                        {fw.slots.map((slot) => {
                                          const value = slotValues[slot.key] || '';
                                          const isFixed = !!value.trim();
                                          return (
                                            <div className={`ed-fw-slot ${isFixed ? 'is-fixed' : ''}`} key={slot.key}>
                                              <label className="ed-fw-slot-label" htmlFor={`fwslot-${fw.id}-${slot.key}`}>
                                                <i className={`fa ${slot.icon}`} aria-hidden></i>
                                                <span className="ed-fw-slot-name">{slot.label}</span>
                                                <span className={`ed-fw-slot-state ${isFixed ? 'is-fixed' : ''}`}>
                                                  {isFixed ? aiLabels.fwSlotFixed : aiLabels.fwSlotAuto}
                                                </span>
                                              </label>
                                              <input
                                                id={`fwslot-${fw.id}-${slot.key}`}
                                                type="text"
                                                className="ed-fw-slot-input"
                                                value={value}
                                                placeholder={slot.placeholder}
                                                onChange={(e) => setFrameworkSlot(fw.id, slot.key, e.target.value)}
                                              />
                                            </div>
                                          );
                                        })}
                                      </div>
                                      <div className="ed-fw-slots-foot">
                                        <button
                                          type="button"
                                          className="ed-fw-slots-link"
                                          onClick={() => setSkeletonOpen(prev => !prev)}
                                        >
                                          <i className={`fa ${skeletonOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden></i>
                                          {aiLabels.fwSkeleton}
                                        </button>
                                        <span className="ed-fw-slots-count">
                                          {fixedCount} {aiLabels.fwSlotFixed} · {(fw.slots.length - fixedCount)} {aiLabels.fwSlotAuto}
                                        </span>
                                        {fixedCount > 0 && (
                                          <button
                                            type="button"
                                            className="ed-fw-slots-link"
                                            onClick={() => clearFrameworkSlots(fw.id)}
                                          >
                                            <i className="fa fa-eraser" aria-hidden></i> {aiLabels.fwSlotsReset}
                                          </button>
                                        )}
                                      </div>
                                      {skeletonOpen && activeSkeleton && (
                                        <div className="ed-fw-skeleton">
                                          {activeSkeleton.segments.map((seg, i) => {
                                            if (seg.type === 'text') return <span key={i}>{seg.value}</span>;
                                            if (seg.type === 'filled') {
                                              return <span key={i} className="ed-fw-skeleton-fixed">{seg.value}</span>;
                                            }
                                            return <span key={i} className="ed-fw-skeleton-auto">{`{${seg.label}}`}</span>;
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ══════ RIGHT PANEL — Generation Settings ══════ */}
                <div className="ed-gen-popup-right">
                  {/* ── Recent presets: restore a previous run's full setup ── */}
                  {genPresets.length > 0 && aiGenPopup.mode !== 'rewrite' && (
                    <div className="ed-ai-field">
                      <label className="ed-ai-field-label">
                        <i className="fa fa-history"></i> {aiLabels.recentSetups}
                      </label>
                      <div className="ed-gen-preset-row">
                        {genPresets.map((preset) => (
                          <button
                            key={preset.id}
                            type="button"
                            className="ed-gen-preset"
                            onClick={() => applyGenerationPreset(preset)}
                            title={`${preset.templateName || ''} · ${new Date(preset.savedAt).toLocaleString()}`}
                          >
                            <span className="ed-gen-preset-label">{preset.label}</span>
                            <span className="ed-gen-preset-meta">
                              {new Date(preset.savedAt).toLocaleDateString()} · {preset.context?.language}
                              {preset.context?.tone ? ` · ${preset.context.tone}` : ''}
                            </span>
                          </button>
                        ))}
                      </div>
                      <p className="ed-ai-context-hint">{aiLabels.recentSetupsHint}</p>
                    </div>
                  )}

                  {/* ── User Instructions ── */}
                  <div className="ed-ai-field">
                    <label className="ed-ai-field-label"><i className="fa fa-pen"></i> {aiLabels.userNote}</label>
                    <textarea
                      className="ed-ai-user-note"
                      value={aiContext.userNote}
                      onChange={(e) => setAiContext(prev => ({ ...prev, userNote: e.target.value }))}
                      placeholder={aiLabels.userNotePlaceholder}
                      rows={2}
                    />
                  </div>

                  {/* ── Tone ── */}
                  <div className="ed-ai-field">
                    <label className="ed-ai-field-label"><i className="fa fa-comment-alt"></i> {aiLabels.tone}</label>
                    <div className="ed-ai-tone-segmented">
                      {[
                        { value: 'formal', icon: 'fa-user-tie', label: aiLabels.toneFormal },
                        { value: 'neutral', icon: 'fa-handshake', label: aiLabels.toneNeutral },
                        { value: 'casual', icon: 'fa-coffee', label: aiLabels.toneCasual }
                      ].map(t => (
                        <button
                          key={t.value}
                          className={`ed-ai-tone-seg-btn ${aiContext.tone === t.value ? 'active' : ''}`}
                          onClick={() => setAiContext(prev => ({ ...prev, tone: t.value }))}
                          type="button"
                        >
                          <i className={`fa ${t.icon}`}></i>
                          <span>{t.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* ── Length ──
                       Relative to the framework, not absolute. Each option
                       shows the real sentence count for the play that is
                       currently selected, and the beat breakdown underneath
                       makes clear where those sentences go. ── */}
                  {aiGenPopup.type !== 'subject' && aiGenPopup.mode !== 'rewrite' && (() => {
                    const lengthFw = getFrameworkById((aiContext.selectedFrameworks || [])[0]);
                    const scale = normalizeLengthScale(aiContext.length);
                    const plan = frameworkLengthPlan(lengthFw, scale, aiContext.language);
                    const unit = aiLabels.sentences;
                    const fwName = lengthFw
                      ? (lengthFw.name[aiContext.language] || lengthFw.name.English)
                      : null;
                    return (
                      <div className="ed-ai-field">
                        <div className="ed-ai-slider-header">
                          <label className="ed-ai-field-label"><i className="fa fa-text-width"></i> {aiLabels.length}</label>
                          {fwName && <span className="ed-ai-len-source">{aiLabels.lengthFromFramework}</span>}
                        </div>
                        <div className="ed-ai-segmented ed-ai-segmented-3">
                          {[
                            { value: 'tight', label: aiLabels.lenTight },
                            { value: 'natural', label: aiLabels.lenNatural },
                            { value: 'roomy', label: aiLabels.lenRoomy },
                          ].map(val => {
                            const p = frameworkLengthPlan(lengthFw, val.value, aiContext.language);
                            const range = p.min === p.max ? `${p.min}` : `${p.min}–${p.max}`;
                            return (
                              <button
                                key={val.value}
                                className={`ed-ai-len-btn ${scale === val.value ? 'active' : ''}`}
                                onClick={() => setAiContext(prev => ({ ...prev, length: val.value }))}
                                type="button"
                              >
                                <span className="ed-ai-len-name">{val.label}</span>
                                <span className="ed-ai-len-hint">{range} {unit}</span>
                              </button>
                            );
                          })}
                        </div>
                        {plan.steps.length > 0 && (
                          <div className="ed-ai-len-beats">
                            {plan.steps.map((s, i) => (
                              <span key={i} className="ed-ai-len-beat">
                                <span className="ed-ai-len-beat-name">{s.label}</span>
                                <span className="ed-ai-len-beat-count">{s.sentences}</span>
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="ed-ai-field-hint">
                          {lengthFw ? aiLabels.lengthBeatsHint : aiLabels.lengthFreeHint}
                        </p>
                      </div>
                    );
                  })()}

                  <div className="ed-gen-popup-divider"></div>

                  {/* ── Creativity / Deviation ── */}
                  <div className="ed-ai-field">
                    <label className="ed-ai-field-label">
                      <i className={`fa ${aiGenPopup.mode === 'rewrite' ? 'fa-sliders-h' : 'fa-magic'}`}></i> {aiLabels.creativity}
                    </label>
                    <div className="ed-ai-creativity-stack">
                      {[
                        {
                          value: 'close',
                          icon: 'fa-copy',
                          label: aiGenPopup.mode === 'rewrite' ? aiLabels.rewriteClose : aiLabels.creativityClose,
                          desc: aiGenPopup.mode === 'rewrite' ? aiLabels.rewriteCloseDesc : aiLabels.creativityCloseDesc
                        },
                        {
                          value: 'balanced',
                          icon: 'fa-balance-scale',
                          label: aiGenPopup.mode === 'rewrite' ? aiLabels.rewriteBalanced : aiLabels.creativityBalanced,
                          desc: aiGenPopup.mode === 'rewrite' ? aiLabels.rewriteBalancedDesc : aiLabels.creativityBalancedDesc
                        },
                        {
                          value: 'creative',
                          icon: 'fa-lightbulb',
                          label: aiGenPopup.mode === 'rewrite' ? aiLabels.rewriteCreative : aiLabels.creativityCreative,
                          desc: aiGenPopup.mode === 'rewrite' ? aiLabels.rewriteCreativeDesc : aiLabels.creativityCreativeDesc
                        }
                      ].map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          className={`ed-ai-creativity-option ${aiContext.creativity === opt.value ? 'active' : ''}`}
                          onClick={() => setAiContext(prev => ({ ...prev, creativity: opt.value }))}
                        >
                          <i className={`fa ${opt.icon} ed-ai-creativity-icon`}></i>
                          <div className="ed-ai-creativity-text">
                            <span className="ed-ai-creativity-label">{opt.label}</span>
                            <span className="ed-ai-creativity-desc">{opt.desc}</span>
                          </div>
                          {aiContext.creativity === opt.value && <i className="fa fa-check ed-ai-creativity-check"></i>}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* ── Variants. Subject lines come back as several lines from
                       one call, so five are cheap. Email text costs a full
                       writer + polish round each, hence the lower ceiling and
                       the running credit total. ── */}
                  {aiGenPopup.mode === 'generate' && (() => {
                    const isText = aiGenPopup.type === 'instruction';
                    const maxVariants = isText ? MAX_TEXT_VARIANTS : 5;
                    const current = isText
                      ? clampTextVariants(aiContext.variantCount)
                      : Math.min(Math.max(aiContext.variantCount || 2, 1), 5);
                    const estimate = isText
                      ? Math.round(textVariantCredits() * current * 100) / 100
                      : costOf('templateSubject');
                    return (
                      <div className="ed-ai-field">
                        <label className="ed-ai-field-label"><i className="fa fa-layer-group"></i> {aiLabels.variants}</label>
                        <div className="ed-ai-tone-segmented">
                          {Array.from({ length: maxVariants }, (_, i) => i + 1).map((n) => (
                            <button
                              key={n}
                              type="button"
                              className={`ed-ai-tone-seg-btn ${current === n ? 'active' : ''}`}
                              onClick={() => setAiContext((prev) => ({ ...prev, variantCount: n }))}
                            >
                              <span>{n}</span>
                            </button>
                          ))}
                        </div>
                        <p className="ed-ai-cost-hint">
                          <i className="fa fa-coins" aria-hidden></i>
                          {` ${aiLabels.costEstimate}: ~${estimate} ${aiLabels.credits}`}
                          {isText && current > 1 && ` (${textVariantCredits()} × ${current})`}
                        </p>
                      </div>
                    );
                  })()}

                  {/* ── Emoji Toggle ── */}
                  {aiGenPopup.mode !== 'rewrite' && (
                    <div className="ed-ai-toggle-row">
                      <div className="ed-ai-toggle-item" onClick={() => setAiContext(prev => ({ ...prev, useEmojis: !prev.useEmojis }))}>
                        <div className={`ed-ai-toggle-switch ${aiContext.useEmojis ? 'on' : ''}`}>
                          <div className="ed-ai-toggle-knob"></div>
                        </div>
                        <span className="ed-ai-toggle-label"><i className="fa fa-smile"></i> {aiLabels.useEmojis}</span>
                      </div>
                    </div>
                  )}

                  {aiGenPopup.mode !== 'rewrite' && <div className="ed-gen-popup-divider"></div>}

                  {/* ── Dynamic Variables ── */}
                  {aiGenPopup.mode !== 'rewrite' && (
                    <div className="ed-ai-field">
                      <label className="ed-ai-field-label"><i className="fa fa-code"></i> {aiLabels.dynamicVars}</label>
                      <p className="ed-ai-field-hint">{aiLabels.dynamicVarsHint}</p>
                      <div className="ed-ai-keyword-grid">
                        {dynamicKeywords.map(kw => {
                          const isActive = aiContext.activeKeywords.includes(kw.key);
                          // Subject lines resolve the full catalogue; the body
                          // does not. Say so on the chip instead of letting the
                          // variable quietly vanish from the sent email.
                          const bare = kw.key.replace(/[{}]/g, '');
                          const bodyNative = SEND_TIME_BODY_VARIABLES.includes(bare) || kw.isCustomField;
                          const bodyAlias = BODY_VARIABLE_ALIASES[bare];
                          const bodyState = aiGenPopup.type !== 'instruction' || bodyNative
                            ? null
                            : (bodyAlias ? 'mapped' : 'unsupported');
                          return (
                            <button
                              key={kw.key}
                              className={`ed-ai-keyword-chip ${isActive ? 'active' : ''} ${bodyState ? `body-${bodyState}` : ''}`}
                              onClick={() => setAiContext(prev => ({
                                ...prev,
                                activeKeywords: isActive
                                  ? prev.activeKeywords.filter(k => k !== kw.key)
                                  : [...prev.activeKeywords, kw.key]
                              }))}
                              type="button"
                              title={
                                bodyState === 'unsupported'
                                  ? `${kw.key} — ${aiLabels.varNotInBody}`
                                  : bodyState === 'mapped'
                                    ? `${kw.key} → {{${bodyAlias}}} — ${aiLabels.varMappedInBody}`
                                    : `${kw.key} — ${kw.description}`
                              }
                            >
                              <i className={`fa ${kw.icon} ed-ai-kw-icon`}></i>
                              <div className="ed-ai-kw-text">
                                <span className="ed-ai-kw-label">{kw.label}</span>
                                <span className="ed-ai-kw-code">
                                  {bodyState === 'mapped' ? `{{${bodyAlias}}}` : kw.key}
                                </span>
                              </div>
                              {bodyState === 'unsupported' && (
                                <i className="fa fa-ban ed-ai-kw-warn" aria-hidden></i>
                              )}
                              {isActive && bodyState !== 'unsupported' && <i className="fa fa-check ed-ai-kw-check"></i>}
                            </button>
                          );
                        })}
                      </div>
                      {aiGenPopup.type === 'instruction' && (
                        <p className="ed-ai-field-hint">{aiLabels.varBodyHint}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="ed-gen-popup-footer">
                <button className="ed-gen-popup-cancel" onClick={() => setAiGenPopup(null)} type="button">
                  {aiLabels.cancel}
                </button>
                <button 
                  className={`ed-gen-popup-generate ${aiGenPopup.mode === 'rewrite' ? 'rewrite' : ''}`}
                  onClick={() => { handleAiGenerate(aiGenPopup); setAiGenPopup(null); }}
                  type="button"
                >
                  {aiGenPopup.mode === 'rewrite' ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      <span>{aiLabels.rewrite}</span>
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                      <span>{aiGenPopup.type === 'subject' ? aiLabels.generateSubjects : aiLabels.generateEmail}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* ===== VARIABLE REFERENCE POPUP ===== */}
        {varRefOpen && createPortal(
          <div className="ed-var-ref-overlay bcScrim" onClick={() => setVarRefOpen(false)}>
            <div className="ed-var-ref-popup" onClick={(e) => e.stopPropagation()}>
              <div className="ed-var-ref-header">
                <div className="ed-var-ref-title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>
                  <h3>Variable Reference</h3>
                </div>
                <button className="ed-var-ref-close" onClick={() => setVarRefOpen(false)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <p className="ed-var-ref-desc">
                Double-bracket variables pull a field from the lead as-is. Single-bracket snippets insert a ready AI prompt — the full text lands in the editor so you can change it after clicking the chip.
              </p>
              <div className="ed-var-ref-body">
                <div className="ed-var-ref-group">
                  <div className="ed-var-ref-group-title">
                    <i className="fa fa-user"></i>
                    <span>Lead / Contact Fields</span>
                  </div>
                  <div className="ed-var-ref-table">
                    {dynamicKeywords.filter(kw => kw.key.startsWith('{{lead_') || kw.key === '{{business_name}}').map(kw => (
                      <div key={kw.key} className="ed-var-ref-row">
                        <div className="ed-var-ref-icon"><i className={`fa ${kw.icon}`}></i></div>
                        <div className="ed-var-ref-label">{kw.label}</div>
                        <code className="ed-var-ref-key">{kw.key}</code>
                        <div className="ed-var-ref-description">{kw.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="ed-var-ref-group">
                  <div className="ed-var-ref-group-title">
                    <i className="fa fa-user-check"></i>
                    <span>Recipient Fields</span>
                  </div>
                  <div className="ed-var-ref-table">
                    {dynamicKeywords.filter(kw => kw.key.startsWith('{{recipient_')).map(kw => (
                      <div key={kw.key} className="ed-var-ref-row">
                        <div className="ed-var-ref-icon"><i className={`fa ${kw.icon}`}></i></div>
                        <div className="ed-var-ref-label">{kw.label}</div>
                        <code className="ed-var-ref-key">{kw.key}</code>
                        <div className="ed-var-ref-description">{kw.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="ed-var-ref-group">
                  <div className="ed-var-ref-group-title">
                    <i className="fa fa-user-circle"></i>
                    <span>Sender / Your Fields</span>
                  </div>
                  <div className="ed-var-ref-table">
                    {dynamicKeywords.filter(kw => kw.key.startsWith('{{user_') || kw.key.startsWith('{{company_') || kw.key === '{{footer_text}}').map(kw => (
                      <div key={kw.key} className="ed-var-ref-row">
                        <div className="ed-var-ref-icon"><i className={`fa ${kw.icon}`}></i></div>
                        <div className="ed-var-ref-label">{kw.label}</div>
                        <code className="ed-var-ref-key">{kw.key}</code>
                        <div className="ed-var-ref-description">{kw.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
                {dynamicKeywords.some(kw => kw.isCustomField) && (
                  <div className="ed-var-ref-group">
                    <div className="ed-var-ref-group-title">
                      <i className="fa fa-puzzle-piece"></i>
                      <span>Custom Fields</span>
                    </div>
                    <div className="ed-var-ref-table">
                      {dynamicKeywords.filter(kw => kw.isCustomField).map(kw => (
                        <div key={kw.key} className="ed-var-ref-row">
                          <div className="ed-var-ref-icon"><i className={`fa ${kw.icon}`}></i></div>
                          <div className="ed-var-ref-label">{kw.label}</div>
                          <code className="ed-var-ref-key">{kw.key}</code>
                          <div className="ed-var-ref-description">{kw.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="ed-var-ref-group">
                  <div className="ed-var-ref-group-title">
                    <i className="fa fa-wand-magic-sparkles"></i>
                    <span>AI Snippets</span>
                  </div>
                  <div className="ed-var-ref-snippets">
                    {DYNAMIC_SNIPPETS.map(snippet => (
                      <div key={snippet.id} className="ed-var-ref-snippet">
                        <div className="ed-var-ref-snippet-head">
                          <div className="ed-var-ref-icon"><i className={`fa ${snippet.icon}`}></i></div>
                          <div className="ed-var-ref-label">{snippet.label}</div>
                        </div>
                        <code className="ed-var-ref-key ed-var-ref-key--snippet">{snippetInsertText(snippet)}</code>
                        <p className="ed-var-ref-snippet-desc">{snippet.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="ed-var-ref-footer">
                <div className="ed-var-ref-hint">
                  <i className="fa fa-lightbulb"></i>
                  <span>Tip: Click a variable or snippet chip below any email text to insert it at the cursor. Snippets stay editable after insert.</span>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  };

  return (
    <>
      {/* Master Editor Full Screen — dispatch by template mode */}
      {masterEditTopic && (
        isVoiceCloneTemplate(masterEditTopic) ? (
          <VoiceCloneTemplateEditor
            userId={userId}
            templateId={masterEditTopic.id}
            initialData={masterEditTopic}
            isSelected={!!selectedTopics[masterEditTopic.id]}
            onSelectionToggle={(id, data) => handleTopicToggle(id, { ...masterEditTopic, ...data })}
            onClose={closeMasterEditor}
            onDeleted={() => closeMasterEditor()}
          />
        ) : (
          renderMasterEditor()
        )
      )}

      {/* Voice Clone Mode Picker — shown after "+ Add New" click */}
      <ModePickerModal
        open={showModeModal}
        onClose={() => {
          setShowModeModal(false);
          setPendingNewType(null);
        }}
        onPick={handleNewTemplateMode}
        typeLabel={topicTypes.find(t => t.value === pendingNewType)?.label}
      />

      {/* CopyTwin Auto-Setup — pick a CopyTwin & one-click derive the template */}
      <CopyTwinAutoSetupModal
        open={showAutoSetupModal}
        userId={userId}
        typeLabel={topicTypes.find(t => t.value === pendingNewType)?.label}
        onCancel={() => {
          setShowAutoSetupModal(false);
          setPendingNewType(null);
        }}
        onReady={handleCloneTemplateReady}
      />

      {/* Overview Mode */}
      <div className={`topics-container ${masterEditTopic ? 'topics-hidden' : ''}`}>
        <h3>Email Templates Selection</h3>
        
        {/* Modal Overlay */}
        {showModal && (
          <div className="topics-modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowModal(false);
              handleCancelEdit();
            }
          }}>
            <div className="topics-modal">
              <div className="topics-modal-header">
                <h3>{editingTopicId ? 'Edit Template' : 'Create New Template'}</h3>
                <button 
                  className="topics-modal-close"
                  onClick={() => {
                    setShowModal(false);
                    handleCancelEdit();
                  }}
                >
                  ×
                </button>
              </div>
              
              <div className="topics-modal-body">
                <form onSubmit={handleSaveCustomTopic}>
                  <div className="topics-form-group">
                    <label>Template Type:</label>
                    <select
                      value={newTopic.type}
                      onChange={(e) => setNewTopic({...newTopic, type: e.target.value})}
                      className="topics-type-select"
                      disabled={isProcessing}
                    >
                      {topicTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="topics-form-group">
                    <label>Template Name:</label>
                    <input
                      type="text"
                      value={newTopic.topic}
                      onChange={(e) => setNewTopic({...newTopic, topic: e.target.value})}
                      placeholder="e.g. My Custom Template"
                      required
                    />
                  </div>
                  
                  <div className="topics-form-group">
                    <label>Subject Lines (at least one required):</label>
                    <div className="topics-subject-lines-container">
                      {newTopic.subjectLines.map((subjectLine, index) => (
                        <div key={subjectLine.id} className="topics-subject-line-item">
                          <input
                            type="text"
                            value={subjectLine.text}
                            onChange={(e) => updateSubjectLine(index, e.target.value)}
                            placeholder="e.g. BonusClub - Boost Your Online Visibility"
                            required={index === 0}
                          />
                          <span className="topics-item-id" title={`Click to copy: ${subjectLine.id}`} onClick={() => navigator.clipboard.writeText(subjectLine.id)}>ID: {subjectLine.id}</span>
                          {newTopic.subjectLines.length > 1 && (
                            <button
                              type="button"
                              className="topics-remove-subject-line"
                              onClick={() => removeSubjectLine(index)}
                              title="Remove subject line"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        className="topics-add-subject-line"
                        onClick={addSubjectLine}
                      >
                        + Add Subject Line
                      </button>
                    </div>
                  </div>
                  
                  <div className="topics-form-group">
                    <label>Template Instructions (at least one required):</label>
                    <div className="topics-instructions-container">
                      {newTopic.topicSpecificNotes.map((instruction, index) => (
                        <div key={instruction.id} className="topics-instruction-item">
                          <textarea
                            value={instruction.text}
                            onChange={(e) => updateInstruction(index, e.target.value)}
                            placeholder="Instructions for the AI on how to handle this template..."
                            rows={3}
                            required={index === 0}
                          />
                          <span className="topics-item-id" title={`Click to copy: ${instruction.id}`} onClick={() => navigator.clipboard.writeText(instruction.id)}>ID: {instruction.id}</span>
                          {newTopic.topicSpecificNotes.length > 1 && (
                            <button
                              type="button"
                              className="topics-remove-instruction"
                              onClick={() => removeInstruction(index)}
                              title="Remove instruction"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        className="topics-add-instruction"
                        onClick={addInstruction}
                      >
                        + Add Instruction
                      </button>
                    </div>
                  </div>
                  
                  <div className="topics-form-group">
                    <label>AI Tag:</label>
                    <select
                      value={newTopic.aiTagId}
                      onChange={(e) => setNewTopic({...newTopic, aiTagId: e.target.value})}
                      className="topics-ai-tag-select"
                      disabled={isProcessing}
                    >
                      <option value="">Select an AI Tag</option>
                      {aiTags.map(tag => (
                        <option key={tag.id} value={tag.id}>{tag.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* DSGVO/UWG Compliance Checker */}
                  {(newTopic.subjectLines.some(line => line.text && line.text.trim() !== '') || 
                    newTopic.topicSpecificNotes.some(note => note.text && note.text.trim() !== '')) && (
                    <div className="topics-compliance-wrapper">
                      <EmailComplianceChecker 
                        emailText={newTopic.topicSpecificNotes.map(n => n.text).join('\n')}
                        subjectLine={newTopic.subjectLines.map(l => l.text).join('\n')}
                      />
                      <TemplateDeliverabilityChecker
                        variants={newTopic.topicSpecificNotes}
                        subjectVariants={newTopic.subjectLines}
                      />
                    </div>
                  )}
                  
                  <div className="topics-modal-actions">
                    <button type="submit" className="topics-modal-save" disabled={isProcessing}>
                      {isProcessing ? 'Saving...' : (editingTopicId ? 'Update Template' : 'Create Template')}
                    </button>
                    <button 
                      type="button" 
                      className="topics-modal-cancel"
                      onClick={() => {
                        setShowModal(false);
                        handleCancelEdit();
                      }}
                      disabled={isProcessing}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {!topicsReady ? (
          <p className="topics-loading" aria-live="polite">Loading templates…</p>
        ) : (
        <>
        {/* Category tab badges — click through to filter by template type */}
        <div className="topics-category-tabs">
          <button
            type="button"
            className={`topics-category-tab ${activeTemplateCategory === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTemplateCategory('all')}
          >
            All
          </button>
          {topicTypes.map(type => {
            const count = (groupedTopics[type.value] || []).length;
            const selectedForType = getSelectedTopicForType(type.value);
            const hasSelection = type.value === 'custom_reply'
              ? Array.isArray(selectedForType) && selectedForType.length > 0
              : !!selectedForType;
            return (
              <button
                type="button"
                key={type.value}
                className={`topics-category-tab ${activeTemplateCategory === type.value ? 'active' : ''} ${hasSelection ? 'has-selection' : ''}`}
                onClick={() => setActiveTemplateCategory(type.value)}
              >
                {type.label}
                {count > 0 && <span className="topics-category-tab-count">{count}</span>}
              </button>
            );
          })}
        </div>

        {Object.entries(groupedTopics)
          .filter(([type]) => activeTemplateCategory === 'all' || type === activeTemplateCategory)
          .map(([type, topics]) => {
          const selectedTopicId = getSelectedTopicForType(type);
          const isCustomReplyType = type === 'custom_reply';
          
          // Bestimme die Anzahl der ausgewählten Topics
          let selectedCount = 0;
          if (isCustomReplyType && selectedTopicId) {
            selectedCount = selectedTopicId.length;
          } else if (selectedTopicId) {
            selectedCount = 1;
          }
          
          return (
            <div key={type} className="topics-category">
              <h4 className="topics-category-title">
                {topicTypes.find(t => t.value === type)?.label || type}
                {selectedCount > 0 ? 
                  <span className="topics-selected-indicator"> ({selectedCount} selected)</span> : 
                  <span className="topics-not-selected-indicator"> (none selected)</span>
                }
              </h4>
              <div className="topics-list">
                {topics.map(topicData => {
                  let isSelected = false;
                  if (isCustomReplyType && selectedTopicId) {
                    isSelected = selectedTopicId.includes(topicData.id);
                  } else {
                    isSelected = selectedTopicId === topicData.id;
                  }
                  return (
                    <div 
                      key={topicData.id} 
                      className={`topics-item ${isSelected ? 'selected' : ''} ${topicData.isCustom ? 'custom' : ''}`}
                      onClick={(e) => {
                        const target = e.target;
                        if (target.closest('button') || target.closest('.topics-id-badge')) return;
                        openMasterEditor(topicData.id, topicData);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div
                        className="topics-header"
                      >
                        <button
                          type="button"
                          className={`topics-checkbox-custom ${isSelected ? 'checked' : ''}`}
                          aria-pressed={isSelected}
                          aria-label={isSelected ? 'Deselect template' : 'Select template'}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleTopicToggle(topicData.id, topicData);
                          }}
                        >
                          <img src="/img/tick.svg" alt="" />
                        </button>
                        <span className="topics-name">
                            {topicData.topic}
                            {isVoiceCloneTemplate(topicData) ? (
                              <span className="topics-voice-clone-badge" title="CopyTwin Mode">
                                <i className="fa-solid fa-wand-magic-sparkles" />
                                CopyTwin
                              </span>
                            ) : topicData.isCustom && (
                              <span className="topics-custom-badge">Custom</span>
                            )}
                            {/* Topic ID für Debugging */}
                            <span 
                              className="topics-id-badge" 
                              title={`ID: ${topicData.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(topicData.id);
                              }}
                            >
                              <i className="fa fa-info-circle"></i>
                            </span>
                            {/* Template Performance Stats */}
                            <TemplateStatsBadge stats={topicData.stats} />
                            {/* Compliance Status Badge direkt nach Custom */}
                            <TopicComplianceBadge topicData={topicData} />
                          </span>
                        <div className="topics-custom-actions">
                          {topicData.isCustom && (
                            <button
                              className="topics-duplicate-button"
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDuplicateModal({
                                  open: true,
                                  sourceId: topicData.id,
                                  name: `${topicData.topic || "Template"} (Copy)`,
                                });
                              }}
                              title="Duplicate template"
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                              </svg>
                            </button>
                          )}
                          {topicData.isCustom && (
                            <button
                              className="topics-delete-button"
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                setConfirmModal({
                                  open: true,
                                  title: "Delete Topic",
                                  message: `Are you sure you want to delete the topic "${topicData.topic}"? This action cannot be undone.`,
                                  onConfirm: () => {
                                    setConfirmModal(prev => ({ ...prev, open: false }));
                                    handleDeleteCustomTopic(topicData.id);
                                  }
                                });
                              }}
                              title="Delete topic"
                            >
                              ×
                            </button>
                          )}
                        </div>
                        <button 
                          className="topics-open-arrow" 
                          title="Open in Editor"
                          onClick={(e) => {
                            e.stopPropagation();
                            openMasterEditor(topicData.id, topicData);
                          }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6"></polyline>
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
                <button
                  className="topics-add-new-button"
                  onClick={() => {
                    // Open the mode picker modal so the user chooses between
                    // Standard Mode (existing flow) and Voice Clone Mode.
                    // The actual creation runs in `handleNewTemplateMode` below.
                    setPendingNewType(type);
                    setShowModeModal(true);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  <span>Add New</span>
                </button>
              </div>
            </div>
          );
        })}
        
        {message && <p className="topics-message">{message}</p>}
        </>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, open: false }))}
      />

      {duplicateModal.open && (
        <div
          className="topics-duplicate-overlay"
          onClick={() => setDuplicateModal({ open: false, sourceId: null, name: "" })}
        >
          <div
            className="topics-duplicate-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="topics-duplicate-title">Duplicate Template</h3>
            <p className="topics-duplicate-message">
              All settings will be copied. The duplicate will not be activated.
            </p>
            <label className="topics-duplicate-label" htmlFor="topics-duplicate-name">
              New template name
            </label>
            <input
              id="topics-duplicate-name"
              className="topics-duplicate-input"
              type="text"
              value={duplicateModal.name}
              autoFocus
              onChange={(e) =>
                setDuplicateModal((prev) => ({ ...prev, name: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && duplicateModal.name.trim()) {
                  const { sourceId, name } = duplicateModal;
                  setDuplicateModal({ open: false, sourceId: null, name: "" });
                  handleDuplicateCustomTopic(sourceId, name);
                }
              }}
              placeholder="Enter a name for the copy"
            />
            <div className="topics-duplicate-actions">
              <button
                className="topics-duplicate-cancel"
                onClick={() => setDuplicateModal({ open: false, sourceId: null, name: "" })}
              >
                Cancel
              </button>
              <button
                className="topics-duplicate-confirm"
                disabled={!duplicateModal.name.trim() || isProcessing}
                onClick={() => {
                  const { sourceId, name } = duplicateModal;
                  setDuplicateModal({ open: false, sourceId: null, name: "" });
                  handleDuplicateCustomTopic(sourceId, name);
                }}
              >
                Duplicate
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WorldSettingsTopics; 